/* ============================================================
   DICTIONARY — ENGLISH
   ============================================================
   Every key here must mirror a key in ms.js. A key that is
   missing here does NOT break the page — it silently falls
   back to the Malay sentence — so a missing key shows up as
   Malay text stranded inside an English page. Treat that as
   a bug, not as acceptable behaviour.

   Tone: plain professional English, second person ("your
   account"), no contractions in warnings, no emoji.
   Product/technical terms stay as they are in both languages
   (run, generate, scrape, extension, browser, prompt,
   collection, storage, Cloud, workspace).
   ============================================================ */

export default {
    'fc.menu': "Open menu",
    'fc.subtitle': "Manage your Google Flow access and subscription.",
    'fc.loading': "Loading…",
    'fc.active': "Subscription active",
    'fc.pilot': "Pilot access allowed",
    'fc.inactive': "Access not available",
    'fc.expiry': "Expires on",
    'fc.renew': "Subscribe / Renew",
    'fc.accessNote': "Open Flow requires the SESAAT extension to be signed in and access to be enabled.",
    'fc.st.ready': "Ready to use.",
    'fc.st.sourceDown': "Flow access is temporarily down. The connection is being restored by the admin — please wait. No need to click Open Flow repeatedly.",
    'fc.st.preparing': "Your access is being set up by the admin.",
    'fc.st.disabled': "Access is temporarily turned off.",
    'fc.st.maintenance': "Brief maintenance. Please try again shortly.",
    'fc.st.busy': "System is busy. Please try again shortly.",
    'fc.st.expired': "Your Flow subscription has ended.",
    'fc.st.noAccess': "You don't have Flow access yet.",
    'fc.st.creditLimit': "You have reached your credit limit. Generation is paused. Please contact the admin to continue.",
    'fc.preparing': "Preparing Flow…",
    'fc.opened': "Flow opened in a new tab.",
    'fc.loadError': "Unable to load status. Try Refresh in Settings & Help.",
    'fc.timeout': "Extension did not respond. Reload the SESAAT extension and this page.",
    'fc.login': "Sign in to the extension with the same SESAAT account.",
    'fc.noAccess': "Flow access turns on automatically once your payment is confirmed.",
    'fc.busy': "Another Flow operation is running. Retry when it finishes.",
    'fc.openError': "Unable to open Flow. Check extension login or contact the admin.",
    'fc.sections': "Flow sections",
    'fc.overview': "My Projects",
    'fc.billing': "Subscription & Billing",
    'fc.settings': "Settings & Help",
    'fc.readyTitle': "Open Flow in one click",
    'fc.instructions': "Install the SESAAT extension, sign in with the same account, then click Open Flow.",
    'fc.billingNote': "View current plans, prices and terms on the Flow Ultra payment page.",
    'fc.viewPlan': "View plans and payment",
    'fc.history': "Payment history",
    'fc.historyNote': "Shows up to 50 records linked to this SESAAT account. Payments using another email may not appear.",
    'fc.date': "Date",
    'fc.amount': "Amount",
    'fc.status': "Status",
    'fc.paid': "Paid",
    'fc.unpaid': "Unpaid",
    'fc.paidManual': "Paid (recorded by admin)",
    'fc.granted': "Granted by admin",
    'fc.noPayments': "No payment records to display.",
    'fc.yourPlans': "Your subscriptions",
    'fc.type': "Type",
    'fc.type1': "Lite (shared Gmail)",
    'fc.type2': "Own Gmail + credits",
    'fc.planType1': "Flow Ultra — Lite",
    'fc.planType2': "Flow Ultra — Credits",
    'fc.started': "Started",
    'fc.daysLeft': "days left",
    'fc.price': "Price",
    'fc.credits': "Credits used",
    'fc.subActive': "Active",
    'fc.subExpiring': "Expiring soon",
    'fc.subExpired': "Expired",
    'fc.help': "Use ordinary Chrome with the SESAAT extension. If opening fails, reload the extension and this page, then retry.",
    'fc.install': "Extension guide",
    'fc.refresh': "Refresh",
    'fc.rf.button': "Refresh session",
    'fc.rf.dead': "Your Flow session has ended. Press Refresh session to get a new one.",
    'fc.rf.refreshing': "Preparing a new session. Usually under 1 minute. Keep this page open.",
    'fc.rf.ready': "A new session is ready. Press Open Flow now.",
    'fc.rf.browserReject': "The session was sent, but your browser could not open it. Close any old Flow tab and press Refresh session again.",
    'fc.rf.maintenance': "Flow is under maintenance. Our team has been notified. This page will update on its own.",
    'fc.rf.timeout': "The session is not ready yet. Please press Refresh session again.",
    'fc.rf.requestError': "Could not send the request. Please try again shortly.",
    /* ── One Flow, one browser (FlowBrowserLain.jsx) ─────────── */
    'fc.br.elsewhere': "Your Flow is active in another browser. One Flow subscription can be used in one browser at a time.",
    'fc.br.seen': "Last active there:",
    'fc.br.move': "Move to this browser",
    'fc.br.moving': "Moving Flow to this browser…",
    'fc.br.note': "Flow in the old browser closes automatically. You can keep using Flow here without waiting.",
    'fc.br.moveError': "Flow could not be moved. Please try again shortly.",
    /* ── Flow Projects ───────────────────────────────────────── */
    'fc.fp.title': "My Projects",
    'fc.fp.search': "Search by URL or name…",
    'fc.fp.sortNewest': "Newest",
    'fc.fp.sortOldest': "Oldest",
    'fc.fp.sortLastUsed': "Recently used",
    'fc.fp.pin': "Pin",
    'fc.fp.unpin': "Unpin",
    'fc.fp.open': "Open",
    'fc.fp.empty': "No saved projects yet. Open Flow and create a project — it will appear here.",
    'fc.fp.note': "Projects are saved automatically when you create them in Flow.",
    'fc.fp.account': "Account",
    /* ── Flow Ultra type 2: credits used ─────────────────────── */
    'fc.t2.title': "Estimated credits used",
    'fc.t2.estimateNote': "This is an estimate read automatically from your Google Flow activity and may differ slightly from Google's own figure.",
    'fc.t2.credits': "credits",
    'fc.t2.refresh': "Refresh",
    'fc.t2.refreshing': "Reading…",
    'fc.t2.near': "By our estimate, you have used over 80% of your credit limit.",
    'fc.t2.full': "By our estimate, you have reached your credit limit. Generation is paused. Please contact the admin to continue.",
    'fc.t2.updated': "Last updated",
    'fc.t2.useBefore': "Use your credits before {date}. Unused credits expire after this date.",
    'fc.t2.never': "Not read yet. Open Flow once, then press Refresh.",
    'fc.t2.errGmail': "The Google account open in this browser is not your Flow account. Open Flow again from this page.",
    'fc.t2.errTimeout': "Reading credits took too long. Keep Chrome open and try again.",
    'fc.t2.errKerap': "Credits were just read. Please wait 2 minutes before refreshing again.",
    'fc.t2.errGeneric': "Credits could not be read right now. Please try again later.",
    'fc.t2.openError': "Flow sign-in could not be completed. Please try again or contact admin.",
    'fc.t2.err.title': "Flow sign-in was not successful",
    'fc.t2.err.retry': "Try again",
    'fc.t2.err.contact': "Contact admin",
    'fc.t2.err.close': "Close",
    'fc.t2.err.waText': "Hi admin, my Flow sign-in was not successful. Code: {kod}. SESAAT account: {emel}",
    'fc.t2.err.tab': "The Flow sign-in tab was closed before it finished. Press Open Flow again and keep that tab open until Flow appears.",
    'fc.t2.err.masa': "Flow sign-in took too long. Please check your internet connection, then press Open Flow again.",
    'fc.t2.err.semak': "Google asked for an extra security check on your Flow account. Please contact the admin and quote this code.",
    'fc.t2.err.admin': "Your Flow account needs admin action before it can be used. Please contact the admin and quote this code.",
    'fc.t2.err.ulang': "The connection to the Flow server was interrupted. Please press Open Flow again. If it keeps happening, contact the admin.",
    'fc.t2.err.halaman': "The Google sign-in page for your Flow account could not be completed automatically. Please contact the admin and quote this code.",
    /* ── Shared ─────────────────────────────────────────────── */
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.change': 'Change',
    'common.back': 'Back',
    'common.logout': 'Log out',
    'common.loggingOut': 'Logging out…',
    'common.saving': 'Saving…',
    'common.none': 'None',
    'common.unlimited': 'Unlimited',
    'common.noRecord': 'No record',
    'common.noNumber': 'No number',
    'common.today': 'Today',
    'common.status': 'Status',
    'common.copyright': '© 2026 Sesaat · v{versi}',

    /* ── Settings shell ─────────────────────────────────────── */
    'settings.loading': 'Loading settings…',
    'settings.planNote': 'Design plan · /settings-v3.9',

    'settings.group.account': 'Account',
    'settings.group.security': 'Security',
    'settings.group.subscription': 'Subscription',
    'settings.group.extension': 'Extension',
    'settings.group.referral': 'Referral',

    'settings.nav.account': 'Profile',
    'settings.nav.appearance': 'Appearance',
    'settings.nav.security': 'Password',
    'settings.nav.billing': 'My plan',
    'settings.nav.storage': 'Storage & quota',
    'settings.nav.extension': 'Extension status',
    'settings.nav.referral': 'Referring agent',

    /* ── Page titles + descriptions ─────────────────────────── */
    'settings.page.account.title': 'Profile',
    'settings.page.account.desc': 'Your account details and how we reach you.',
    'settings.page.appearance.title': 'Appearance',
    'settings.page.appearance.desc': 'Choose the theme and language for your SESAAT display.',
    'settings.page.security.title': 'Password',
    'settings.page.security.desc': 'The same password is used for the webapp and the SESAAT Extension.',
    'settings.page.billing.title': 'My plan',
    'settings.page.billing.desc': 'Your subscription plan and expiry date.',
    'settings.page.storage.title': 'Storage & quota',
    'settings.page.storage.desc': 'Video storage space and your remaining generations today.',
    'settings.page.extension.title': 'Extension',
    'settings.page.extension.desc': 'SESAAT Extension installation status on this browser.',
    'settings.page.referral.title': 'Referring agent',
    'settings.page.referral.desc': 'The agent who brought you to SESAAT.',

    /* ── Profile page ───────────────────────────────────────── */
    'settings.profile.photo': 'Profile picture',
    'settings.profile.upload': 'Upload',
    'settings.profile.remove': 'Remove',
    'settings.profile.photoHint': 'Image files, maximum 5MB.',
    'settings.profile.displayName': 'Display name',
    'settings.profile.email': 'Email address',
    'settings.profile.emailPlaceholder': 'enter new email',
    'settings.profile.emailNote': 'A verification email will be sent to the new address. Your old email keeps working until you click the verification link. Your current password is required.',
    'settings.profile.whatsapp': 'WhatsApp number',
    'settings.profile.whatsappPlaceholder': 'Example: 0123456789',
    'settings.profile.whatsappNote': 'This number is for your SESAAT account. To change the number shown to your affiliate referrals, go to Affiliate > Settings.',
    'settings.profile.package': 'Plan',
    'settings.profile.memberSince': 'Member since',

    /* ── Appearance page (theme + language) ─────────────────── */
    'settings.appearance.themeTitle': 'Display theme',
    'settings.appearance.themeDesc': 'Light is the default SESAAT theme. Your choice takes effect immediately.',
    'settings.appearance.dark': 'Dark',
    'settings.appearance.light': 'Light',
    'settings.appearance.themeFoot': 'This choice is saved on this browser only — other browsers keep their own setting.',
    'settings.appearance.langTitle': 'Display language',
    'settings.appearance.langDesc': 'English is the default SESAAT language. Choose Bahasa Melayu if you are more comfortable with it — your choice takes effect immediately.',
    'settings.appearance.langFoot': 'Like the theme, your language is saved on this browser only. Some older pages may stay in Bahasa Melayu for now.',

    /* ── Password page ──────────────────────────────────────── */
    'settings.password.googleOnly': 'You currently sign in with the Google button only. Set a password below — after that you can sign in with {emel} and your password on any browser, including the SESAAT Extension.',
    'settings.password.current': 'Current password',
    'settings.password.new': 'New password',
    'settings.password.confirm': 'Confirm new password',
    'settings.password.retype': 'Type it again',
    'settings.password.strength': 'Strength: {tahap}',
    'settings.password.mismatch': 'Passwords do not match.',
    'settings.password.matches': 'Passwords match.',
    'settings.password.rule': 'Your password must be at least 6 characters, and should ideally contain an uppercase letter and a number.',
    'settings.password.checkInbox': 'Please check the inbox (or spam folder) at {emel}.',
    'settings.password.stayLoggedIn': 'You will stay signed in after changing your password.',
    'settings.password.emailIsUsername': 'Your email becomes your username.',
    'settings.password.resetSent': 'Email sent',
    'settings.password.sendReset': 'Send reset email',
    'settings.password.update': 'Update',
    'settings.password.set': 'Set password',

    /* ── My plan page ───────────────────────────────────────── */
    'settings.billing.currentPlan': 'Current plan',
    'settings.billing.activeUntil': 'Active until',
    'settings.billing.manageFoot': 'Renew or change your plan.',
    'settings.billing.manage': 'Manage subscription',
    'settings.billing.upgradeTitle': 'Upgrade to a paid plan',
    'settings.billing.upgradeDesc': 'Unlock every module, {kuota} generations per day, and Flow plus TikTok automation without trial limits.',
    'settings.billing.upgradeFoot': 'One payment, full access for the whole term.',
    'settings.billing.smartTitle': 'Upgrade to Premium',
    'settings.billing.smartDesc': 'You are on the SMART plan. Upgrade once to unlock every ready-made module and 100 generates a day.',
    'settings.billing.smartFoot': 'Your subscription period stays as it is.',
    'settings.billing.seePricing': 'See pricing',
    'settings.billing.slotTitle': 'Your slots',
    'settings.billing.slotDesc': 'One quota covers both. Every Chrome browser you sign in on takes one slot, and so does every avatar you save.',
    'settings.billing.slotBrowser': 'Chrome browsers',
    'settings.billing.slotAvatar': 'Saved avatars',
    'settings.billing.slotOf': 'of {n} used',
    'settings.billing.slotUnlimited': 'in use · no limit',
    'settings.billing.slotUnknown': 'Could not be read right now',
    'settings.billing.slotFoot': 'Need more slots? Talk to us on WhatsApp.',
    'settings.billing.modulesTitle': 'Your modules',
    'settings.billing.modulesDesc': 'Modules unlocked on your account.',
    'settings.billing.modulesNone': 'No module has been unlocked on this account yet.',
    'settings.billing.flowTitle': 'Flow subscription',
    'settings.billing.flowDesc': 'Your Flow Google access — a separate subscription from your main plan.',
    'settings.billing.flowPlan': 'Flow plan',
    'settings.billing.flowChecking': 'Checking your Flow subscription…',
    'settings.billing.flowUnavailable': 'Could not load your Flow status right now.',
    'settings.billing.flowNone': "You don't have a Flow subscription yet.",
    'settings.billing.flowManage': 'Full details',
    'settings.billing.flowManageFoot': 'See your Flow plan, billing and payment history.',
    'settings.billing.flowGet': 'Get Flow',
    'settings.billing.flowGetFoot': 'Add Flow Google automation to your account.',
    'settings.billing.yearly': 'Yearly',
    'settings.billing.monthly': 'Monthly',

    /* ── Storage & quota page ───────────────────────────────── */
    'settings.storage.title': 'Storage space',
    'settings.storage.desc': 'Videos and images saved to the Cloud.',
    'settings.storage.of': 'of {jumlah}',
    'settings.storage.almostFull': 'Storage is almost full. New videos may fail to save until you free up space.',
    'settings.storage.hint': 'Delete videos you have already uploaded to TikTok to free up space.',
    'settings.storage.foot': 'Videos are deleted in the Workspace.',
    'settings.storage.manage': 'Manage videos',
    'settings.storage.quotaTitle': 'Generation quota',
    'settings.storage.quotaDesc': 'One generation produces 3 prompt sets.',
    'settings.storage.quotaAdmin': '{n} used · unlimited',
    'settings.storage.quotaOfToday': 'of {n} today',
    'settings.storage.quotaResetNote': 'Your quota resets in full at 12:00 midnight (Malaysia time). This quota is shared across every module.',
    'settings.storage.quotaOfTrial': 'of {n} trial credits',
    'settings.storage.quotaTrialNote': 'Trial credits are granted once only and are not refilled daily.',

    /* ── Extension page ─────────────────────────────────────── */
    'settings.ext.title': 'Status on this browser',
    'settings.ext.desc': 'Each browser needs the extension installed separately.',
    'settings.ext.connected': 'Connected',
    'settings.ext.checking': 'Checking…',
    'settings.ext.missing': 'Not detected',
    'settings.ext.installedVersion': 'Installed version',
    'settings.ext.oldVersion': 'Older version',
    'settings.ext.latestVersion': 'Latest released version:',
    'settings.ext.duplicateWarn': 'This browser has more than one SESAAT Extension active. Please open {laluan} and remove the older installation — two installations at once can make automation behave unpredictably.',
    'settings.ext.missingNote': 'The extension is not responding on this browser. It may not be installed yet, or it was just installed and this page has not been reloaded.',
    'settings.ext.checkFoot': 'The check runs directly on this browser.',
    'settings.ext.recheck': 'Check again',
    'settings.ext.download': 'Download extension',
    'settings.ext.howToInstall': 'How to install',
    'settings.ext.storeNote': 'SESAAT is not distributed through the Chrome Web Store. After installing a new version, reload this page so the status above updates.',

    /* ── Referring agent page ───────────────────────────────── */
    'settings.referral.loading': 'Loading agent details…',
    'settings.referral.agentName': 'Agent name',
    'settings.referral.agentFallback': 'Affiliate Agent',
    'settings.referral.code': 'Referral code',
    'settings.referral.contact': 'Contact agent',
    'settings.referral.inaccurate': 'If these agent details are not correct, please contact the SESAAT admin to have them checked.',
    'settings.referral.emptyTitle': 'No referring agent',
    'settings.referral.emptyDesc': 'You registered directly without an affiliate link. If you did click an agent link but their details are not shown here, please contact the admin to have it checked.',

    /* ── Account operation messages (useAccountSettings) ─────
       Result messages shown on the settings cards after the
       customer presses Save. They originate in the hook, not in
       a view file — that hook is the shared brain behind both
       the old look (ProfileView) and the v3.9 look
       (SettingsV39View), so translating there fixes both. */
    'acc.nameEmpty': 'Your name cannot be empty.',
    'acc.nameUpdated': 'Your name has been updated.',
    'acc.nameFailed': 'Could not update your name: {ralat}',
    'acc.waInvalid': 'That WhatsApp number is not valid. Please enter 9 to 15 digits.',
    'acc.waUpdated': 'Your WhatsApp number has been updated.',
    'acc.waFailed': 'Could not update the number: {ralat}',
    'acc.emailInvalid': 'Please enter a valid email address.',
    'acc.emailInUse': 'That email address is already used by another account.',
    'acc.emailBad': 'That email address is not valid.',
    'acc.sessionExpiredRetry': 'Your session has expired. Please sign in again and try once more.',
    'acc.emailFailed': 'Could not change your email: {ralat}',
    'acc.unknownError': 'Unknown error',
    'acc.pwMismatchNew': 'The new passwords do not match.',
    'acc.pwTooShortNew': 'Your new password must be at least 6 characters.',
    'acc.pwUpdated': 'Your password has been updated.',
    'acc.pwCurrentWrong': 'Your current password is incorrect.',
    'acc.tooManyAttempts': 'Too many attempts. Please wait a moment.',
    'acc.sessionExpired': 'Your session has expired. Please sign in again.',
    'acc.pwChangeFailed': 'Could not change your password: {ralat}',
    'acc.pwMismatch': 'The passwords do not match.',
    'acc.pwTooShort': 'Your password must be at least 6 characters.',
    'acc.noEmail': 'This account has no email address. Please contact the SESAAT admin.',
    'acc.alreadyHasPw': 'This account already has a password. Please reload the page.',
    'acc.emailInUseAdmin': 'That email is already used by another account. Please contact the SESAAT admin.',
    'acc.pwTooWeak': 'That password is too weak. Please use at least 6 characters.',
    'acc.googleCancelled': 'Google verification was cancelled. Please try again.',
    'acc.googleBlocked': 'The Google window was blocked by your browser. Please allow popups for this site, then try again.',
    'acc.sessionExpiredGoogle': 'Your session has expired. Please log out, sign in again with Google, then try once more.',
    'acc.pwSetFailed': 'Could not set your password: {ralat}',
    'acc.tooManyRequests': 'Too many requests. Please wait a moment.',
    'acc.resetEmailFailed': 'Could not send the reset email: {ralat}',
    'acc.logoutFailed': 'Could not log out.',
    'acc.pickImageOnly': 'Please choose an image file only.',
    'acc.fileTooBig': 'That file is too large. The maximum is 5MB.',
    'acc.avatarFailed': 'Could not upload the picture.',
    'acc.reauthEnterPw': 'Please enter your current password.',
    'acc.reauthWrongPw': 'That password is incorrect. Please try again.',
    'acc.reauthFailed': 'Verification failed: {ralat}',
    'acc.pwStrength1': 'Very Weak',
    'acc.pwStrength2': 'Weak',
    'acc.pwStrength3': 'Fair',
    'acc.pwStrength4': 'Strong',
    'acc.pwStrength5': 'Very Strong',

    /* ── Re-authentication modal ────────────────────────────── */
    'settings.reauth.title': 'Security check',
    'settings.reauth.desc': 'Enter your current password to continue.',
    'settings.reauth.emailNote': 'Changing your email to {emel}. A verification email will be sent to the new address.',
    'settings.reauth.placeholder': 'Enter your password',
    'settings.reauth.verifying': 'Verifying…',
    'settings.reauth.confirm': 'Confirm and continue',

    /* ── adminmarket ─────────────────────────────────────────── */
    'adminmarket.accessDenied': 'Access denied — admins only.',
    'adminmarket.addProduct': 'Add Product',
    'adminmarket.cancel': 'Cancel',
    'adminmarket.card.affiliateGlobal': 'Affiliate global',
    'adminmarket.card.affiliatePct': 'Affiliate {peratus}%',
    'adminmarket.card.autoDeliver': 'Auto deliver',
    'adminmarket.card.autoDeliverDays': 'Auto deliver · {n} days',
    'adminmarket.card.delete': 'Delete',
    'adminmarket.card.edit': 'Edit',
    'adminmarket.card.manageStock': 'Manage Account Stock',
    'adminmarket.card.stockN': 'Stock {n}',
    'adminmarket.card.stockUnlimited': 'Stock ∞',
    'adminmarket.close': 'Close',
    'adminmarket.file.change': 'Change file',
    'adminmarket.file.choose': 'Choose file',
    'adminmarket.file.none': 'No file selected',
    'adminmarket.form.affiliateEnable': 'Allow affiliates to promote this product',
    'adminmarket.form.affiliateRate': 'Custom commission rate (%) — blank = use the global rate (25%)',
    'adminmarket.form.affiliateRatePlaceholder': 'e.g. 40',
    'adminmarket.form.category': 'Category',
    'adminmarket.form.created': 'Product created.',
    'adminmarket.form.description': 'Description',
    'adminmarket.form.duration': 'Access period (days) — blank = owned forever',
    'adminmarket.form.durationHint1': 'Enter ',
    'adminmarket.form.durationHint2': ' for a monthly rental (Flow Ultra) — the buyer stops seeing the credentials once it expires. Leave it ',
    'adminmarket.form.durationHint3': ' for an outright sale (TikTok account).',
    'adminmarket.form.durationHintBlank': 'blank',
    'adminmarket.form.durationPlaceholder': 'e.g. 30 for a one-month rental',
    'adminmarket.form.ebook': 'Ebook file (PDF)',
    'adminmarket.form.ebookMustPdf': 'The ebook file must be a PDF.',
    'adminmarket.form.editProduct': 'Edit Product',
    'adminmarket.form.fulfillment': 'Delivery method',
    'adminmarket.form.image': 'Product image',
    'adminmarket.form.internalNotes': 'Internal notes (admin only)',
    'adminmarket.form.keepBlank': ' (leave blank to keep the current one)',
    'adminmarket.form.needTitle': 'Please fill in the product title.',
    'adminmarket.form.order': 'Sort order',
    'adminmarket.form.originalPrice': 'Original / struck-through price (RM)',
    'adminmarket.form.paid': 'Paid',
    'adminmarket.form.price': 'Price (RM)',
    'adminmarket.form.pricingMode': 'Pricing mode',
    'adminmarket.form.rateRange': 'The commission rate must be between 1–100%.',
    'adminmarket.form.saveFailed': 'Failed to save: {ralat}',
    'adminmarket.form.status': 'Status',
    'adminmarket.form.stock': 'Stock',
    'adminmarket.form.stockAccounts': '{n} accounts',
    'adminmarket.form.stockAutoHint1': 'Calculated automatically from the account list. Use the ',
    'adminmarket.form.stockAutoHint2': ' button on the product card.',
    'adminmarket.form.stockManual': 'Stock (blank = unlimited)',
    'adminmarket.form.stockNone': 'No accounts yet',
    'adminmarket.form.title': 'Title',
    'adminmarket.form.updated': 'Product updated.',
    'adminmarket.free': 'Free',
    'adminmarket.noTitle': '(no title)',
    'adminmarket.orders.accessExtended': 'Access was extended on the buyer\'s existing account.',
    'adminmarket.orders.accountHandedOver': 'One account was delivered automatically from stock.',
    'adminmarket.orders.commission': '{asas} Commission {jumlah} credited.',
    'adminmarket.orders.deliverFailed': 'Failed to deliver: {ralat}',
    'adminmarket.orders.deliveryPlaceholder': 'Delivery info: username / password / Gmail access…',
    'adminmarket.orders.emptyDesc': 'There are no orders in this status right now.',
    'adminmarket.orders.emptyTitle': 'No orders',
    'adminmarket.orders.filter.delivered': 'Delivered',
    'adminmarket.orders.filter.rejected': 'Rejected',
    'adminmarket.orders.filter.partial': 'Partial',
    'adminmarket.orders.shortfall': '{n} not delivered',
    'adminmarket.orders.filter.requested': 'Requested',
    'adminmarket.orders.loadFailed': 'Failed to load orders: {ralat}',
    'adminmarket.orders.markDelivered': 'Mark as Delivered',
    'adminmarket.orders.markedDelivered': 'Order marked as delivered.',
    'adminmarket.orders.needDeliveryInfo': 'Please fill in the delivery info (credentials/access) first.',
    'adminmarket.orders.notePlaceholderEbook': 'Note (optional) — ebook auto-download',
    'adminmarket.orders.notePlaceholderStock': 'Internal note (optional) — the account is delivered automatically from stock',
    'adminmarket.orders.processing': 'Processing…',
    'adminmarket.orders.ref': 'Ref: {kod}',
    'adminmarket.orders.reject': 'Reject',
    'adminmarket.orders.rejectFailed': 'Failed to reject: {ralat}',
    'adminmarket.orders.rejectPrompt': 'Reason for rejection (optional):',
    'adminmarket.orders.rejected': 'Order rejected.',
    'adminmarket.orders.viewProof': 'View payment proof',
    'adminmarket.orders.whatsapp': 'WhatsApp: {nombor}',
    'adminmarket.products.confirmDelete': 'Delete the product "{tajuk}"? This action is permanent.',
    'adminmarket.products.deleteFailed': 'Failed to delete: {ralat}',
    'adminmarket.products.deleted': 'Product deleted.',
    'adminmarket.products.emptyDesc': 'Products created here appear in the buyer\'s Marketplace.',
    'adminmarket.products.emptyTitle': 'No products yet',
    'adminmarket.products.loadFailed': 'Failed to load products: {ralat}',
    'adminmarket.save': 'Save',
    'adminmarket.saving': 'Saving…',
    'adminmarket.stock.addFailed': 'Failed to add: {ralat}',
    'adminmarket.stock.addN': 'Add {n} accounts',
    'adminmarket.stock.added': '{n} accounts added to stock.',
    'adminmarket.stock.aria': 'Account stock',
    'adminmarket.stock.available': '{n} available',
    'adminmarket.stock.badgeAvailable': 'Available',
    'adminmarket.stock.badgeSold': 'Sold',
    'adminmarket.stock.buyerFallback': 'buyer',
    'adminmarket.stock.confirmDelete': 'Delete this account from stock? This action is permanent.',
    'adminmarket.stock.deleteAria': 'Delete account',
    'adminmarket.stock.detected': 'Detected: {senarai}',
    'adminmarket.stock.empty': 'No accounts in stock yet. Paste some above to get started.',
    'adminmarket.stock.forever': ' · forever',
    'adminmarket.stock.hint1': 'One account per block. Separate each account with a ',
    'adminmarket.stock.hint2': ' or a line of ',
    'adminmarket.stock.hintBlankLine': 'blank line',
    'adminmarket.stock.labelEmpty': '(empty)',
    'adminmarket.stock.loadFailed': 'Failed to load stock: {ralat}',
    'adminmarket.stock.loading': 'Loading…',
    'adminmarket.stock.pasteLabel': 'Paste new accounts',
    'adminmarket.stock.pastePlaceholder': 'example@gmail.com\npassword: abc123\n2FA: 1234 5678\n\nexample2@gmail.com\npassword: xyz789',
    'adminmarket.stock.sold': '{n} sold',
    'adminmarket.stock.title': 'Account stock — {tajuk}',
    'adminmarket.stock.until': ' · until {tarikh}',
    'adminmarket.subtitle': 'Manage the products you sell and deliver buyer orders.',
    'adminmarket.tab.orders': 'Orders',
    'adminmarket.tab.products': 'Products',
    'adminmarket.title': 'Manage Marketplace',

    /* ── affiliate ───────────────────────────────────────────── */
    'affiliate.badge.credited': 'Credited',
    'affiliate.badge.done': 'Completed',
    'affiliate.badge.pending': 'Pending',
    'affiliate.codeHint': 'Choose the link based on how well the person already knows SESAAT.',
    'affiliate.codeLine': 'Your agent code is {kod}.',
    'affiliate.copied': 'Copied',
    'affiliate.copy': 'Copy',
    'affiliate.historyTitle': 'Payout history',
    'affiliate.link.pricing': 'Pricing',
    'affiliate.link.pricingDesc': 'For those already convinced — straight to the pricing',
    'affiliate.link.salespage': 'Salespage',
    'affiliate.link.salespageDesc': 'For a new audience — let them read the salespage first',
    'affiliate.link.signup': 'Sign up directly',
    'affiliate.link.signupDesc': 'For a hot lead — skip the salespage and go straight into the app',
    'affiliate.linksTitle': 'Your links',
    'affiliate.payout.countHint': '{n} payouts',
    'affiliate.payout.earnings': 'Total earnings',
    'affiliate.payout.earningsHint': 'Lifetime of the account',
    'affiliate.payout.minHint': 'Minimum {jumlah}',
    'affiliate.payout.paid': 'Already paid out',
    'affiliate.payout.withdrawable': 'Available to withdraw',
    'affiliate.prototypeNote': 'Prototype · /affiliate-v3.9',
    'affiliate.recentTitle': 'Recent referrals',
    'affiliate.requestPayout': 'Request payout',
    'affiliate.stat.available': 'Available balance',
    'affiliate.stat.availableHint': 'Ready to withdraw',
    'affiliate.stat.clicks': 'Total clicks',
    'affiliate.stat.conversion': 'Conversion rate',
    'affiliate.stat.conversionHint': 'Referrals divided by clicks',
    'affiliate.stat.pending': 'Pending balance',
    'affiliate.stat.pendingHint': 'Matures after 14 days',
    'affiliate.stat.referrals': 'Total referrals',
    'affiliate.subtitle': 'Share your link and earn a commission every time someone subscribes.',
    'affiliate.tabDashboard': 'Dashboard',
    'affiliate.tabPayouts': 'Payouts',
    'affiliate.th.amount': 'Amount',
    'affiliate.th.commission': 'Commission',
    'affiliate.th.customer': 'Customer',
    'affiliate.th.date': 'Date',
    'affiliate.th.method': 'Method',
    'affiliate.th.package': 'Plan',
    'affiliate.title': 'Affiliates',

    /* ── dashboard ───────────────────────────────────────────── */
    'dashboard.campaign.daysLeft': '{n} days left',
    'dashboard.campaign.holiday': 'Public holiday',
    'dashboard.campaign.platform': 'Platform campaign',
    'dashboard.campaign.title': 'Campaign Calendar',
    /* The REAL campaign calendar (CampaignCalendarSection), not the sample
       one. Event names themselves (Hari Raya, 11.11, school holidays) stay
       out of the dictionary — they are proper names and they come from
       data/campaignCalendar.js + the admin doc, not from code. */
    'dashboard.campaign.eyebrow': 'Plan ahead',
    'dashboard.campaign.edit': 'Edit calendar',
    'dashboard.campaign.saveFailed': 'Save failed: {sebab}',
    'dashboard.campaign.upcoming': 'Coming up',
    'dashboard.campaign.startContent': 'Start content {tarikh}',
    'dashboard.campaign.startPrep': 'Start preparing content {tarikh} ({n} days before)',
    'dashboard.campaign.prepStarted': 'Prep time has already started',
    'dashboard.campaign.prepStartedLong': 'Prep time has already started — do not wait any longer',
    'dashboard.campaign.ongoing': 'Happening now',
    'dashboard.campaign.tomorrow': 'Tomorrow',
    'dashboard.campaign.holidaysMissing': 'Public holidays for {tahun} are not filled in yet — only platform campaigns and commercial dates are shown. Take the official list from kabinet.gov.my, then add it to the calendar data file.',
    'dashboard.campaign.tierBig': 'Big',
    'dashboard.campaign.tierMid': 'Medium',
    'dashboard.campaign.tierSmall': 'Small',
    'dashboard.campaign.tierBigOpt': 'Big (red)',
    'dashboard.campaign.tierMidOpt': 'Medium (yellow)',
    'dashboard.campaign.tierSmallOpt': 'Small (grey)',
    'dashboard.campaign.legendHint': '· Click a day to focus on its events',
    'dashboard.campaign.eventsOf': 'Events · {bulan} {tahun}',
    'dashboard.campaign.showAll': 'Show all',
    'dashboard.campaign.emptyMonth': 'No events this month.',
    'dashboard.campaign.emptyDay': 'No events on this date.',
    'dashboard.campaign.emptyDayShort': 'No events on this day.',
    'dashboard.campaign.hiddenTag': '(hidden)',
    'dashboard.campaign.prevMonth': 'Previous month',
    'dashboard.campaign.nextMonth': 'Next month',
    'dashboard.campaign.school': 'School holiday',
    'dashboard.campaign.commerce': 'Commercial date',
    'dashboard.campaign.custom': 'SESAAT addition',
    /* Edit mode — admin only. */
    'dashboard.campaign.newEvent': 'New event',
    'dashboard.campaign.introLabel': 'Intro line (above the calendar)',
    'dashboard.campaign.noTitle': '(no title)',
    'dashboard.campaign.hide': 'Hide',
    'dashboard.campaign.showAgain': 'Show again',
    'dashboard.campaign.delete': 'Delete',
    'dashboard.campaign.fTitle': 'Title',
    'dashboard.campaign.fDate': 'Date',
    'dashboard.campaign.fUntil': 'Until (optional)',
    'dashboard.campaign.fPriority': 'Priority',
    'dashboard.campaign.fNote': 'Note',
    'dashboard.campaign.fNoteExtra': 'Extra note — {tarikh}',
    'dashboard.campaign.phTitle': 'E.g. Raya campaign',
    'dashboard.campaign.phNote': 'E.g. Push Merdeka-themed outfits, start 17 August. Focus on the fashion niche.',
    'dashboard.footer.rights': '© 2026 SESAAT. All rights reserved.',
    /* Time-of-day greeting — picked by greetingKeyNow() in useDashboard. */
    'dashboard.greeting.morning': 'Good morning',
    'dashboard.greeting.afternoon': 'Good afternoon',
    'dashboard.greeting.evening': 'Good evening',
    'dashboard.help.extension.cta': 'Open the guide',
    'dashboard.help.extension.desc': 'Installation steps and how to update to the latest version.',
    'dashboard.help.extension.title': 'Install the extension',
    'dashboard.help.faq.browsers.a': 'One licence can be installed on 3 to 5 browsers. Each browser needs the extension installed separately and must be signed in with the same SESAAT account.',
    'dashboard.help.faq.browsers.q': 'How many browsers can I use?',
    'dashboard.help.faq.flow.a': 'Make sure the SESAAT extension is installed on the browser you are using, and that you are signed in to Google Labs Flow in that same browser.',
    'dashboard.help.faq.flow.q': 'No video appears after I press Run Flow.',
    'dashboard.help.faq.quota.a': 'The paid quota is 100 generates per day, shared across every formula. It resets in full at 12:00 midnight Malaysia time. One generate produces 3 sets, so up to 300 videos a day.',
    'dashboard.help.faq.quota.q': 'My generate quota is used up.',
    'dashboard.help.faq.storage.a': 'Every account has 5 GB. Open the Workspace and delete videos you have already uploaded to TikTok — they no longer need to be kept once they are posted.',
    'dashboard.help.faq.storage.q': 'My storage is full.',
    'dashboard.help.faqTitle': 'Frequently asked questions',
    'dashboard.help.group.cta': 'Join the group',
    'dashboard.help.group.desc': 'The fastest place to get an answer. Ask directly and someone will reply.',
    'dashboard.help.group.title': 'WhatsApp group',
    'dashboard.help.protoNote': 'Prototype · /help-v3.9',
    'dashboard.help.subtitle': 'Stuck? Start here.',
    'dashboard.help.title': 'Help',
    'dashboard.help.tutorial.cta': 'Open playlist',
    'dashboard.help.tutorial.desc': 'From installing the extension to your first video going up on TikTok.',
    'dashboard.help.tutorial.title': 'Video tutorials',
    'dashboard.newProducts.emptyDesc': 'New products from the System library appear here each time one is added.',
    /* Stand-in name for a product that genuinely has none — rare, not an error. */
    'dashboard.newProducts.fallbackName': 'Product #{n}',
    'dashboard.newProducts.emptyTitle': 'No new products yet',
    'dashboard.newProducts.eyebrow': 'Latest',
    'dashboard.newProducts.seeAll': 'See all',
    'dashboard.newProducts.title': 'New Products',
    'dashboard.passwordNotice.cta': 'Set password',
    /* Terjemahan SETIA kepada notis rupa lama — lihat nota dalam ms.js. */
    'dashboard.passwordNotice.desc': 'Set one now so you can sign in on other browsers and in the SESAAT Extension.',
    'dashboard.passwordNotice.title': 'Your account has no password yet.',
    'dashboard.planNote': 'Design plan · /dashboard-v3.9',
    'dashboard.stat.quota': 'Quota today',
    'dashboard.stat.quotaHint': 'Resets at 12:00 midnight',
    'dashboard.stat.storage': 'Storage',
    'dashboard.stat.storageHint': 'Delete videos you have already posted',
    'dashboard.stat.videos': 'Videos completed this week',
    'dashboard.stat.videosHint': 'Across every formula',
    'dashboard.state.newUser': 'New user',
    'dashboard.state.noProducts': 'No products',
    'dashboard.state.normal': 'Normal',
    'dashboard.subtitle': 'Where every day begins.',
    'dashboard.support.cta': 'Join the WhatsApp group',
    'dashboard.support.desc': 'Have a problem or a question? The support team helps you directly in the WhatsApp group.',
    'dashboard.support.title': 'Group Support',
    'dashboard.title': 'Dashboard',
    'dashboard.wish.editAria': 'Edit your goal',

    /* ── ext ─────────────────────────────────────────────────── */
    'ext.downloadBtn': 'Download Extension',
    'ext.heroDesc': 'Automate Google Flow and upload to TikTok straight from your browser.',
    'ext.heroTitle': 'SESAAT Browser Extension',
    'ext.pageSubtitle': 'This extension is what runs Flow and sends your videos to TikTok.',
    'ext.pageTitle': 'Extension',
    'ext.planNote': 'Design plan · /extension-v3.9',
    'ext.platformNote': 'For Google Chrome and Microsoft Edge on desktop',
    /* Baris pautan ke Tutorial. Menggantikan langkah pasang + FAQ yang
       dibuang daripada halaman Extension (2026-08-16) — panduan penuh
       kini hidup di /tutorial/v_install sahaja. */
    'ext.tutorialLink.desc': 'The full step-by-step guide is in the Tutorial.',
    'ext.tutorialLink.title': 'How to install the SESAAT Extension',

    /* ── formula ─────────────────────────────────────────────── */
    'formula.action.new': 'New formula',
    'formula.badge.active': 'Active',
    'formula.badge.locked': 'Locked',
    'formula.card.meta': '{engine} · 3 sets per generate',
    'formula.card.open': 'Open',
    'formula.card.options': 'Options for {name}',
    'formula.card.ready': 'Ready to run.',
    'formula.card.upgrade': 'Upgrade to unlock.',
    'formula.create.body': 'Got an angle that keeps selling? Build it once — set the scenes, lock the style, then run it on every product you add.',
    'formula.create.cta': 'Start building',
    'formula.create.private': 'Yours only. Nobody else sees it.',
    'formula.create.title': 'Create your own formula',
    'formula.desc.ai16saat-half': 'Reveal the product, then demo how it works.',
    'formula.desc.ai8saat': 'Creator holds the product and talks straight to camera.',
    'formula.desc.fallback': 'Official Sesaat formula.',
    'formula.desc.flownity': 'Full story told across multiple scenes.',
    'formula.desc.goyang': 'Moving template with its own backdrop.',
    'formula.desc.homeniche': 'Household items shown in a real home setting.',
    'formula.desc.smartcustom': 'You write the prompts yourself.',
    'formula.editor.aksara': '{n} characters',
    'formula.editor.enjin.banyak': 'Many scenes (3–6)',
    'formula.editor.enjin.banyakNota': 'One long video, a continuous story. Not open yet.',
    'formula.editor.enjin.dua': '2 scenes',
    'formula.editor.enjin.duaNota': 'Reveal the product first, then show how to use it.',
    'formula.editor.edar.notis': 'Every licensed customer will see this formula the moment you save it.',
    'formula.editor.enjin.satu': '1 scene',
    'formula.editor.enjin.satuNota': 'Creator holds the product and speaks straight to camera.',
    'formula.editor.kaki.kembali': 'Back',
    'formula.editor.kaki.langkah': 'Step {kini} of {jumlah}',
    'formula.editor.kaki.seterusnya': 'Next',
    'formula.editor.kaki.simpanDraf': 'Save draft',
    'formula.editor.kaki.simpanFormula': 'Save formula',
    'formula.editor.langkah.bentuk': 'Video format',
    'formula.editor.langkah.nama': 'Name',
    'formula.editor.langkah.prompt': 'Master prompt',
    'formula.editor.langkah.simpan': 'Save',
    'formula.editor.langkah.uji': 'Test',
    'formula.editor.lulus': 'Passed',
    'formula.editor.milik.label': 'Admin',
    'formula.editor.milik.sendiri': 'Mine',
    'formula.editor.milik.system': 'For system',
    'formula.editor.prototaip': 'Prototype · /formula/editor',
    'formula.editor.rel.aria': 'Steps to build a formula',
    'formula.editor.rel.kembaliKe': 'Back to: {tajuk}',
    'formula.editor.s1.huraianAria': 'Formula description',
    'formula.editor.s1.huraianPlaceholder': 'Creator shows the skin before, then the result after 3 weeks.',
    'formula.editor.s1.namaAria': 'Formula name',
    'formula.editor.s1.namaPlaceholder': 'Serum Review — before & after style',
    'formula.editor.s1.nota': 'A short name you will recognise yourself, plus a one-line description. The name is the only required field apart from the master prompt.',
    'formula.editor.s1.tajuk': 'What should this formula be called?',
    'formula.editor.s2.amaran': 'The video format is locked once the formula is used for the first time. The number of sets and the master prompt can be changed at any time.',
    'formula.editor.s2.nanti': 'Later',
    'formula.editor.s2.nota': 'This choice decides two things: which robot runs inside Google Flow, and the field names in the output contract on the next step.',
    'formula.editor.s2.setLabel': 'Sets per Generate',
    'formula.editor.s2.setNota': 'Each set becomes one video. Three sets should attack from three different angles.',
    'formula.editor.s2.tajuk': 'Is your video one scene or two scenes?',
    'formula.editor.s3.belumDiisi': 'Not filled in yet',
    'formula.editor.s3.hintSatu': 'One instruction is used for all {bil} sets.',
    'formula.editor.s3.hintSetiap': '{bil} sets = {bil} separate prompts.',
    'formula.editor.s3.kontrakBadge': 'Fixed',
    'formula.editor.s3.kontrakNota': 'This block is appended after your prompt, every time you Generate. You do not need to write it — and cannot remove it, because these field names are exactly what the Flow robot looks for.',
    'formula.editor.s3.kontrakTajuk': 'The output contract the system adds',
    'formula.editor.s3.modSatu': 'One prompt',
    'formula.editor.s3.modSetiap': 'Per set',
    'formula.editor.s3.mulaContoh': 'Start from an example',
    'formula.editor.s3.mulaContohNota': 'A ready-made skeleton you can edit. Change the character, location and tone to make it yours.',
    'formula.editor.s3.nota': 'This is the brain of your formula: character, style, storyline, tone, hook, caption. The AI follows these instructions for every product you attach.',
    'formula.editor.s3.phSatu': 'Write your instructions here — role, character, location, story angle, hook style, caption style, things to avoid.',
    'formula.editor.s3.phSetiap': 'Instructions for Set {n} only — a story angle different from the other sets.',
    'formula.editor.s3.promptPendek': 'A short prompt usually produces generic video. The example skeleton is roughly 900 characters.',
    'formula.editor.s3.setBelumDiisi': 'Set {senarai} not filled in yet',
    'formula.editor.s3.tajuk': 'Write your master prompt',
    'formula.editor.s4.amaranProto': 'Prototype: the result and the text above are written in code, not AI output from your prompt.',
    'formula.editor.s4.hintUji': 'One sample product only — your own products are not touched.',
    'formula.editor.s4.barisGagal': 'video prompt empty',
    'formula.editor.s4.barisOk': 'image · video · caption',
    'formula.editor.s4.barisOk2': 'image · video scene 1 · video scene 2 · caption',
    'formula.editor.s4.belumLulus': 'Not passed yet',
    'formula.editor.s4.betulkanSet': 'Fix Set {n}',
    'formula.editor.s4.butangUji': 'Test formula · 1 quota',
    'formula.editor.s4.captionTiktok': 'TikTok caption',
    'formula.editor.s4.gagalNota': 'One set did not produce a video prompt.',
    'formula.editor.s4.hasilSet1': 'Set 1 result',
    'formula.editor.s4.kuotaNota': 'Each test uses 1 Generate quota.',
    'formula.editor.s4.lengkap': 'complete',
    'formula.editor.s4.lulusNota': 'Every required field is filled, in every set.',
    'formula.editor.s4.lulusNota2': 'Every required field is filled (including both video scenes), in every set.',
    'formula.editor.s4.nota': 'The test generates one sample product and checks every required field, in every set. This is what stops a broken prompt from becoming 20 wasted minutes of Run Flow. It uses 1 quota out of 100 a day.',
    'formula.editor.s4.promptGambar': 'Image prompt',
    'formula.editor.s4.promptVideo': 'Video prompt',
    'formula.editor.s4.promptVideo1': 'Video prompt · scene 1',
    'formula.editor.s4.promptVideo2': 'Video prompt · scene 2',
    'formula.editor.s4.sedangUji': 'Testing…',
    'formula.editor.s4.suisGagal': 'Prototype: show the FAILED state',
    'formula.editor.s4.tajuk': 'Try your prompt on one product',
    'formula.editor.s4.ujiSemula': 'Test again',
    'formula.editor.s5.huraianKosong': 'Your description appears here.',
    'formula.editor.s5.kadMeta': '{babak} scenes · {set} sets per generate',
    'formula.editor.s5.lblBentuk': 'Video format',
    'formula.editor.s5.lblMod': 'Prompt mode',
    'formula.editor.s5.lblNama': 'Name',
    'formula.editor.s5.lblPanjang': 'Prompt length',
    'formula.editor.s5.lblSet': 'Sets per Generate',
    'formula.editor.s5.lblUjian': 'Test',
    'formula.editor.s5.modSatu': 'One prompt for all sets',
    'formula.editor.s5.modSetiap': 'Each set separate ({bil} prompts)',
    'formula.editor.s5.nilaiBabak': '{n} scenes',
    'formula.editor.s5.nota': 'After Save you are taken straight to the Workspace with this formula already selected — so you can attach products and hit Generate right away.',
    'formula.editor.s5.rupaRak': 'How it looks on the shelf',
    'formula.editor.s5.tajuk': 'This is what will be saved',
    'formula.editor.sekat.bentuk': 'Pick one video format to continue.',
    'formula.editor.sekat.nama': 'Give this formula a name to continue.',
    'formula.editor.sekat.prompt': 'Write your master prompt, or press "Start from an example".',
    'formula.editor.sekat.setKosong': 'Set {senarai} is still empty — every set needs its own prompt.',
    'formula.editor.sekat.ujiBelum': 'Run the test first — it checks that your prompt really produces the required fields.',
    'formula.editor.sekat.ujiGagal': 'Fix the set that failed, then test again.',
    'formula.editor.tajuk.draf': '· draft',
    'formula.editor.tajuk.kembali': 'Back to the Formulas shelf',
    'formula.editor.tajuk.tanpaNama': 'Untitled formula',
    'formula.empty.loading.body': 'Hang on a moment. Refresh if this stays.',
    'formula.empty.loading.title': 'Loading formulas…',
    'formula.empty.noMatch.body': 'Clear the search to see every formula.',
    'formula.empty.noMatch.title': 'Nothing matches that search',
    'formula.engine.manual': 'Manual',
    'formula.engine.scene.many': '{n} scenes',
    'formula.engine.scene.one': '1 scene',
    'formula.lock.maintenance': 'This formula is under maintenance.',
    'formula.lock.noAccess': 'This formula is not unlocked for your account yet.',
    'formula.lock.trialUsed': 'Your free trial is used up. Get the add-on to continue.',
    'formula.mine.badge.draft': 'Draft',
    'formula.mine.edit': 'Edit prompt',
    'formula.mine.intro': 'With a formula you write the master prompt yourself, and it runs on the same robot as the official engines.',
    'formula.mine.meta': '{scene} scenes · {set} sets · {mode}',
    'formula.mine.mode.separate': 'prompt per set',
    'formula.mine.mode.single': 'one prompt',
    'formula.mine.prototypeNote': 'Prototype — the two cards below are sample data.',
    'formula.mine.sample.f1.desc': 'Creator shows the skin before, then the result after 3 weeks.',
    'formula.mine.sample.f1.name': 'My serum review',
    'formula.mine.sample.f2.desc': 'Open the box with an over-the-top reaction, then demo how to use it.',
    'formula.mine.sample.f2.name': 'Funny unboxing',
    'formula.mine.tip.draft': 'A draft formula must pass testing first',
    'formula.mine.tip.use': 'Use this formula in Workspace',
    'formula.mine.untested': 'Not tested yet',
    'formula.mine.use': 'Use',
    'formula.page.subtitle': 'Pick a proven recipe, drop in your products, then run.',
    'formula.page.title': 'Formulas',
    'formula.scope.community': 'Community',
    'formula.scope.engine': 'Engine',
    'formula.scope.mine': 'My formulas',
    'formula.search.placeholder': 'Search formulas',

    /* ── login ───────────────────────────────────────────────── */
    'login.backToLogin': 'Back to Sign In',
    'login.contactAdminWa': 'Contact Admin on WhatsApp',
    'login.continueEmail': 'Continue with Email',
    'login.continueGoogle': 'Continue with Google',
    'login.emailPlaceholder': 'Email',
    'login.err.badCredentials': 'That email or password is not valid.',
    'login.err.emailInUse': 'This email is already registered.',
    'login.err.emailNotFound': 'That email was not found in the system.',
    'login.err.googleDisabled': 'Google sign-in is not enabled at the moment. Please use your email and password.',
    'login.err.googleFailed': 'Google sign-in failed. Please try again.',
    'login.err.invalidEmail': 'Please enter a valid email address.',
    'login.err.invalidPhone': 'That WhatsApp number is not valid. Example: 0123456789 or +60123456789',
    'login.err.linkFailed': 'Linking the account failed. Please try again.',
    'login.err.loginFailed': 'Sign-in failed. Please check your details.',
    'login.err.popupBlocked': 'The popup was blocked by your browser. Please allow popups for this site and try again.',
    'login.err.refRequired': 'Registration requires an affiliate agent referral link. Please contact the admin.',
    'login.err.registerFailed': 'Registration failed. Please try again.',
    'login.err.resetFailed': 'The reset email could not be sent. Please try again.',
    'login.err.tooManyAttempts': 'Too many attempts. Please try again shortly.',
    'login.err.tooManyRequests': 'Too many requests. Please try again shortly.',
    'login.err.weakPassword': 'That password is too weak. Minimum 6 characters.',
    'login.err.wrongPassword': 'That password is not valid.',
    'login.existingPasswordPlaceholder': 'Password for the existing account',
    'login.feature.autoContent': 'Auto Content',
    'login.feature.autoContentDesc': 'Generate captions, images and videos automatically with AI.',
    'login.feature.more': 'And many more',
    'login.feature.moreDesc': 'Explore powerful tools built to save you time and boost your growth.',
    'login.feature.multiPlatform': 'Multi Platform',
    'login.feature.multiPlatformDesc': 'Post to TikTok, Instagram, Facebook and more from one place.',
    'login.feature.scheduling': 'Smart Scheduling',
    'login.feature.schedulingDesc': 'Schedule your posts ahead — let SESAAT handle the rest.',
    'login.forgot': 'Forgot Password?',
    'login.haveAccount': 'Already have an account?',
    'login.headlineLine1': 'Create, Generate & Post',
    'login.headlineLine2': '— Automatically',
    'login.linkDesc': 'The email {emel} already has an account. Enter your password to link Google sign-in to it.',
    'login.linkSubmit': 'Link & Sign In',
    'login.linkTitle': 'Link Your Google Account',
    'login.login': 'Sign In',
    'login.noAccount': 'Don\'t have an account?',
    'login.or': 'or',
    'login.passwordPlaceholder': 'Password',
    'login.refMissingDesc': 'Registering with SESAAT requires a referral link from an affiliate agent. Please contact the admin to get one.',
    'login.refMissingTitle': 'No agent referral link',
    'login.refOk': 'You were referred by agent: {kod}',
    'login.register': 'Sign Up',
    'login.registerLocked': 'Sign Up (agent link required)',
    'login.registerTitle': 'Create a New Account',
    'login.resetDesc': 'Enter your email and we will send you a reset link.',
    'login.resetSent': 'The reset email has been sent. Please check your inbox.',
    'login.resetTitle': 'Reset Password',
    'login.sendReset': 'Send Reset Email',
    'login.showcaseDesc': 'Created by sellers for sellers — powering product content, captions, video scripts, and more.',
    'login.showcaseTitle': 'Automate Your Content',
    'login.terms': 'By continuing, you agree to SESAAT\'s Terms of Service & Privacy Policy.',
    'login.whatsappNote': 'We will use WhatsApp to send you plan renewal reminders.',
    'login.whatsappPlaceholder': 'WhatsApp number (e.g. 0123456789)',

    /* ── notes ───────────────────────────────────────────────── */
    'notes.addNote': 'Add Note',
    'notes.bodyPlaceholder': 'Write a note or paste a link…',
    'notes.clearSearch': 'Clear Search',
    'notes.confirmDelete': 'Delete this note?',
    'notes.copyLine': 'Copy Line',
    'notes.copyTitle': 'Copy Title',
    'notes.deleteNote': 'Delete Note',
    'notes.editNote': 'Edit Note',
    'notes.emptyDesc': 'Keep the hooks, captions and settings you reuse in one place.',
    'notes.emptyTitle': 'No notes yet',
    'notes.errAdd': 'Could not save the note. Please try again.',
    'notes.errDelete': 'Could not delete the note. Please try again.',
    'notes.errUpdate': 'Could not update the note. Please try again.',
    'notes.filter.all': 'All ({n})',
    'notes.filter.pinned': 'Pinned ({n})',
    'notes.filter.quick': 'Quick Notes ({n})',
    'notes.more': 'More',
    'notes.newNote': 'New Note',
    'notes.noMatchPinned': 'No note is pinned yet. Press the pin icon on any note to keep it at the top.',
    'notes.noMatchQuick': 'No note is marked as a Quick Note yet. Press the lightning icon on any note to mark it.',
    'notes.noMatchSearch': 'No note contains that word. Try another word, or clear the search.',
    'notes.noMatchTitle': 'No matching notes',
    'notes.openMenu': 'Open menu',
    'notes.pin': 'Pin',
    'notes.pinHint': 'Pin — keeps this note at the top of the list',
    'notes.pinnedSection': 'Pinned',
    'notes.pinNote': 'Pin note',
    'notes.planNote': 'Design plan · /notes-v3.9',
    'notes.quickAdd': 'Add to Quick Notes',
    'notes.quickAddHint': 'Add to Quick Notes — this note appears in the Workspace panel while you edit prompts',
    'notes.quickHint': 'Quick Notes — this note appears in the Workspace panel while you edit prompts',
    'notes.quickNotes': 'Quick Notes',
    'notes.quickRemove': 'Remove from Quick Notes',
    'notes.quickRemoveHint': 'Remove from Quick Notes — this note stops appearing while you edit prompts',
    'notes.search': 'Search notes',
    'notes.time.days': '{n} days ago',
    'notes.time.hours': '{n}h ago',
    'notes.time.minutes': '{n} min ago',
    'notes.time.now': 'just now',
    'notes.time.yesterday': 'yesterday',
    'notes.title': 'Notes',
    'notes.titlePlaceholder': 'Title (optional)',
    'notes.unpin': 'Unpin',
    'notes.untitled': 'Untitled',

    /* ── pricing ─────────────────────────────────────────────── */
    'pricing.bonus.boost': 'Sesaat BOOST — list of products & sellers running GMV MAX from 3 MCN/TAP',
    'pricing.bonus.title': 'Bonus',
    'pricing.cta.renewSmart': 'Renew — RM99',
    'pricing.cta.subscribePrice': 'Subscribe — RM{harga}',
    'pricing.cta.subscribeSmart': 'Subscribe — RM99',
    'pricing.cta.upgradeSmart': 'Upgrade to Premium — RM100',
    'pricing.cta.upgradeSmartNote': 'One-time payment. Your subscription period stays as it is.',
    'pricing.flowUltra.detailLink': 'view details',
    'pricing.flowUltra.freeGpts': 'Free GPTS / Gems TTS & TPS',
    'pricing.flowUltra.oneMonth': 'This RM100 covers 1 month of Flow Ultra access only',
    'pricing.row.aiIncluded': 'SESAAT AI included',
    'pricing.row.cloudVideoPerDay': 'Videos saved to cloud / day',
    'pricing.row.ownFormula': 'Create Your Own Formula',
    'pricing.tier.browser3': '3 browsers to run 3 TikTok accounts',
    'pricing.tier.browser6': '6 browsers to run 6 TikTok accounts',
    'pricing.tier.fighter.priceNote': 'One payment for 12 months',
    'pricing.tier.premium.demo': 'Watch the Premium demo',
    'pricing.tier.premium.p1': 'Includes SMART — connect your own GPT / Gems',
    'pricing.tier.premium.p2': 'Create Your Own Formula',
    'pricing.tier.premium.p3': '4 ready-made modules, no GPT of your own needed',
    'pricing.tier.premium.p4': '100 generates a day (300 videos)',
    'pricing.tier.premium.subNew': 'Our AI writes it for you',
    'pricing.tier.smart.demo': 'Watch the SMART demo',
    'pricing.tier.smart.h1': 'Connect your own GPT / Gems',
    'pricing.tier.smart.h3': 'Flow automation (images + video)',
    'pricing.tier.smart.h4': 'TikTok automation (direct / draft schedule)',
    'pricing.tier.smart.h5': '300 videos saved to cloud every day',
    'pricing.tier.smart.h6': 'Group support (WhatsApp)',
    'pricing.tier.smart.priceNote': 'RM99 every month · no joining fee',
    'pricing.tier.smart.sub': 'You already have your own GPT or Gems',
    'pricing.toggle.monthly': 'Monthly',
    'pricing.toggle.yearly': 'Yearly · save RM389',
    'pricing.val.ownGpt': 'Your own GPT / Gems',
    'pricing.flowUltra.bonusCta': 'See the full details',
    'pricing.flowUltra.bonusGone': 'The first-50 bonus is finished',
    'pricing.flowUltra.bonusLeft': '{baki} / {jumlah} left',
    'pricing.flowUltra.bonusTitle': 'Bonus for the first 50',
    'pricing.smartPick.ai8saat': 'Single-scene video, quickest',
    'pricing.smartPick.goyang': 'Video using GOYANG templates',
    'pricing.smartPick.semasa': 'Your current extra module. It stays when you renew.',
    'pricing.smartPick.info': 'Every SMART customer picks one when they sign up.',
    'pricing.smartPick.sub': 'The SMART plan includes one ready-made module. Pick one — it unlocks as soon as payment clears.',
    'pricing.smartPick.title': 'Pick 1 extra module',
    'pricing.smartPick.wajib': 'Pick one module before you continue.',
    'pricing.tier.smart.h7': '1 ready-made module — pick AI8SAAT or GOYANG',
    'pricing.val.ownGptPlus': 'Your own GPT / Gems + 1 module',
    'pricing.val.pilihSatu': 'Pick 1',
    'pricing.bonus.toukey': 'Access to Toukey Viral (AIT Fighter).',
    'pricing.badge.popular': 'Popular',
    'pricing.compare.hide': 'Hide comparison',
    'pricing.compare.show': 'Compare all plans',
    'pricing.cta.continue': 'Continue',
    'pricing.cta.continuePrice': 'Continue — RM{harga}',
    'pricing.cta.currentPlan': 'Current Plan',
    'pricing.cta.renew': 'Renew',
    'pricing.cta.renewPrice': 'Renew — RM99',
    'pricing.cta.subscribe': 'Subscribe',
    'pricing.cta.subscribePremium': 'Subscribe — RM199',
    'pricing.cta.subscribePremiumUltra': 'Subscribe — RM299',
    'pricing.flowUltra.b1': 'Google Flow Ultra account (sharing) — warranty included',
    'pricing.flowUltra.b2': 'Use the "Lite (Lower Priority)" mode only — 0 credits, unlimited video',
    'pricing.flowUltra.b3': 'Login on 1 device only',
    'pricing.flowUltra.b4': 'Using credits or other modes (Omni, Fast, Quality) is not allowed',
    'pricing.gate.closedLabel': 'Temporarily Closed',
    'pricing.gate.closedNote': 'New customer registration is temporarily closed. Existing customers can renew their subscription as usual.',
    'pricing.perMonth': '/month',
    'pricing.row.browserPerAccount': 'Browsers / TikTok account',
    'pricing.row.generationsPerDay': 'Generations / day',
    'pricing.row.support': 'Support',
    'pricing.row.videoOutputPerDay': 'Video output / day',
    'pricing.section.modules': 'Modules',
    'pricing.section.toolsSupport': 'Tools & Support',
    'pricing.section.usage': 'Usage',
    'pricing.tier.fighter.h1': '6 browsers for 6 TikTok accounts',
    'pricing.tier.fighter.h2': 'Access to GOYANG, SMART & FLOWNITY',
    'pricing.tier.fighter.h3': 'Annual payment — save RM389',
    'pricing.tier.fighter.intro': 'Everything in Premium, plus:',
    'pricing.tier.fighter.period': 'per year',
    'pricing.tier.fighter.sub': 'All modules · 6 browsers',
    'pricing.tier.premium.h1': '3 browsers for 3 TikTok accounts',
    'pricing.tier.premium.h2': '100 generates per day (300 videos per day)',
    'pricing.tier.premium.h5': 'Group Support (WhatsApp)',
    'pricing.tier.premium.period': 'first month',
    'pricing.tier.premium.priceNote': 'RM99 renewal every month',
    'pricing.tier.premium.sub': 'All modules · 3 browsers',
    'pricing.val.notAvailable': 'Not Available',
    'pricing.val.whatsappGroup': 'WhatsApp Group',

    /* ── products ────────────────────────────────────────────── */
    'products.action.cancel': 'Cancel',
    'products.action.close': 'Close',
    'products.action.closeTooltip': 'Close',
    'products.action.processing': 'Processing…',
    'products.action.save': 'Save',
    'products.action.saving': 'Saving…',
    'products.actions': 'Actions',
    'products.addToWorkspace': 'Add to Workspace',
    'products.admin.genIntel': 'Generate Intelligence',
    'products.admin.genIntelTooltip': 'Run Stage 1 (vision) + Stage 2 (research) — admin pre-cache',
    'products.admin.regenIntel': 'Re-Generate Intelligence',
    'products.admin.regenIntelTooltip': 'Re-generate (bypass cache) — you pay again',
    'products.alert.addGroupFailed': 'Failed to add group: {ralat}',
    'products.alert.addManualFailed': 'Failed to add products: {ralat}',
    'products.alert.addToMineFailed': 'Failed to add to My Products: {ralat}',
    'products.alert.assignGroupFailed': 'Failed to assign group: {ralat}',
    'products.alert.bulkDeleteFailed': 'Bulk delete failed: {ralat}',
    'products.alert.bulkUploadFailed': 'Bulk upload failed: {ralat}',
    'products.alert.confirmDeleteProduct': 'Delete this product?',
    'products.alert.deleteGroupFailed': 'Failed to delete group: {ralat}',
    'products.alert.groupNameExists': 'That group name already exists.',
    'products.alert.invalidJson': 'Invalid JSON format. Please check again.',
    'products.alert.moveToGroupFailed': 'Failed to move to group: {ralat}',
    'products.alert.renameGroupFailed': 'Failed to rename group: {ralat}',
    'products.alert.replaceImageFailed': 'Failed to replace image: {ralat}',
    'products.alert.saveFailed': 'Failed to save: {ralat}',
    'products.bulk.clearing': 'Clearing...',
    'products.bulk.clearIntel': 'Clear Intel',
    'products.bulk.clearIntelTitle': 'Clear intelligence cache (admin only) — reset hasIntelligence + remove cached JSON',
    'products.bulk.copyNTo': 'Copy {n} to:',
    'products.bulk.deleteN': 'Delete ({n})',
    'products.bulk.moveNTo': 'Move {n} to:',
    'products.bulk.moveToGroup': 'Move to group',
    'products.bulk.moveToGroupTitle': 'Move selected products to a group',
    'products.bulk.moving': 'Moving...',
    'products.bulk.noGroupsForProfile': 'This account has no groups yet. Create one via the Group button.',
    'products.bulk.noGroupsYet': 'No groups created yet.',
    'products.bulk.processing': 'Processing…',
    'products.bulk.profile': 'Profile',
    'products.bulk.profileTitle': 'Move or copy the selected products to a TikTok profile',
    'products.bulk.removeGroup': 'Remove group',
    'products.bulk.sendCapped': 'A maximum of {n} products can be sent to the Workspace at once. The first {n} were sent — continue with the rest after this.',
    'products.bulk.sendChanged': 'Nothing was sent. These products changed after this page was opened (deleted, or now PENDING):\n{nama}\n\nReload this page, then try again.',
    'products.bulk.sendFailed': 'Could not send the products to the Workspace: {ralat}. Please try again.',
    'products.bulk.sendMore': 'and {n} more',
    'products.bulk.sendPending': 'Nothing was sent. These selected products are still PENDING (product data incomplete):\n{nama}\n\nUntick the PENDING products, or complete their data first, then press Send to Workspace again.',
    'products.bulk.sendToWorkspace': 'Send to Workspace',
    'products.bulk.sendToWorkspaceTitle': 'Send selected products to the Workspace',
    'products.bulk.tipEscClear': '{kekunci} to clear',
    'products.bulk.tipShiftRange': 'Tip: {kekunci} + click to select range',
    'products.bulk.unselectAll': 'Unselect all',
    'products.bulk.selectPage': 'Select page ({n})',
    'products.bulk.selectAllN': 'Select all {n}',
    'products.bulkDelete.confirmBtn': 'Delete {n} Products',
    'products.bulkDelete.deleting': 'Deleting...',
    'products.bulkDelete.msgAll': 'You are about to delete {n} products. This action cannot be undone.',
    'products.bulkDelete.msgGroup': 'You are about to delete {n} products in group "{group}". This action cannot be undone.',
    'products.bulkDelete.msgNoGroup': 'You are about to delete {n} products in "No Group". This action cannot be undone.',
    'products.bulkDelete.title': 'Delete Products?',
    'products.bulkUpload.emptyHint': 'Please enter data',
    'products.bulkUpload.formatExample': 'No *|* Product Name *|* Image URL *|* Link',
    'products.bulkUpload.formatLabel': 'Format:',
    'products.bulkUpload.formatNote': 'Or just write the product name (1 per line). The number is generated automatically.',
    'products.bulkUpload.linesDetected': '{n} lines detected',
    'products.bulkUpload.placeholder': '1 *|* Product Name A *|* https://image.url *|* https://link.url\n2 *|* Product Name B\n3 *|* Product Name C *|* https://image.url\n\nOr write just the name:\nProduct Name X\nProduct Name Y',
    'products.bulkUpload.submit': 'Upload',
    'products.bulkUpload.subtitle': 'Enter one product per line',
    'products.bulkUpload.title': 'Bulk Upload Products',
    'products.bulkUpload.uploading': 'Uploading...',
    'products.card.addToMine': 'Add to My Products',
    'products.card.alreadyInMine': 'Already in My Products',
    'products.card.completeData': 'Complete Data',
    'products.card.completeProductData': 'Complete product data',
    'products.card.copyData': 'Copy product data',
    'products.card.copyScrapeSource': 'Copy scrape source link',
    'products.card.copyShowcase': 'Copy showcase link',
    'products.card.discountLabel': 'Discount',
    'products.card.editProduct': 'Edit product',
    'products.card.editProductData': 'Edit Product Data',
    'products.card.fillDataTooltip': 'Type this product\'s data yourself',
    'products.card.genPrompt': 'Generate Prompt',
    'products.card.genPromptTooltip': 'Powered by OpenAI GPT-5 Mini',
    'products.card.idTooltip': 'TikTok product ID',
    'products.card.intelPartialTitle': 'Stage 1 or 2 partially failed',
    'products.card.intelPendingTooltip': 'No Intelligence Data yet',
    'products.card.intelReadyTooltip': 'Intelligence Data Ready',
    'products.card.noName': '(No name)',
    'products.card.originalLabel': 'Original',
    'products.card.pasteIntel': 'Paste Intelligence JSON',
    'products.card.pending': 'Pending',
    'products.card.priceLabel': 'Price',
    'products.card.ready': 'Ready',
    'products.card.replaceImage': 'Replace image',
    'products.card.scrapeSource': 'Scrape source',
    'products.card.scrapeSourceTooltip': 'Scrape source — {url}',
    'products.card.select': 'Select',
    'products.card.selectForIntel': 'Select for intel generation',
    'products.card.showcase': 'Showcase',
    'products.card.showcaseTooltip': 'Showcase — {link}',
    'products.card.soon': 'SOON',
    'products.card.tiktokIdTooltip': 'TikTok product ID',
    'products.card.unselect': 'Unselect',
    'products.card.uploading': 'Uploading...',
    'products.card.viewIntel': 'View Intelligence Data',
    'products.card.viewScraped': 'View Scraped Data',
    'products.cell.clickToType': '{label} — click to type',
    'products.changeGroup': 'Change group',
    'products.clearIntel.clearing': 'Clearing...',
    'products.clearIntel.confirmBtn': 'Clear Intel for {n} Products',
    'products.clearIntel.msg': 'You are about to clear cached intelligence (Stage 1+2) for {n} products.',
    'products.clearIntel.note': 'The products remain — only the cached intelligence is removed. Re-generating calls the API again (~${kos} cost).',
    'products.clearIntel.title': 'Clear Intelligence?',
    'products.clearSelection': 'Clear selection',
    'products.col.creator14': 'Creator 14D',
    'products.col.creator7': 'Creator 7D',
    'products.col.discount': 'Discount',
    'products.col.gmv': 'GMV (Max)',
    'products.col.gmvShort': 'GMV',
    'products.col.group': 'Group',
    'products.col.id': 'ID',
    'products.col.original': 'Original',
    'products.col.price': 'Price',
    'products.col.product': 'Product',
    'products.complete.closeAria': 'Close',
    'products.complete.descHint': 'No length limit — the more detail you give, the better the video script produced.',
    'products.complete.descPlaceholder': 'Type or paste the product details here — features, benefits, material, size, how to use…',
    'products.complete.imgHint': 'Paste an image link, or press Upload to pick an image from your device.',
    'products.complete.shopeeHint': 'For Facebook Reels — paste your Shopee affiliate link. You can leave it blank for now.',
    'products.complete.imgNotImage': 'That file is not an image. Please pick a JPG or PNG file.',
    'products.complete.imgPending': 'will be uploaded when you save',
    'products.complete.imgRemove': 'Remove',
    'products.complete.imgTooBig': 'That image is too large — 5 MB maximum.',
    'products.complete.extraLabel': 'Additional images for Flow',
    'products.complete.extraHint': 'Up to {max} extra images. Flow uses all of them as references so the product comes out accurately. Pick from the TikTok gallery (no upload needed) or upload from your device.',
    'products.complete.extraGallery': 'TikTok gallery ({n})',
    'products.complete.extraUpload': 'Upload',
    'products.complete.extraFull': 'Limit reached — remove an image first.',
    'products.complete.extraPickHint': 'Tap an image to add or remove it. Tap the magnifier icon to view it large.',
    'products.complete.extraDone': 'Done',
    'products.complete.extraPending': 'Selected files will be uploaded when you save.',
    'products.complete.kad.skopTitle': 'Apply changes to',
    'products.complete.kad.skopSemua': 'All cards + My Products',
    'products.complete.kad.skopKad': 'This card only',
    'products.complete.kad.skopHintSemua': 'The My Products item is updated, and every Workspace card of this product changes with it.',
    'products.complete.kad.skopHintKad': 'Only this card changes. My Products and other cards are left as they are.',
    'products.complete.kad.notisSystem': 'This product comes from System. Saving will copy it into your My Products — choose where to keep it below.',
    'products.complete.kad.notisBaru': 'This product is not in My Products yet. Saving will add it there — choose where to keep it below.',
    'products.complete.kad.notisTiadaLink': 'This card has no TikTok link, so it cannot be matched with My Products. Changes apply to this card only.',
    'products.complete.kad.notisShopee': 'Shopee product — the data below comes from your My Products. Changes apply to this card only.',
    'products.complete.sah.tajuk': 'This change affects {n} places',
    'products.complete.sah.tajukSemua': 'This change affects My Products and every card of this product',
    'products.complete.sah.item': 'This item in My Products',
    'products.complete.sah.itemBaru': 'A new item added to My Products',
    'products.complete.sah.kad': 'This card + {n} other cards of the same product',
    'products.complete.sah.kadSemua': 'This card and every other card of the same product',
    'products.complete.sah.nota': 'The image, links, price and product data in all the places above will be replaced. Other cards keep their own names.',
    'products.complete.sah.ya': 'Change My Products + all {n} cards',
    'products.complete.sah.yaSemua': 'Change My Products + every card of this product',
    'products.complete.sah.yaBaru': 'Add to My Products + change all {n} cards',
    'products.complete.sah.yaBaruSemua': 'Add to My Products + change every card of this product',
    'products.complete.sah.kadSahaja': 'Change this card only',
    'products.complete.kad.profil': 'Profile',
    'products.complete.kad.group': 'Group',
    'products.complete.kad.tiadaProfil': 'No profile',
    'products.complete.kad.tiadaGroup': 'No group',
    'products.complete.kad.gagalKad': 'Saved to My Products, but the Workspace cards could not be updated. Open Edit data again and save once more.',
    'products.complete.lbl.detail': 'Product Details',
    'products.complete.lbl.nama': 'Product Name',
    'products.complete.lbl.gambar': 'Product Image',
    'products.complete.lbl.tiktok': 'TikTok Link',
    'products.complete.lbl.nombor': 'Number',
    'products.complete.status.ready': "Ready — this product can run in Workspace.",
    'products.complete.status.notReady': "Not enough yet — this product is PENDING and cannot run in Workspace.",
    'products.complete.chk.nama': "Name",
    'products.complete.chk.gambar': "Image",
    'products.complete.chk.link': "Product link",
    'products.complete.sek.produk': "Product",
    'products.complete.sek.linkHarga': "Links and price",
    'products.complete.lbl.linkGambar': "Image link",
    'products.complete.linksHint': "One link is enough. The TikTok link is for the TikTok yellow bag, the Shopee link is for Facebook Reels.",
    'products.complete.extraGalleryShopee': "Shopee gallery ({n})",
    'products.complete.extraHintUmum': "Up to {max} extra images. Flow uses all of them as references so the product comes out accurately. Pick from the product gallery (no upload needed) or upload from your device.",
    'products.complete.butiran.tajuk': "More details for a sharper script",
    'products.complete.butiran.kira': "{n} of {total} filled",
    'products.complete.kakiNota': "Changes apply to the next generate.",
    'gbrBesar.galeriShopee': "Shopee gallery",
    'products.complete.lbl.links': 'Product Links',
    'products.complete.linkTiktokHint': 'TikTok Shop product link — needed for the TikTok yellow bag.',
    'products.complete.sumber.pill': 'Product data scraped from {platform}',
    'products.complete.sumber.short': 'Source: {platform}',
    'products.complete.lbl.shopee': 'Shopee Affiliate Link',
    'products.complete.lbl.harga': 'Price',
    'products.complete.lbl.hargaAsal': 'Original Price',
    'products.complete.lbl.diskaun': 'Discount',
    'products.complete.lbl.skrip': 'Script Data',
    'products.complete.lbl.desc': 'Description',
    'products.complete.lbl.kategori': 'Category',
    'products.complete.lbl.jenama': 'Brand',
    'products.complete.lbl.kedai': 'Shop Name',
    'products.complete.lbl.rating': 'Rating',
    'products.complete.lbl.ratingCount': 'Rating Count',
    'products.complete.lbl.terjual': 'Sold Count',
    'products.complete.lbl.variasi': 'Variants',
    'products.complete.lbl.kelebihan': 'Selling Points',
    'products.complete.lbl.spek': 'Specifications',
    'products.complete.lbl.ulasan': 'Customer Reviews',
    'products.complete.lbl.idProduk': 'Product ID (TikTok link)',
    'products.complete.lbl.namaPh': 'Product name',
    'products.complete.intro.edit': 'The details below are the material used to produce the video script for this product. You can correct or add to them at any time — changes apply only to the next generate; scripts already produced do not change.',
    'products.complete.intro.fill': 'Some sellers put the product details inside the image only, so that information cannot be captured automatically. Please retype it here. Once saved, this product is ready to use.',
    'products.complete.priceNote': 'This price is used on the card AND in the video script — one place only, so the two can never disagree.',
    'products.complete.bonusNote': 'The fields above are optional. Everything you fill in is sent to the AI and sharpens your video script.',
    'products.complete.saveFailed': 'Failed to save. Please try again.',
    /* Tiada emoji — v3.9 monokrom; amaran dibawa oleh warna, bukan ikon. */
    'products.complete.status.downgrade': 'Not enough yet — this product will drop back to PENDING after saving.',
    'products.complete.status.readyBecome': 'Enough — this product will turn READY after saving.',
    'products.complete.status.readyKeep': 'Enough — this product stays READY after saving.',
    'products.complete.status.stayPending': 'Not enough yet — the product stays PENDING after saving.',
    'products.complete.title.edit': 'Product Data',
    'products.complete.title.fill': 'Product Data',
    'products.confirm.deleteGroup': 'Delete this group? Products inside it will not be deleted, but they will no longer belong to any group.',
    'products.copyToMine': 'Copy to Mine',
    'products.countPending': '{n} pending',
    'products.countReady': '{n} ready',
    'products.countTotal': '{n} Total',
    'products.delete': 'Delete',
    'products.edit.discountPlaceholder': 'Discount',
    'products.edit.linkPlaceholder': 'TikTok Shop link',
    'products.edit.namePlaceholder': 'Product name',
    'products.edit.numberPlaceholder': 'No. / Product ID',
    'products.edit.originalPlaceholder': 'Original',
    'products.edit.priceHint': '💡 Auto-prefilled from the scrape. Price + discount are injected into the script prompt (e.g. the TIBA-TIBA hook fragment). Make sure they are correct.',
    'products.edit.pricePlaceholder': 'Price',
    'products.empty.adminDesc': 'Click "Bulk Upload" to add products in bulk.',
    'products.empty.bulkUploadCta': 'Bulk Upload Products',
    'products.empty.filterDesc': 'No products in this category',
    'products.empty.noneTitle': 'No products yet',
    'products.empty.notFoundTitle': 'No products found',
    'products.empty.ownDesc': 'No My Products yet. Add your own products.',
    'products.empty.searchDesc': 'No products match "{carian}"',
    'products.empty.systemDesc': 'The admin has not added any products yet.',
    'products.emptyImport': 'Import products from TikTok Shop to get started.',
    'products.emptySearch': 'Clear the search to see everything.',
    'products.emptyTitle': 'No products here',
    'products.folders.editNameTitle': 'Edit folder name',
    'products.folders.emptyDesc': 'The Library will be set up by an admin.',
    'products.folders.emptyTitle': 'No folders',
    'products.folders.noDescription': 'No description',
    'products.folders.subtitle': 'Shared resource hub — product data, hooks, eCourse and more',
    'products.folders.title': 'Library',
    'products.group.all': 'All groups',
    'products.group.fallbackName': 'Group',
    'products.group.moveGroupTitle': 'Move every product in this group to one profile',
    'products.group.moveGroupToProfile': 'Move group to profile',
    'products.group.none': 'No group',
    'products.group.pick': 'Select group',
    'products.groups.addBtn': 'Add',
    'products.groups.addNewTitle': 'Add New Group',
    'products.groups.deleteTooltip': 'Delete group',
    'products.groups.empty': 'No groups yet. Create your first group below.',
    'products.groups.namePlaceholder': 'Group name (e.g. Clothing)',
    'products.groups.newForProfile': 'This group belongs to account: {nama}',
    'products.groups.newShared': 'No account selected — this group will be shared (no account). Pick an account first for an account-specific group.',
    'products.groups.productCount': '{n} products',
    'products.groups.renameTooltip': 'Rename group',
    'products.groups.subtitle': 'Create groups to separate products by category',
    'products.groups.title': 'Manage Product Groups',
    'products.import': 'Import products',
    'products.intel.clear': 'Clear',
    'products.intel.clearData': 'Delete Intelligence Data',
    'products.intel.clearFailed': 'Failed to clear intelligence: {ralat}',
    'products.intel.confirmRegenerate': 'Re-generate intelligence for "{nama}"?\n\nThis will replace the existing cache (cost ~${kos}).',
    'products.intel.copyJson': 'Copy JSON',
    'products.intel.editSubtitle': 'Paste Intelligence data (JSON format)',
    'products.intel.editTitle': 'Intelligence JSON — #{no} {nama}',
    'products.intel.errors': '({n} error)',
    'products.intel.generateN': 'Generate ({n})',
    'products.intel.jsonInvalid': 'Invalid JSON',
    'products.intel.jsonValid': 'JSON valid',
    'products.intel.loading': 'Loading intelligence data...',
    'products.intel.loadingExisting': 'Loading existing intelligence...',
    'products.intel.noneWithIntel': 'None of the selected products have intelligence. Please select products with the READY badge.',
    'products.intel.progress': 'Generating intelligence — {siap}/{jumlah} done',
    'products.intel.regenerate': 'Re-Generate',
    'products.intel.regenTitle': 'Resubmit — bypass the cache & re-generate (costs again)',
    'products.intel.running': '({n} running)',
    'products.intel.selectAllPending': 'Select all pending',
    'products.intel.status.done': 'Done',
    'products.intel.status.failed': 'Failed',
    'products.intel.status.generating': 'Generating',
    'products.intel.status.partial': 'Partial',
    'products.intel.status.queued': 'Queued',
    'products.intel.stop': 'Stop',
    'products.intel.viewSubtitle': 'Intelligence data used to generate prompts',
    'products.intel.viewTitle': 'Intelligence Data — #{no} {nama}',
    'products.intelBulk.cacheHit': 'Cache hit (skip, free)',
    'products.intelBulk.estCost': 'Estimated cost (USD)',
    'products.intelBulk.noteForce': 'Resubmit mode — bypasses the cache. Every item calls OpenAI even if it already has intelligence.',
    'products.intelBulk.noteNormal': 'Parallel max {n} concurrent. You can Stop at any time.',
    'products.intelBulk.run': 'Generate Now',
    'products.intelBulk.runForce': 'Re-Generate Now',
    'products.intelBulk.selected': 'Items selected',
    'products.intelBulk.willCall': 'Will call the API',
    'products.manual.addMore': 'Add another',
    'products.manual.imageUrlLabel': 'Image URL',
    'products.manual.imageUrlPlaceholder': 'https://... or click the thumbnail to upload',
    'products.manual.linkLabel': 'TikTok link',
    'products.manual.nameExample': 'e.g. Baju Kurung Moden',
    'products.manual.nameLabel': 'Product Name',
    'products.manual.namePlaceholder': 'Product name...',
    'products.manual.numberLabel': 'No.',
    'products.manual.productIdPlaceholder': 'Product ID',
    'products.manual.productIdTooltip': 'Enter the Product ID only',
    'products.manual.saving': 'Saving...',
    'products.manual.submit': 'Add ({n})',
    'products.manual.subtitle': 'Enter product data manually into the Library',
    'products.manual.title': 'Add Product Manually',
    'products.manual.upload': 'Upload',
    // ── Troli Marketplace (beli banyak barang sekali bayar) ──
    'market.cart.title': 'Cart',
    'market.cart.subtitle': 'Review your items before paying.',
    'market.cart.back': 'Back to Marketplace',
    'market.cart.emptyTitle': 'Your cart is empty',
    'market.cart.emptyDesc': 'Pick items in the Marketplace, then come back here to pay for them all at once.',
    'market.cart.browse': 'Browse Marketplace',
    'market.cart.summary': 'Summary',
    'market.cart.units': '{n} units',
    'market.cart.total': 'Total',
    'market.cart.checkout': 'Pay {jumlah}',
    'market.cart.preparing': 'Preparing…',
    'market.cart.payNote': 'You will be taken to the Billplz payment page (FPX or card). Once payment is complete, your access is released in My Purchases.',
    'market.cart.whatsapp': 'WhatsApp number (optional — so admin can reach you)',
    'market.cart.whatsappPh': 'e.g. 60123456789',
    'market.cart.remove': 'Remove from cart',
    'market.cart.less': 'Decrease',
    'market.cart.more': 'Increase',
    'market.cart.stockLeft': 'Only {n} left',
    'market.cart.maxStock': 'That is all the stock available',
    'market.cart.rentalOne': 'Rental — one account only',
    'market.cart.soldOut': 'Out of stock',
    'market.cart.gone': 'Product no longer available',
    'market.cart.withdrawn': 'Withdrawn from sale',
    'market.cart.isFree': 'Free product — claim it on the product page',
    'market.cart.skipNote': '{n} item(s) cannot be purchased right now and are excluded from the total. Remove them or try again later.',
    'market.cart.loadFailed': 'Failed to load cart: {ralat}',
    'market.cart.payFailed': 'Failed to start payment: {ralat}',
    'market.cart.loginFirst': 'Please log in first.',
    'market.cart.addToCart': 'Add to Cart',
    'market.cart.added': 'Added to cart ({n} units).',
    'market.cart.full': 'Cart is full — maximum {n} product types.',
    'market.cart.qtyLabel': 'Quantity',

    'products.market.cat.all': 'All',
    'products.market.cat.ebook': 'Ebook',
    'products.market.cat.flowUltra': 'Flow Ultra',
    'products.market.cat.tiktok': 'TikTok Account',
    'products.market.demo.empty': 'Empty',
    'products.market.demo.loading': 'Loading',
    'products.market.demo.normal': 'Normal',
    'products.market.emptyDesc': 'New products are added from time to time. Please try another category for now.',
    'products.market.emptyTitle': 'No products in this category',
    'products.market.free': 'FREE',
    'products.market.loadFailed': 'Failed to load the marketplace: {ralat}',
    'products.market.myPurchases': 'My Purchases',
    'products.market.planNote': 'Design plan · /marketplace-v3.9',
    'products.market.soldOut': 'SOLD OUT',
    'products.market.stockLeft': 'Only {n} left',
    'products.market.subtitle': 'TikTok accounts, Flow Ultra and ebooks — ready to buy.',
    'products.market.title': 'Marketplace',
    'products.moveGroup.desc': 'The group {group} and all products in it will belong to the account you pick. After this the group only appears under that account.',
    'products.moveGroup.noProfiles': 'No profiles yet. Add a profile first.',
    'products.moveGroup.profileProductCount': '{n} products',
    'products.moveGroup.removeFromProfile': 'Remove from profile',
    'products.moveGroup.title': 'Move group to profile',
    'products.noGroup': 'No group',
    'products.openInTikTok': 'Open in TikTok Shop',
    'products.pagination.next': 'Next →',
    'products.pagination.prev': '← Prev',
    'products.pagination.showing': 'Showing {dari}–{hingga} of {jumlah}',
    /* Sebab kad PENDING — dipulangkan oleh services/productReadyService.js.
       {medan} ialah senarai nama medan yang ditulis semasa scrape
       (Description, Price…) — ia data, jadi ia tidak diterjemah. */
    'products.pending.incomplete': 'Product data is incomplete.',
    'products.pending.missing': 'Missing: {medan}',
    'products.pending.noData': 'No product data yet — scrape it using the Import Products button.',
    'products.pendingLock': 'PENDING — no product data',
    'products.pendingNote': 'PENDING products do not have product data yet, so they cannot be selected. Please complete them using the Import Products button first.',
    'products.profile.active': 'Active',
    'products.profile.add': 'Add profile',
    'products.profile.addTitle': 'Add a TikTok profile',
    'products.profile.allAlreadyExist': 'All {n} selected products already exist in this profile.',
    'products.profile.allSub': 'Includes those without a profile',
    'products.profile.allTitle': 'All products',
    'products.profile.confirmDelete': 'Delete profile "{nama}"?',
    'products.profile.confirmDeleteWithProducts': 'Delete profile "{nama}"?\n\nThe {n} products inside will NOT be deleted — they will go back to "No profile".',
    'products.profile.copy': 'Copy',
    'products.profile.copyFailed': 'Failed to copy to profile: {ralat}',
    'products.profile.copyResultCopied': '{n} products copied',
    'products.profile.copyResultFailed': '{n} failed',
    'products.profile.copyResultSkipped': '{n} skipped (already exist)',
    'products.profile.deleteFailed': 'Failed to delete profile: {ralat}',
    'products.profile.editTooltip': 'Edit profile',
    'products.profile.emptyMenu': 'No profiles yet. Add a profile first.',
    'products.profile.groupEmpty': 'No products in this group.',
    'products.profile.groups': '{n} groups',
    'products.profile.group1': '{n} group',
    'products.profile.lastUsed': 'Last: {v}',
    'products.profile.limitReached': 'The limit of {n} profiles has been reached.',
    'products.profile.move': 'Move',
    'products.profile.moveFailed': 'Failed to move to profile: {ralat}',
    'products.profile.moveGroupFailed': 'Failed to move group: {ralat}',
    'products.profile.nameExists': 'This profile name already exists.',
    'products.profile.nameRequired': 'Please enter a profile name.',
    'products.profile.noUsername': 'No username',
    'products.profile.pending': '{n} Pending',
    'products.profile.products': '{n} products',
    'products.profile.product1': '{n} product',
    'products.profile.ready': '{n} Ready',
    'products.profile.removeFromProfile': 'Remove from profile',
    'products.profile.saveFailed': 'Failed to save profile: {ralat}',
    'products.profile.slot': 'Profile {n}',
    'products.profile.syncHint': 'Sync Showcase to start',
    'products.profile.unused': 'Not in use yet',
    'products.profile.withoutProfile': 'No profile {n}',
    'products.profile.withoutProfileTip': 'Show only products that are not in any profile yet',
    'products.profileModal.addTitle': 'Add TikTok Profile',
    'products.profileModal.colorLabel': 'Colour',
    'products.profileModal.desc': 'Each profile represents one of your TikTok accounts. Products can be moved between profiles at any time.',
    'products.profileModal.editTitle': 'Edit Profile',
    'products.profileModal.iconLabel': 'Icon',
    'products.profileModal.iconLetter': 'Letter',
    'products.profileModal.nameLabel': 'Profile name',
    'products.profileModal.namePlaceholder': 'e.g. Main TT',
    'products.profileModal.usernameLabel': 'TikTok username (optional)',
    'products.profileModal.usernamePlaceholder': 'e.g. ttutama.my',
    'products.prototypeNote': 'Prototype · /products-v3.9',
    'products.scope.mine': 'Mine',
    'products.scope.system': 'System',
    'products.scraped.badge.sourceTooltip': 'Data from {source}',
    'products.scraped.badge.title': 'Scraped Data',
    'products.scraped.close': 'Close',
    'products.scraped.closeAria': 'Close',
    'products.scraped.legend.source': 'Source:',
    'products.scraped.row.affiliateLink': 'Affiliate Link',
    'products.scraped.row.avgUnitPrice': 'Avg Unit Price',
    'products.scraped.row.badges': 'Badges',
    'products.scraped.row.brand': 'Brand',
    'products.scraped.row.category': 'Category',
    'products.scraped.row.commissionAmount': 'Commission Amount',
    'products.scraped.row.commissionRate': 'Commission Rate',
    'products.scraped.row.creatorConvRatio': 'Creator Conv. Ratio',
    'products.scraped.row.creatorNumber': 'Creator Number',
    'products.scraped.row.eta': 'ETA',
    'products.scraped.row.followers': 'Followers',
    'products.scraped.row.gallery': 'Gallery',
    'products.scraped.row.images': 'Images',
    'products.scraped.row.kalodataUrl': 'Kalodata URL',
    'products.scraped.row.launchDate': 'Launch Date',
    'products.scraped.row.liveRevenue': 'Live Revenue',
    'products.scraped.row.mallRevenue': 'Shopping Mall Rev',
    'products.scraped.row.originalPrice': 'Original Price',
    'products.scraped.row.points': 'Points',
    'products.scraped.row.primaryImage': 'Primary Image',
    'products.scraped.row.productId': 'Product ID',
    'products.scraped.row.productVideo': 'Product Video',
    'products.scraped.row.promoEnds': 'Promo Ends',
    'products.scraped.row.promoLabel': 'Promo Label',
    'products.scraped.row.rating': 'Rating',
    'products.scraped.row.ratingCount': 'Rating Count',
    'products.scraped.row.resolvedUrl': 'Resolved URL',
    'products.scraped.row.responseRate': 'Response Rate',
    'products.scraped.row.returnPolicy': 'Return Policy',
    'products.scraped.row.revenue': 'Revenue',
    'products.scraped.row.shipFrom': 'Ship From',
    'products.scraped.row.shippingFee': 'Shipping Fee',
    'products.scraped.row.shopBadges': 'Shop Badges',
    'products.scraped.row.shopName': 'Shop Name',
    'products.scraped.row.shopRating': 'Shop Rating',
    'products.scraped.row.shopUrl': 'Shop URL',
    'products.scraped.row.sold': 'Sold',
    'products.scraped.row.source': 'Source',
    /* Jamak — baris berasingan daripada 'row.source' di atas. Tanpa kunci ini
       pelanggan nampak nama kunci mentah tercetak sebagai label. */
    'products.scraped.row.sources': 'Sources',
    'products.scraped.row.sourceUrl': 'Source URL',
    'products.scraped.row.specifications': 'Specifications',
    'products.scraped.row.stockCount': 'Stock Count',
    'products.scraped.row.stockStatus': 'Stock Status',
    'products.scraped.row.tags': 'Tags',
    'products.scraped.row.text': 'Text',
    'products.scraped.row.topReviews': 'Top Reviews',
    'products.scraped.row.variants': 'Variants',
    'products.scraped.row.videoRevenue': 'Video Revenue',
    'products.scraped.row.videos': 'Videos',
    'products.scraped.section.affiliate': 'Affiliate',
    'products.scraped.section.analytics': 'Analytics (Kalodata)',
    'products.scraped.section.basic': 'Basic',
    'products.scraped.section.content': 'Content',
    'products.scraped.section.description': 'Description',
    'products.scraped.section.extraFields': 'Extra Fields',
    'products.scraped.section.gallery': 'Product Gallery',
    'products.scraped.section.meta': 'Meta',
    'products.scraped.section.pricePromo': 'Price & Promo',
    'products.scraped.section.rating': 'Rating',
    'products.scraped.section.sellingPoints': 'Selling Points',
    'products.scraped.section.shippingReturn': 'Shipping & Return',
    'products.scraped.section.shop': 'Shop',
    'products.scraped.section.variantsStock': 'Variants & Stock',
    'products.scraped.variants.name': 'Name',
    'products.scraped.variants.stock': 'Stock',
    'products.searchPlaceholder': 'Search products',
    'products.selectAll': 'Select all',
    'products.selectedCount': '{n} selected',
    'products.selectProduct': 'Select product',
    'products.setGroup': 'Set…',
    'products.sort.label': 'Sort',
    'products.sort.name': 'Sort: Name',
    'products.sort.newest': 'Sort: Newest',
    'products.sort.number': 'Sort: Number',
    'products.status.pending': 'PENDING',
    'products.status.ready': 'READY',
    'products.subtitle': 'Your saved products. Pull them into the Workspace when you want to run them.',
    'products.tab.myProducts': 'My Products',
    'products.table.completeData': 'Complete product data',
    'products.table.dataIncomplete': 'Product data is incomplete',
    'products.table.deleteProduct': 'Delete product',
    'products.table.editData': 'Edit product data',
    'products.table.editProduct': 'Edit product',
    'products.table.groupAria': 'Product group',
    'products.table.selectAllPage': 'Select everything on this page',
    'products.time.daysAgo': '{n}d ago',
    'products.time.monthsAgo': '{n}mo ago',
    'products.time.today': 'today',
    'products.time.weeksAgo': '{n}w ago',
    'products.time.yearsAgo': '{n}y ago',
    'products.time.yesterday': 'yesterday',
    'products.title': 'Products',
    'products.toolbar.bulkUpload': 'Bulk Upload',
    'products.toolbar.group': 'Group',
    'products.toolbar.intelSelectedN': 'Selected: {n}',
    'products.toolbar.intelSelectTitle': 'Select many items to generate intelligence in one go',
    'products.toolbar.import': 'Import Products',
    'products.toolbar.manual': 'Manual',
    // ── Import stepper (Fasa 2) ──
    'import.title': 'Import Products',
    'import.step.setup': 'Setup',
    'import.step.scrape': 'Scrape',
    'import.step.review': 'Review',
    'import.setup.linksLabel': 'Paste TikTok product links (one or many)',
    'import.setup.note': 'The extension does the scraping behind the scenes. If a captcha appears, it will show up in the TikTok tab for you to solve.',
    'import.setup.count': 'links detected',
    'import.ext.on': 'SESAAT extension active',
    'import.ext.off': 'Extension not connected yet.',
    'import.ext.reconnect': 'Click the button below to connect.',
    'import.ext.refreshBtn': 'Refresh page',
    'import.ext.check': 'Checking extension…',
    'import.captcha': 'Captcha appeared — please solve it in the open TikTok tab. Scraping resumes automatically after.',
    'import.scrape.ok': 'done',
    'import.scrape.fail': 'failed',
    'import.scrape.now': 'Scraping: ',
    'import.scrape.starting': 'Connecting to extension…',
    'import.scrape.finishing': 'Finishing…',
    'import.scrape.running': 'Extension is working…',
    'import.scrape.stop': 'Stop',
    'import.review.summary': '{ok} products succeeded ({ready} READY). {fail} failed.',
    'import.review.exists': 'ALREADY IN',
    'import.review.failed': 'FAILED',
    'import.review.dest': 'Products will be saved to account: {nama}',
    'import.review.destNone': 'Products will be saved without an account — they will sit in "All products". Open an account card first if you want them to go straight into that account.',
    'import.review.group': 'Group',
    'import.review.noGroup': 'No group',
    'import.review.back': '← Import more',
    'import.review.empty': 'No results. Go back to Setup to start.',
    'import.bulk.all': 'Select all',
    'import.bulk.n': '{n} selected',
    'import.bulk.move': 'Move to Group',
    'import.bulk.remove': 'Remove',
    'import.card.scraping': 'Scraping…',
    'import.card.failed': 'Scrape failed',
    'import.card.price': 'Price',
    'import.card.remove': 'Remove this product',
    'import.card.edit': 'Edit Manually',
    'import.card.retry': 'Retry',
    'import.card.fill': 'Complete Data',
    'import.card.pendingWarn': 'Not enough data — will save as PENDING',
    'import.card.stillNeed': 'Still need',
    'import.copy.view': '✓ Product link copied',
    'import.copy.viewBtn': 'Product link',
    'import.copy.src': '✓ Source link copied',
    'import.copy.srcBtn': 'Source link',
    'import.copyFail': 'Copy failed',
    'import.group.pick': 'Pick Group',
    'import.group.none': 'No Group',
    'import.group.new': 'New Group',
    'import.group.name': 'Group name',
    'import.group.saving': 'Saving…',
    'import.group.saveUse': 'Save & Use',
    'import.group.dup': 'Group name already exists',
    'import.group.addFail': 'Failed to add group',
    'import.group.noneForProfile': 'This account has no groups yet',
    'import.group.pickProfile': 'Pick an account first for an account-specific group',
    'import.edit.title': 'Edit Product Manually',
    'import.edit.hint': 'Fill in the product data yourself. Name, Price & Description matter most for READY.',
    'import.edit.name': 'Product Name *',
    'import.edit.price': 'Price *',
    'import.edit.orig': 'Original Price',
    'import.edit.disc': 'Discount %',
    'import.edit.cat': 'Category',
    'import.edit.img': 'Image URL',
    'import.edit.desc': 'Description *',
    'import.edit.descPh': 'What is this product, its benefits, materials, how to use…',
    'import.edit.spec': 'Specification',
    'import.edit.rev': 'Customer Reviews',
    'import.edit.ok': '✓ Enough — this product will be READY',
    'import.edit.notReady': '⚠ Not enough for READY yet',
    'import.edit.need': 'Name and Price are required',
    'import.edit.saved': '✓ Product updated',
    'import.edit.save': 'Save',
    'import.saved': '{n} cards saved to My Products',
    'import.saveFail': 'Save failed — try again',
    'import.cancel': 'Cancel',
    'import.start': 'Start scrape',
    'import.save': 'Save {n} cards',
    'import.saving': 'Saving…',
    'import.mode.link': 'Paste Link',
    'import.mode.showcase': 'Scrape Showcase',
    'import.showcase.note': 'The extension will open your Seller showcase, collect all products, then you pick which ones to import. Make sure you are logged in to TikTok Seller.',
    'import.showcase.collect': 'Collect from Showcase',
    'import.showcase.fromPage': 'From page',
    'import.showcase.toPage': 'To page',
    'import.showcase.toPagePh': 'all',
    'import.showcase.pageHint': '1 page = 20 products. Leave "To page" empty to collect all.',
    'import.showcase.pageErr': '"To page" must be ≥ start page ({start}).',
    'import.showcase.collecting': 'Collecting… page {page} · {found} products',
    'import.showcase.search': 'Search product name…',
    'import.showcase.all': 'Select all',
    'import.showcase.none': 'Deselect',
    'import.showcase.selected': '{n} of {total} selected',
    'import.showcase.chosen': 'products selected',
    'import.showcase.timeout': 'No response from the extension — make sure it is active.',
    'import.showcase.fail': 'Failed to collect showcase.',
    'import.showcase.empty': 'No products found — make sure you are logged in to TikTok Seller.',
    // ── Import stepper — Shopee tab (2026-10-03) ──
    'importShopee.ext.old': 'Your extension does not support Shopee import yet. Please install the latest extension version, then refresh this page.',
    'importShopee.close': 'Close',
    'importShopee.setup.linksLabel': 'Paste Shopee product links (one or many)',
    'importShopee.setup.skipped': 'Ignored {n} link(s) — not a Shopee Malaysia product page.',
    'importShopee.setup.autoAff': 'Convert to affiliate links automatically',
    'importShopee.setup.subId': 'Sub ID',
    'importShopee.setup.subIdHint': 'Letters and numbers only. The Sub ID helps you track clicks in your Shopee Affiliate reports.',
    'importShopee.setup.noteAff': 'The extension will open each product page in this browser, then convert the links in Shopee Affiliate. Make sure you are logged in to Shopee and Shopee Affiliate (an approved affiliate account).',
    'importShopee.setup.note': 'The extension will open each product page in this browser. Make sure you are logged in to Shopee.',
    'importShopee.mode.storefront': 'Scrape Storefront',
    'importShopee.sf.urlLabel': 'Storefront link',
    'importShopee.sf.maksLabel': 'Number of products (latest first)',
    'importShopee.sf.maksHint': 'Products are collected in the storefront\'s "Latest" order. Maximum {maks}.',
    'importShopee.sf.collect': 'Collect from Storefront',
    'importShopee.sf.collecting': 'Collecting products from the storefront…',
    'importShopee.sf.wait': 'The extension is opening the storefront and scrolling the product list. This can take 1–2 minutes — do not close that tab.',
    'importShopee.sf.errUrl': 'Invalid storefront link. Example: https://collshp.com/yourname',
    'importShopee.sf.fail': 'Failed to collect products from the storefront.',
    'importShopee.sf.empty': 'No products found in this storefront.',
    'importShopee.sf.partial': 'The storefront list stopped loading before the end — some products may not have been collected.',
    'importShopee.sf.soldOutBtn': 'Sold Out ({n})',
    'importShopee.sf.soldOutTipOff': 'Untick all Sold Out products',
    'importShopee.sf.soldOutTipOn': 'Tick all Sold Out products again',
    'importShopee.sf.pickPh': 'Choose a storefront…',
    'importShopee.sf.edit': 'Edit',
    'importShopee.sf.savedLabel': 'Saved storefronts',
    'importShopee.sf.newOption': '+ New storefront',
    'importShopee.sf.nameLabel': 'Name',
    'importShopee.sf.namePh': 'e.g. Main shop',
    'importShopee.sf.saveNew': 'Save this storefront',
    'importShopee.sf.saveEdit': 'Save changes',
    'importShopee.sf.remove': 'Remove from list',
    'importShopee.sf.saved': 'Storefront saved',
    'importShopee.sf.removed': 'Storefront removed',
    'importShopee.sf.saveFail': 'Could not save the storefront — try again.',
    'importShopee.sf.dupUrl': 'This storefront link is already saved under another name.',
    'importShopee.sf.full': 'Maximum {maks} saved storefronts. Remove one first.',
    'importShopee.sf.again': 'Collect again',
    'importShopee.aff.running': 'Converting affiliate links… {siap} / {jumlah}',
    'importShopee.row.ok': 'Done',
    'importShopee.row.fail': 'Failed',
    'importShopee.err.notReached': 'Not scraped yet — the batch was stopped. Press Retry.',
    'importShopee.notice.stopped': 'Scraping stopped early. Check the reason on the failed products, then press Retry.',
    'importShopee.bulk.retry': 'Retry all failed ({n})',
    'importShopee.bulk.aff': 'Convert affiliate links ({n})',
    'importShopee.card.idTip': 'Shopee product ID (shop.product)',
    'importShopee.card.dup': 'SAME PRODUCT',
    'importShopee.card.dupNote': 'This link points to the same product as another row — it will not be saved twice.',
    'importShopee.card.sold': 'sold',
    'importShopee.card.aff': 'Affiliate link',
    'importShopee.card.affNone': 'No affiliate link yet.',
    'importShopee.card.affOff': 'Automatic conversion is switched off.',
    'importShopee.card.edit': 'Edit data',
    'importShopee.copy.aff': 'Affiliate link copied',
    'importShopee.copy.btn': 'Copy',
    'importShopee.save': 'Save {n} products',
    'importShopee.saved': '{n} products saved to My Products',
    'importShopee.saveFail': 'Save failed — try again',
    'importShopee.save.rejected': '{n} products were NOT saved:',
    'importShopee.edit.title': 'Edit product data',
    'importShopee.edit.hint': 'Name, image and description are needed to become READY.',
    'importShopee.edit.price': 'Price',
    'importShopee.edit.img': 'Image URL *',
    'importShopee.edit.ok': 'Enough — this product will be READY',
    'importShopee.edit.notReady': 'Not enough for READY yet',
    'importShopee.edit.needName': 'Product name is required',
    'importShopee.edit.needImg': 'Image URL must start with https://',
    'importShopee.edit.noId': 'This link has no product ID — it cannot be filled in manually',
    'importShopee.edit.saved': 'Product updated',
    'products.toolbar.selectForIntel': 'Select for Intel',
    'products.view.grid': 'Grid view',
    'products.view.table': 'Table view',
    'products.view.imgSizeTip': 'Image size: {saiz} — click to change',
    'products.view.imgS': 'Small',
    'products.view.imgM': 'Medium',
    'products.view.imgL': 'Large',

    /* ── prostudio ───────────────────────────────────────────── */
    'prostudio.agent.mockBadge': 'Display mock-up',
    'prostudio.backToHub': 'Back to PRO Studio',
    'prostudio.cfg.adv': 'Advanced settings',
    'prostudio.cfg.advHint': 'Only needed when something is not right',
    'prostudio.cfg.again': 'Configure again',
    'prostudio.cfg.backResult': 'Back to the result',
    'prostudio.cfg.blokAjar': 'A block is just a piece of heading text. Pick the LABEL — HOOK: — not the answer under it. The label appears on every set and on every product you run later; the answer only appears once.',
    'prostudio.cfg.blokBuang': 'Remove',
    'prostudio.cfg.blokCount': '{n} of your own',
    'prostudio.cfg.blokDaripadaPilihan': 'Make a block from the selection',
    'prostudio.cfg.blokHint': 'When the detected blocks are not the right shape',
    'prostudio.cfg.blokJumpa': 'Matches {n}× in the answer',
    'prostudio.cfg.blokPesan.kosong': 'Nothing was highlighted. Drag across the label inside the answer box, then press this again.',
    'prostudio.cfg.blokPesan.dikesan': 'The agent already found this block on its own. Open any field above and you will see it in the block list there — no need to add it here.',
    'prostudio.cfg.blokPesan.panjang': 'That is too long — it looks like a whole line including the answer. Highlight just the label.',
    'prostudio.cfg.blokPesan.pendek': 'Too short — a block needs at least 2 characters.',
    'prostudio.cfg.blokPesan.sudah': 'That block already exists.',
    'prostudio.cfg.blokPilihHint': 'Highlight the label inside the answer above, then press this.',
    'prostudio.cfg.blokPerluJawapan': 'The answer is not on screen yet, so there is nothing to highlight. Run Configure once, then come back here.',
    'prostudio.cfg.blokPilihLabel': 'Highlight the label in the answer',
    'prostudio.cfg.blokTakSemak': 'Cannot be checked right now — run Configure to see whether it matches',
    'prostudio.cfg.blokTiada': 'Found nothing — try a shorter label',
    'prostudio.cfg.blokTitle': 'Your own blocks',
    'prostudio.cfg.composerPh': 'Click a block below to build this field.',
    'prostudio.cfg.done': 'Done',
    'prostudio.cfg.errGemUrl': 'That is not a Gem link. It should start with gemini.google.com/gem/',
    'prostudio.cfg.extBlocked': 'Pick one before running — the chip changes how your GPT answers, so it must match every time.',
    'prostudio.cfg.extHint': 'ChatGPT has no model list to choose from — this chip is the only run setting there is.',
    'prostudio.cfg.extKeep': 'Leave it as ChatGPT set it',
    'prostudio.cfg.extKeepDesc': 'SESAAT does not touch the Thinking chip. Pick this if your GPT was built to run with Thinking on.',
    'prostudio.cfg.extLabel': 'Thinking (ChatGPT)',
    'prostudio.cfg.extOff': 'Turn Thinking off',
    'prostudio.cfg.extOffDesc': 'SESAAT removes the Thinking chip before it types your product — the GPT answers straight away, faster. SESAAT does not switch it back on after the run.',
    'prostudio.cfg.thinkingGagal': 'Thinking could not be turned off on this run — the answer above was generated with Thinking on. Run Configure again.',
    'prostudio.cfg.freshTitle': 'New answer taken',
    'prostudio.cfg.freshMeta': '{tarikh} · tested with {produk}',
    'prostudio.cfg.freshSave': 'Not saved yet — press Save',
    'prostudio.cfg.bukaPerbualan': 'Open agent conversation',
    'prostudio.cfg.hariIni': 'Today, {jam}',
    'ext.ralatSambungan': 'SESAAT could not connect to the extension. Press "Reload" in Manage extensions, then try again.',
    'prostudio.cfg.tahapLabel': 'Thinking (ChatGPT)',
    'prostudio.cfg.tahapRead': 'Read Thinking options',
    'prostudio.cfg.tahapReading': 'Reading options…',
    'prostudio.cfg.tahapReadHint': 'SESAAT briefly opens your GPT to read the Thinking options available on your ChatGPT account. No message is sent.',
    'prostudio.cfg.tahapReadAgain': 'Read again',
    'prostudio.cfg.tahapAkaun': 'ChatGPT account: {akaun}',
    'prostudio.cfg.tahapHint': 'This level is applied every time the agent runs, including in the My Agent tab.',
    'prostudio.cfg.tahapBlocked': 'Pick a level before running — the Thinking level changes how your GPT answers.',
    'prostudio.cfg.tahapLama': 'The old "Turn off Thinking" setting now means the lowest level. Pick a level above to replace it.',
    'prostudio.cfg.tahapGagal': 'Thinking could not be set to "{mahu}" on this run ({nota}). Check the Thinking options, then run Configure again.',
    'prostudio.cfg.tahapNeedUrl': 'Paste the GPT link in Step 1 first.',
    'prostudio.cfg.tahapFail': 'Thinking options could not be read. Please try again.',
    'prostudio.cfg.tahapNone': 'No Thinking options were found on that ChatGPT. Make sure you are logged in to ChatGPT.',
    'prostudio.cfg.tahapTiada': 'This ChatGPT account has no Thinking options — SESAAT follows the ChatGPT setting.',
    'prostudio.cfg.geminiExtGagalOn': 'Extended thinking could not be turned on for this run — the answer above was generated without it. Run Configure again.',
    'prostudio.cfg.geminiExtGagalOff': 'Extended thinking could not be turned off for this run — the answer above was generated with it on. Run Configure again.',
    'prostudio.cfg.geminiModelGagal': 'The {mahu} model could not be selected for this run — the answer above was generated with {guna}. Check your Gem\'s model list, then run Configure again.',
    'prostudio.cfg.geminiModelLabel': 'Gemini model',
    'prostudio.cfg.geminiExtLabel': 'Extended thinking (Gemini)',
    'prostudio.cfg.geminiExtOn': 'Turn on',
    'prostudio.cfg.geminiExtOnDesc': 'SESAAT turns Extended thinking on before it types your product — the Gem thinks more deeply. Pick this if your Gem was calibrated with it on.',
    'prostudio.cfg.geminiExtOff': 'Turn off',
    'prostudio.cfg.geminiExtOffDesc': 'SESAAT turns Extended thinking off before typing — a normal, faster answer. Pick this if your Gem was calibrated without it.',
    'prostudio.cfg.geminiHint': 'The model and Thinking level above are set on every run, so the answer always matches what was calibrated.',
    'prostudio.cfg.geminiBlocked': 'Pick a model AND a Thinking level first — both change how the Gem answers, so they must match every time.',
    'prostudio.cfg.geminiModelBlocked': 'Pick a Gemini model first.',
    'prostudio.cfg.geminiExtBlocked': 'Pick a Thinking level (Low, Medium or High) first.',
    'prostudio.cfg.geminiTahapLabel': 'Thinking level (Gemini)',
    'prostudio.cfg.geminiTahapLowDesc': 'The fastest answer. Pick this level if your Gem was calibrated on Low.',
    'prostudio.cfg.geminiTahapMediumDesc': 'A balance of speed and depth. Pick this level if your Gem was calibrated on Medium.',
    'prostudio.cfg.geminiTahapHighDesc': 'The Gem thinks most deeply and answers more slowly. Pick this level if your Gem was calibrated on High.',
    'prostudio.cfg.geminiTahapGagal': 'The {mahu} Thinking level could not be set for this run — the answer above was generated on {guna}. Run Configure again.',
    'prostudio.cfg.geminiTahapGagalUmum': 'The {mahu} Thinking level could not be set for this run. Run Configure again.',
    'prostudio.cfg.fieldsHint': 'These five fields are fixed. They are the same five boxes you see on the product card in the Workspace. Flow automation reads the image and video ones directly.',
    'prostudio.cfg.fieldsName': 'Fields',
    'prostudio.cfg.fieldsQ': 'What should each result contain?',
    'prostudio.cfg.edit': 'Change this row',
    'prostudio.cfg.editHint': 'Blocks are the pieces of your agent’s answer. Whatever you type around them stays exactly as you typed it.',
    'prostudio.cfg.insert': 'Insert a block:',
    'prostudio.cfg.introBody': 'One real product is sent to your agent through the SESAAT Extension. A ChatGPT tab will open — leave it running until it finishes, about one to two minutes.',
    'prostudio.cfg.introBodyGemini': 'One real product is sent to your agent through the SESAAT Extension. A Gemini tab will open — leave it running until it finishes, about one to two minutes.',
    'prostudio.cfg.modelDibaca': 'The list above was read from your Gemini account. Read it again if Google renames its models.',
    'prostudio.cfg.modelReadAgain': 'Read my models again',
    'prostudio.cfg.modelEmpty': 'Press the button below — SESAAT opens your Gem, copies the model list from your account, and closes it again.',
    'prostudio.cfg.modelFail': 'The model list could not be read. Please try again.',
    'prostudio.cfg.modelNeedUrl': 'Paste your Gem link in step 1 first.',
    'prostudio.cfg.modelNone': 'No model list was found on that Gem. Make sure you are signed in to Gemini.',
    'prostudio.cfg.modelRead': 'Read my models',
    'prostudio.cfg.modelReadHint': 'Opens your Gem, copies the list, closes it. Nothing is sent and no quota is used.',
    'prostudio.cfg.modelReading': 'Reading…',
    'prostudio.cfg.modelWarn': 'Nothing picked means SESAAT does not touch the model menu at all — your Gem runs on whatever model Gemini happens to be set to at that moment, and its answer can differ from the model you built the Gem on.',
    'prostudio.cfg.modelBoxHint': 'The model your Gem runs on',
    'prostudio.cfg.preview': 'Preview the product card',
    'prostudio.cfg.previewEmpty': '— empty —',
    'prostudio.cfg.previewSet': 'Set {n}',
    'prostudio.cfg.previewHint': 'How it will look in the Workspace',
    'prostudio.cfg.imgTitle': 'Image from the agent\'s answer',
    'prostudio.cfg.imgHint': 'Preview only — the image is saved to the card during a real run in the Workspace.',
    'prostudio.cfg.imgNone': 'No image was received on this run. Make sure the SESAAT extension is version 3.9.4 or newer, and that the agent actually produces an image on the marked answer.',
    'prostudio.cfg.prodImgOk': 'Product images sent to the agent: {n}.',
    'prostudio.cfg.prodImgShort': 'Only {dihantar} of {dijangka} product images reached the agent — the answer may not reflect every image.',
    'prostudio.cfg.prodImgOld': 'The extra product images were not sent — install the latest SESAAT extension (3.9.4 or newer), then run Configure again.',
    'prostudio.cfg.readyMeta': 'Configured {tarikh} · tested with {produk}',
    'prostudio.cfg.readyMetaBaharu': 'Tested with {produk}',
    'prostudio.cfg.readyTitle': 'This agent is ready',
    'prostudio.cfg.rematchBody': 'The results below will update as soon as this finishes. Please wait and do not change anything yet.',
    'prostudio.cfg.rematchTitle': 'Matching the selected answer again…',
    'prostudio.cfg.resultAll': 'Sets: {set} · Scenes: {babak} · all {jum} fields matched. Tap any row to change it.',
    'prostudio.cfg.resultSome': '{n} of {jum} fields matched — the rest are marked below. Open each marked row and pick its source.',
    'prostudio.cfg.resultTitle': 'What SESAAT takes from the answer',
    'prostudio.cfg.resultBlokAmaran': '{n} row(s) point at a block that does not appear the right number of times. Read the note under each one — sets that run out take text from another set.',
    'prostudio.cfg.babakKe': 'Scene {n}',
    'prostudio.cfg.notaKosong': 'Block "{blok}" was not found anywhere in this answer.',
    'prostudio.cfg.notaTakCukup': 'Block "{blok}" appears {n}× in this answer, but {perlu}× is needed to fill every set. Sets that run out take text from another set, or come out empty.',
    'prostudio.cfg.rowEmpty': 'No source yet',
    'prostudio.cfg.run': 'Configure agent',
    'prostudio.cfg.runningBody': 'Leave the ChatGPT tab alone until it finishes. This page fills in by itself.',
    'prostudio.cfg.runningTitle': 'Testing your agent',
    'prostudio.cfg.stepOf': 'Step {n} of {jum}',
    'prostudio.hook.addHook': 'Add hook',
    'prostudio.hook.bulkAdd': 'Add all',
    'prostudio.hook.bulkClose': 'Close bulk paste',
    'prostudio.hook.bulkOpen': 'Bulk paste',
    'prostudio.hook.bulkPlaceholder': 'One hook per line:\nToday only, this price is gone tomorrow\nI tried it myself, it is worth it',
    'prostudio.hook.countInGroup': '{n} hooks in this group',
    'prostudio.hook.deleteGroup': 'Delete group',
    'prostudio.hook.emptyBody': 'Select a group on the left, or create a new one.',
    'prostudio.hook.emptyTitle': 'No group selected',
    'prostudio.hook.examplesDesc': 'One sentence per line. The system draws one at random on every Generate.',
    'prostudio.hook.examplesTitle': 'Hook examples',
    'prostudio.hook.groupMeta': '{n} hooks · {mod}',
    'prostudio.hook.groupName': 'Group name',
    'prostudio.hook.groupsDesc': 'The star marks your default group in the Workspace.',
    'prostudio.hook.groupsTitle': 'Your groups',
    'prostudio.hook.hookPlaceholder': 'Write your opening line',
    'prostudio.hook.howToUse': 'How it is used',
    'prostudio.hook.isDefaultGroup': 'Your default group',
    'prostudio.hook.makeDefaultGroup': 'Set as default group',
    'prostudio.hook.modeAdapt': 'AI adapts',
    'prostudio.hook.modeVerbatim': 'Used as-is',
    'prostudio.hook.newGroup': 'New group',
    'prostudio.hook.noHooks': 'No hook examples in this group yet.',
    'prostudio.hook.pageDesc': 'Your saved opening lines. On every Generate, the system draws one example from the selected group.',
    'prostudio.hook.removeHook': 'Remove hook',
    'prostudio.hook.settingsDesc': 'A name for your own reference, and how the system uses the examples inside it.',
    'prostudio.hook.settingsTitle': 'Group settings',
    'prostudio.hook.sharedBadge': 'Shared across every module',
    'prostudio.hook.styleNote': 'Style note for the AI (optional)',
    'prostudio.hook.styleNotePlaceholder': 'Example: urgent but friendly tone, short sentences',
    'prostudio.hook.useAdapt': 'Adapt to the product',
    'prostudio.hook.useAdaptFor': 'For those who want to set the direction and tone, but let the AI do the writing.',
    'prostudio.hook.useAdaptSummary': 'The AI copies the style of your examples and writes new lines that fit the current product.',
    'prostudio.hook.useVerbatim': 'Use as-is, unchanged',
    'prostudio.hook.useVerbatimFor': 'For those who already have proven hooks and do not want anyone changing them.',
    'prostudio.hook.useVerbatimSummary': 'The example is used word for word as the hook. The AI does not touch it at all.',
    /* Labelled, not "{n} agents" — the count can be 1, and the dictionary
       has no way to express an English plural rule. */
    'prostudio.hub.badge.agents': 'Agents: {n}',
    'prostudio.hub.badge.cta': '{n} CTA',
    'prostudio.hub.badge.groups': '{n} groups',
    'prostudio.hub.badge.templates': '{n} templates',
    'prostudio.hub.coreRule': 'The core SESAAT rules (headscarf for women\'s products, the product must follow the reference photo, poses must suit the product size) always take priority — nothing set here may override them.',
    'prostudio.hub.group.all': 'For every module',
    'prostudio.hub.group.allDesc': 'These settings are applied on top of whatever you run.',
    'prostudio.hub.group.proto': 'Prototype — not live yet',
    'prostudio.hub.group.protoDesc': 'Still being designed. Customers cannot see or open this.',
    'prostudio.hub.group.smart': 'My Agent — for the SMART module only',
    'prostudio.hub.group.smartDesc': 'SMART does not use the SESAAT master prompt — it uses your own custom GPT.',
    'prostudio.agentLock.title': 'The SMART module is not available on your account',
    'prostudio.agentLock.body': 'My Agent is where you connect your own custom GPT to SESAAT. It only works together with the SMART module, so it is not available on your current plan.',
    'prostudio.agentLock.cta': 'See pricing',
    /* ── Agent Saya — seksyen SEBENAR (ProStudioAgentSection) ─────────
       Berasingan daripada `prostudio.wizard.*` (lakaran). Kunci yang
       maknanya SAMA sengaja diguna semula daripada blok wizard itu —
       tambah di sini hanya yang seksyen sebenar ada lebih. */
    'prostudio.agentsec.introTitle': 'Connect your own custom GPT',
    'prostudio.agentsec.introBody': 'If you bought a custom GPT from another seller, connect it here. Set the output you want, and SESAAT will send your products to that GPT in bulk, collect its answers, and run Flow automation to your settings.',
    'prostudio.agentsec.introStrong': 'Configure once for each agent.',
    'prostudio.agentsec.listLoading': 'Loading your agents…',
    'prostudio.agentsec.listEmpty': 'No agents yet.',
    'prostudio.agentsec.listEmptyCta': 'Click {b} to begin.',
    'prostudio.agentsec.rowMeta': '{set} sets · {babak} scenes · {hasil}',
    'prostudio.agentsec.hasilImage': 'image',
    'prostudio.agentsec.hasilImageVideo': 'image + video',
    'prostudio.agentsec.unsavedDot': 'There are unsaved changes',
    'prostudio.agentsec.newAgent': 'New agent',
    'prostudio.agentsec.railNote': 'An agent marked {b} cannot be run in the Workspace — finish step 5 first.',
    'prostudio.agentsec.nonePickedTitle': 'No agent selected',
    'prostudio.agentsec.nonePickedBody': 'Pick an agent on the left, or click "New agent".',
    'prostudio.agentsec.unnamed': 'Untitled agent',
    'prostudio.agentsec.unsaved': 'Not saved',
    'prostudio.agentsec.saved': 'Saved',
    'prostudio.agentsec.saving': 'Saving…',
    'prostudio.agentsec.save': 'Save',
    'prostudio.agentsec.saveTitleOn': 'Save every change on this agent',
    'prostudio.agentsec.saveTitleOff': 'Nothing to save',
    'prostudio.agentsec.s1sub': 'Basic details of the custom GPT you own.',
    'prostudio.agentsec.agentNamePh': 'Example: Storyboard GPT (Cikgu Ali)',
    'prostudio.agentsec.soon': 'SOON',
    'prostudio.agentsec.s2sub': 'What SESAAT sends to your agent every time it runs.',
    'prostudio.agentsec.extraLineHint': 'This sentence is appended to the end of the first message, after the product name and link. It is used for {b} product you run with this agent — no need to retype it on every product card.',
    'prostudio.agentsec.extraLineHintEvery': 'every',
    'prostudio.agentsec.repliesLabel': 'Consecutive replies (optional)',
    'prostudio.agentsec.repliesNote': 'Some agents {b1} before producing output. Type your answers here in order. SESAAT sends them one by one and waits for the agent to finish each time, then takes the {b2} as the output. Leave this empty if your agent gives its output on the first message.',
    'prostudio.agentsec.repliesNoteAsk': 'ask questions first',
    'prostudio.agentsec.repliesNoteLast': 'last answer',
    'prostudio.agentsec.replyPh1': 'Your answer to the agent’s first question',
    'prostudio.agentsec.replyPhN': 'Your answer to question {n}',
    'prostudio.agentsec.removeReply': 'Remove this reply',
    'prostudio.agentsec.addReply': 'Add reply',
    'prostudio.agentsec.repliesTimeHint': 'Each reply adds roughly one minute to the run time for every product.',
    'prostudio.agentsec.imgFirstToggle': 'Save the image from the first answer',
    'prostudio.agentsec.imgReplyToggle': 'Save the image from this answer',
    'prostudio.agentsec.imgCount': '{n}/{max} images marked',
    'prostudio.agentsec.imgNote': 'Turn on "Save image" on the answer where your agent produces an image (for example a product sheet or a storyboard). SESAAT waits for the image to finish, downloads it, and saves it to the card as an extra image — up to 5 images per card including the main one. A second run adds to the card, it does not replace.',
    'prostudio.agentsec.imgFull': 'Limit reached — turn one off before marking another.',
    'prostudio.agentsec.previewImgMark': 'SESAAT saves the image from this answer into the card',
    'prostudio.agentsec.sumImgOne': 'SESAAT also saves 1 image from the agent\'s answer into the card.',
    'prostudio.agentsec.sumImgMany': 'SESAAT also saves {n} images from the agent\'s answer into the card.',
    // Data produk yang ditampal kepada agent (Langkah 2) — 2026-08-21
    'prostudio.agentsec.dataLabel': 'Product data SESAAT sends',
    'prostudio.agentsec.produkPilih': 'Pick a product',
    'prostudio.agentsec.produkCari': 'Search product name…',
    'prostudio.agentsec.produkTiadaPadan': 'No matching product.',
    'prostudio.agentsec.produkReadyKosong': 'No READY product in My Products. Complete the data for at least one product on the Products page before running Configure.',
    'prostudio.agentsec.dataHint': 'Every piece of product data on the card — price, category, specifications, customer reviews and the rest — is pasted along with the name and link. To change that data, use the Edit Product Data form on the Products page.',
    'prostudio.agentsec.previewDataEmpty': '(this test product has no data filled in yet)',
    'prostudio.agentsec.previewTitle': 'Example conversation',
    'prostudio.agentsec.previewProduct': 'Your product name',
    'prostudio.agentsec.previewAgentFallback': 'Your agent',
    'prostudio.agentsec.previewReplyWord': 'reply',
    'prostudio.agentsec.previewNoExtra': '(no extra instruction line)',
    'prostudio.agentsec.previewAsk': 'Your agent asks a question…',
    'prostudio.agentsec.previewLastReply': 'Final reply from your agent',
    'prostudio.agentsec.previewOutputBadge': 'SESAAT takes this last reply as the output',
    'prostudio.agentsec.previewFoot': 'Example only — the real product name and link are filled in at run time.',
    'prostudio.agentsec.s3sub': 'Set what SESAAT should produce for each product.',
    'prostudio.agentsec.setsHint': 'One set = one complete result. Three sets = three different versions of the same product.',
    'prostudio.agentsec.kunciTip': 'Not available yet — needs the new extension version',
    'prostudio.agentsec.babakKunciNota': 'For now only 1 to {siap} scenes can be run. {seterusnya} and above open up once the new extension version is released.',
    'prostudio.agentsec.hasilKunciNota': 'For now every run generates images and video. Image only opens up with the new extension version.',
    'prostudio.agentsec.produkKunciNota': 'For now the product photo is always used on every scene. Choosing a specific scene opens up with the new extension version.',
    'prostudio.agentsec.avatarKunciNota': 'Whether to use the avatar is chosen in the Workspace during Generate (No avatar / Male / Female / your avatar). Placing it on a specific scene opens up with the new extension version.',
    'prostudio.agentsec.scenesHint': 'A scene is one slice of the story inside a set. Maximum {n} scenes.',
    'prostudio.agentsec.productPhotoHint': 'Your real product photo is uploaded to Flow as a reference — so the product looks exactly right, not invented by AI.',
    'prostudio.agentsec.avatarLabel': 'Avatar used on',
    'prostudio.agentsec.avatarHint': 'The avatar is your fixed model face. Use it so every video looks like it comes from the same person.',
    'prostudio.agentsec.sceneEvery': 'Every scene',
    'prostudio.agentsec.sceneOnly': 'Scene {n} only',
    'prostudio.agentsec.summary': 'SUMMARY',
    'prostudio.agentsec.s4sub': 'Run it once with a single product so SESAAT learns to read your agent’s output.',
    'prostudio.agentsec.perSceneNote': 'Repeated {n}× for every set.',
    'prostudio.agentsec.perSetTitle': 'Once per set',
    'prostudio.agentsec.perSetNote': 'Not repeated per scene.',
    'prostudio.agentsec.videoHidden': 'The {b1} field is hidden because step 3 is set to {b2}.',
    'prostudio.agentsec.fieldTypes': 'Two kinds of field:',
    'prostudio.agentsec.fieldEngine': 'ENGINE',
    'prostudio.agentsec.fieldEngineDesc': '— used by Flow automation to generate images and video. These fields are required, but you may name them however you like.',
    'prostudio.agentsec.fieldText': 'TEXT',
    'prostudio.agentsec.fieldTextDesc': '— carried into the product card for display and copying (hook, video script, caption). Add as many as you need.',
    'prostudio.agentsec.lockedField': 'Required field — Flow automation uses it to generate images and video.',
    'prostudio.agentsec.s5sub': 'Run it once with a single product so SESAAT learns to read your agent’s output.',
    'prostudio.agentsec.geminiModel': 'Gemini model',
    'prostudio.agentsec.geminiModelDefault': 'Use whatever Gemini is set to',
    'prostudio.agentsec.geminiModelEmpty': 'Run Configure above — your Gemini account models will then be listed here to choose from.',
    'prostudio.agentsec.geminiModelFail': 'Your account models could not be read from Gemini. Press Configure Again above to retry.',
    'prostudio.agentsec.geminiModelHint': 'Changing the model clears Configured — run Configure again afterwards.',
    'prostudio.agentsec.runNote': 'One real product is sent to your agent through the SESAAT Extension. A ChatGPT tab opens — {b} (roughly one to two minutes).',
    'prostudio.agentsec.runNoteStrong': 'let it run until it finishes',
    'prostudio.agentsec.configuredOn': 'This agent was configured on {tarikh}. You can run it in the Workspace.',
    'prostudio.agentsec.productsLoading': 'Loading products…',
    'prostudio.agentsec.productsEmpty': 'No products in the Workspace',
    'prostudio.agentsec.productsEmptyHint': 'Add at least one product in the Workspace before running Configure.',
    'prostudio.agentsec.runBlockedUrl': 'Fill in the custom GPT link first',
    'prostudio.agentsec.runBlockedProduct': 'Pick a product to test with first',
    'prostudio.agentsec.runReady': 'Run once so SESAAT learns the output format',
    'prostudio.agentsec.running': 'Testing…',
    'prostudio.agentsec.configureAgain': 'Configure again',
    'prostudio.agentsec.configureAgent': 'Configure agent',
    'prostudio.agentsec.notConfiguredTitle': 'This agent is not configured yet',
    'prostudio.agentsec.notConfiguredBody': 'Click {b} above. SESAAT will send one product to your agent, then match its answer to the form in step 4 automatically. You only need to check and confirm.',
    'prostudio.agentsec.outputFrom': 'Output taken from',
    'prostudio.agentsec.turnFirst': 'First answer',
    'prostudio.agentsec.turnAfter': 'After reply {n}',
    'prostudio.agentsec.turnEmpty': ' — empty',
    'prostudio.agentsec.turnChars': ' — {n} characters',
    'prostudio.agentsec.rematching': 'Matching again…',
    'prostudio.agentsec.outputFromHint': 'Some agents give their final output only after a few replies. Pick the round that holds the real output — SESAAT matches again straight away, with no need to run the agent a second time.',
    'prostudio.agentsec.matchTitle': 'Matches detected',
    'prostudio.agentsec.matchBadge': '{set} SETS · {babak} SCENES DETECTED',
    'prostudio.agentsec.matchCheck': 'Check every row. Change any that are wrong.',
    'prostudio.agentsec.missingWarn': '{b} did not match. Flow automation cannot run without these fields. Pick their source manually below, or run Configure again.',
    'prostudio.agentsec.missingAnd': ' and ',
    'prostudio.agentsec.matchScene': 'Every scene — repeated {n}×',
    'prostudio.agentsec.colField': 'YOUR FIELD',
    'prostudio.agentsec.colSource': 'SOURCE IN THE AGENT ANSWER',
    'prostudio.agentsec.colNone': '— None —',
    'prostudio.agentsec.composerPh': 'Compose the output here — click a block below to insert it.',
    'prostudio.agentsec.composerInsert': 'Click to insert:',
    /* ⚠️ Dahulu DUA kunci ini bernama `previewTitle` / `previewProduct` — nama
       yang SAMA seperti kunci panel "Example conversation" di atas. Takrifan
       kedua menang, jadi panel Langkah 2 terpampang "Product card preview".
       Dinamakan semula 2026-08-21; jangan pulangkan nama lama. */
    'prostudio.agentsec.previewKadTitle': 'Product card preview',
    'prostudio.agentsec.previewHint': 'This is how it looks in Workspace. The text comes from the Gem output during Configure.',
    'prostudio.agentsec.previewKadProduk': 'Test product',
    'prostudio.agentsec.previewSet': 'Set {n}',
    'prostudio.agentsec.previewEmpty': '— empty (the agent did not output this part) —',
    'prostudio.agentsec.seeRaw': 'See the full answer from the agent',
    'prostudio.agentsec.seeRawFirst': 'first answer',
    'prostudio.agentsec.seeRawAfter': 'after reply {n}',
    'prostudio.agentsec.saveHintTop': 'Check the matches above, then press {b} in the top bar — every change (steps 1 to 5) is saved together.',
    'prostudio.agentsec.prev': 'Back',
    'prostudio.agentsec.footNoteLabel': 'Note:',
    'prostudio.agentsec.footNote': 'A configured agent appears in the picker on the {b} tab in the Workspace. Pick your products there, then run them in bulk with one click — no need to configure again.',
    'prostudio.agentsec.errCreate': 'Could not create a new agent: ',
    'prostudio.agentsec.errDelete': 'Could not delete the agent: ',
    'prostudio.agentsec.errSave': 'Could not save the agent: ',
    'prostudio.agentsec.confirmDelete': 'Delete agent "{nama}"? This cannot be undone.',
    'prostudio.agentsec.errTurnEmpty': 'The round you picked holds no answer that can be matched. Pick another round.',
    'prostudio.agentsec.errRematch': 'Could not match again. Please try once more.',
    'prostudio.agentsec.errNoProduct': 'Pick a product to test with first.',
    'prostudio.agentsec.errNoExt': 'The SESAAT Extension could not be reached. Make sure it is installed and active, then refresh this page.',
    'prostudio.agentsec.errNoAnswer': 'The agent gave no answer.',
    'prostudio.agentsec.errShort': 'The answer from the agent is empty or too short. Make sure that GPT really replied, then try again.',
    'prostudio.agentsec.errRun': 'Configure failed. Please try again.',
    'prostudio.agentsec.errNoReply': 'No answer from the extension.',
    'prostudio.agentsec.errTimeout': 'Timed out waiting for the agent ({n} minutes).',
    'prostudio.agentsec.sumSetOne': 'Each product produces {n} set.',
    'prostudio.agentsec.sumSetMany': 'Each product produces {n} sets — meaning {n} separate results.',
    'prostudio.agentsec.sumSceneOne': 'Each set contains 1 scene only.',
    'prostudio.agentsec.sumSceneMany': 'Each set contains {n} scenes.',
    'prostudio.agentsec.sumImageOnly': 'SESAAT generates {n} images only — no video.',
    'prostudio.agentsec.sumImageVideo': 'SESAAT generates {n} images, then {n} videos from those images.',
    'prostudio.agentsec.sumPhotoNone': 'The product photo is not included.',
    'prostudio.agentsec.sumPhotoAll': 'The product photo is included on every scene.',
    'prostudio.agentsec.sumPhotoOne': 'The product photo is included on scene {n} only.',
    'prostudio.agentsec.sumAvatarNone': 'The avatar is not used.',
    'prostudio.agentsec.sumAvatarAll': 'The avatar is used on every scene.',
    'prostudio.agentsec.sumAvatarOne': 'The avatar is used on scene {n} only.',
    'prostudio.hub.previewBody': 'This is where you will save your own styles, hooks and instructions later. Click any section to see how it looks; the settings cannot be changed yet.',
    'prostudio.hub.previewTitle': 'Not unlocked yet — you may look around first.',
    'prostudio.hub.stat.agents': 'Connected agents',
    'prostudio.hub.stat.agentsHint': 'Your own custom GPT',
    'prostudio.hub.stat.groups': 'Hook groups',
    'prostudio.hub.stat.groupsHint': 'Shared across every module',
    'prostudio.hub.stat.templates': 'Style templates',
    'prostudio.hub.stat.templatesHint': 'Slots depend on your subscription plan',
    'prostudio.hub.subtitle': 'Where you save your own styles, hooks and instructions. Whatever you save here is used every time you press Generate in the Workspace.',
    'prostudio.planNote': 'Design plan · /prostudio-v3.9',
    'prostudio.ready': 'Ready',
    'prostudio.saved': 'Saved',
    'prostudio.section.agent.desc': 'Connect your own custom GPT so that SESAAT can use it during Generate.',
    'prostudio.section.agent.label': 'My Agent',
    'prostudio.cta.pageDesc': "The text on the TikTok yellow bag and the Shopee product bag on Facebook Reels.",
    'prostudio.cta.addTitle': "Add a CTA",
    'prostudio.cta.addHint': "This text appears on the product link button (yellow bag) in your TikTok video.",
    'prostudio.cta.add': "Add",
    'prostudio.cta.loading': "Loading…",
    'prostudio.cta.save': "Save",
    'prostudio.cta.cancel': "Cancel",
    'prostudio.cta.edit': "Edit",
    'prostudio.cta.defaultBadge': "Default",
    'prostudio.cta.defaultHint': "Used automatically until you pick another CTA. Cannot be deleted.",
    'prostudio.cta.delete': "Delete",
    'prostudio.cta.footNote': "To choose which CTA is used on a run: open Workspace → right panel → Schedule tab. That choice is saved on this browser only, so each browser can use a different CTA.",
    'prostudio.section.shortcut.label': "Shortcut /",
    'prostudio.section.shortcut.desc': "Save text you use often. Type \"/\" in Edit Prompts to insert it.",
    'prostudio.hub.badge.shortcut': "{n} shortcuts",
    'prostudio.sc.pageDesc': "Saved text you can insert into any Edit Prompts box by typing \"/\".",
    'prostudio.sc.addTitle': "Add shortcut",
    'prostudio.sc.addHint': "In Workspace, open Edit Prompts and type \"/\" to pick a shortcut. Its text is inserted at the cursor.",
    'prostudio.sc.teksPlaceholder': "Text to insert when this shortcut is picked…",
    'prostudio.sc.add': "Save shortcut",
    'prostudio.sc.loading': "Loading…",
    'prostudio.sc.empty': "No shortcuts yet. Save your first one above.",
    'prostudio.sc.save': "Save",
    'prostudio.sc.cancel': "Cancel",
    'prostudio.sc.edit': "Edit",
    'prostudio.sc.delete': "Delete",
    'prostudio.sc.confirmDelete': "Delete this shortcut?",
    'prostudio.sc.locked': "Locked",
    'prostudio.sc.foot': "You can save up to {n} shortcuts. Shortcuts are saved to your account, so they are the same in every browser.",
    'prostudio.sc.err.kosong': "Enter a name and text first.",
    'prostudio.sc.err.namaSalah': "Names can only contain lowercase letters, numbers and _ (2–40 characters), with no spaces.",
    'prostudio.sc.err.namaWujud': "This name is already used. Choose another name.",
    'prostudio.sc.err.terlaluPanjang': "Text is too long (maximum {n} characters).",
    'prostudio.sc.err.hadPenuh': "You have reached the limit of {n} shortcuts. Delete unused ones first.",
    'prostudio.sc.err.tiadaLagi': "This shortcut was already deleted in another browser.",
    'prostudio.sc.err.ditolak': "Save was rejected. Refresh the page and try again.",
    'prostudio.sc.err.umum': "Could not save. Try again.",
    'prostudio.sc.err.padam': "Could not delete. Try again.",
    'prostudio.sc.err.muat': "Could not load your shortcuts. Refresh the page.",
    'ws.sc.loading': "Loading shortcuts…",
    'ws.sc.empty': "No shortcuts yet. Create one in PRO Studio → Shortcut /.",
    'ws.sc.noMatch': "No matching shortcut.",
    'ws.sc.hint': "↑↓ select · Enter insert · Esc close",
    'ciri.shortcut.popupTitle': "Try the \"Shortcut /\" feature for free",
    'ciri.shortcut.popupBody': "Save the prompts you use often, then type \"/\" in Edit Prompts to insert them instantly.",
    'ciri.cta.popupTitle': "Try the \"CTA Beg Kuning\" feature for free",
    'ciri.cta.popupBody': "Use your own words on the TikTok yellow bag and the Shopee product bag on Facebook Reels, instead of \"BELI KAT SINI\". Pick the CTA you want to use in the Control Panel.",
    'ciri.badgeFree': "Free until 30 November 2026",
    'ciri.badgeGift': "Free for you",
    'ciri.badgeShort': "Free until 30 Nov",
    'ciri.box.proTitle': "Part of PRO Studio",
    'ciri.box.proBody': "A paid add-on we are currently building.",
    'ciri.box.dateTitle': "Free until 30 November 2026",
    'ciri.box.dateBody': "After this date, this feature requires the PRO Studio package.",
    'ciri.later': "Maybe later",
    'ciri.claim': "Got it, activate for free",
    'ciri.claiming': "Activating…",
    'ciri.activate': "Activate for free",
    'ciri.err.tamat': "The free period has ended.",
    'ciri.err.umum': "Could not activate right now. Try again.",
    'ciri.st.admin': "Admin",
    'ciri.st.aktif': "Active · until {tarikh}",
    'ciri.st.tamat': "Ended",
    'ciri.myplan.desc': "Features that are ready can be tried for free until 30 November 2026.",
    'ciri.myplan.cta': "CTA Beg Kuning",
    'ciri.myplan.agentSmart': "Follows SMART module",
    'ciri.myplan.soonList': "Style · Hook Bank · My Prompts · My Templates",
    'prostudio.ctaLock.belum': "CTA Beg Kuning is free until 30 November 2026. Activate it first to add and use your own CTAs.",
    'prostudio.ctaLock.tamat': "The free period for CTA Beg Kuning has ended. Your CTAs are still saved and can be used again with the PRO Studio package.",
    'prostudio.sc.err.terkunci': "Activate Shortcut / for free first to save.",
    'prostudio.sc.lockedBelum': "Shortcut / is free until 30 November 2026. Activate it first to start saving.",
    'prostudio.sc.lockedTamat': "The free period has ended. Your shortcuts are still saved and can be used again with the PRO Studio package.",
    'prostudio.sc.footAktif': "Free until 30 November 2026. You can save up to {n} shortcuts, and they are the same in every browser.",
    'ws.sc.lockedBelum': "Shortcut / is free until 30 November 2026.",
    'ws.sc.lockedTamat': "The free period for Shortcut / has ended.",
    'ws.sc.activate': "Activate now",
    'workspace.cta.lockedTag': "locked",
    'workspace.cta.lockedBelum': "Your own CTAs are locked. The default \"BELI KAT SINI\" is used.",
    'workspace.cta.lockedTamat': "The free period for your own CTAs has ended. The default \"BELI KAT SINI\" is used.",
    'workspace.cta.activate': "Activate CTA for free until 30 Nov 2026",
    'workspace.cta.label': "Yellow bag CTA",
    'workspace.cta.hint': "Button text on the product link. Saved on this browser only.",
    'workspace.cta.hintFb': "Text shown on the reel product link (Facebook: Link name). Same as the TikTok yellow-bag CTA — one choice for both. Saved on this browser only.",
    'workspace.cta.default': "Default — BELI KAT SINI",
    'workspace.cta.manage': "Manage list in PRO Studio",
    'workspace.cta.missing': "The CTA you picked no longer exists. Using the default.",
    'prostudio.section.blok.desc': 'The whole GOYANG instruction, split into blocks you can edit, reorder or lock word for word.',
    'prostudio.section.blok.label': 'Blocks (prototype)',
    'prostudio.section.cta.desc': "The text on the TikTok yellow bag and the Shopee product bag on Facebook Reels. Caption lines come later.",
    'prostudio.section.cta.label': 'CTA & Hashtag',
    'prostudio.section.gaya.desc': 'Camera angle, background, expression and lighting — saved as a template, and may differ per set.',
    'prostudio.section.gaya.label': 'Style',
    'prostudio.section.hook.desc': 'Save your opening lines. The system draws one at random on every Generate.',
    'prostudio.section.hook.label': 'Hook Bank',
    'prostudio.section.prompt.desc': 'Your standing instructions, added to every image and video prompt.',
    'prostudio.section.prompt.label': 'My Prompts',
    'prostudio.section.template.desc': 'Style, hooks and CTA bundled into one package you can switch with a single click.',
    'prostudio.section.template.label': 'My Templates',
    'prostudio.set.all.sub': 'Sets 1 + 2 + 3 use the same settings',
    'prostudio.set.all.title': 'All sets',
    'prostudio.set.s1.sub': 'Angle: the problem',
    'prostudio.set.s1.title': 'Set 1',
    'prostudio.set.s2.sub': 'Angle: result / benefit',
    'prostudio.set.s2.title': 'Set 2',
    'prostudio.set.s3.sub': 'Angle: curiosity',
    'prostudio.set.s3.title': 'Set 3',
    'prostudio.slotFull': 'Slots full',
    'prostudio.soon': 'Coming soon',
    'prostudio.soonBody': 'It opens once Style and Hook Bank are stable.',
    'prostudio.soonTitle': 'This section is not built yet',
    'prostudio.style.allSets': 'All sets',
    'prostudio.style.allSetsSame': 'All sets the same',
    'prostudio.style.applyBy': 'Apply settings by',
    'prostudio.style.auto': 'Auto (AI decides)',
    'prostudio.style.copyToOther': 'Copy this set to the other sets',
    'prostudio.style.deleteTemplate': 'Delete template',
    'prostudio.style.emptyBody': 'Select a template on the left, or create a new one.',
    'prostudio.style.emptyTitle': 'No template selected',
    'prostudio.style.extraInstruction': 'Additional instruction (optional)',
    'prostudio.style.extraPlaceholder': 'Example: a calm morning mood',
    'prostudio.style.field.angle': 'Camera angle',
    'prostudio.style.field.expression': 'Model expression',
    'prostudio.style.field.light': 'Lighting',
    'prostudio.style.field.location': 'Location / background',
    'prostudio.style.isDefaultTpl': 'Your default template',
    'prostudio.style.makeDefaultTpl': 'Set as default template',
    'prostudio.style.module': 'Module',
    'prostudio.style.moduleSoon': '{nama} — coming soon',
    'prostudio.style.moduleTitle': '{nama} style settings',
    'prostudio.style.newTemplate': 'New template',
    'prostudio.style.noTemplate': 'No templates yet.',
    'prostudio.style.pageDesc': 'How the images and videos should look for each set. Saved as a template — you may keep several and switch at any time.',
    'prostudio.style.perSet': 'Per set',
    'prostudio.style.random': 'Random',
    'prostudio.style.randomCount': 'Every Generate: one is picked at random from {n} options.',
    'prostudio.style.randomEmpty': 'Tick at least one option to randomise.',
    'prostudio.style.randomOff': 'Turn off random mode — go back to a single value',
    'prostudio.style.randomOn': 'Random mode: tick several options and the system picks one on every Generate',
    'prostudio.style.templateName': 'Template name',
    'prostudio.style.templatesDesc': 'The star marks your default template in the Workspace.',
    'prostudio.style.templatesTitle': 'Your templates',
    'prostudio.wizard.add': 'Add agent',
    'prostudio.wizard.agentName': 'Agent name',
    'prostudio.wizard.agentNameHint': 'This is the name you will pick in the Workspace later.',
    'prostudio.wizard.agentNamePh': 'Example: My Skincare Agent',
    'prostudio.wizard.avatar': 'Avatar',
    'prostudio.wizard.avatarNone': 'Not used',
    'prostudio.wizard.avatarUse': 'Use my avatar',
    'prostudio.wizard.back': 'Back',
    'prostudio.wizard.cannotRun': 'An agent that is not configured cannot be run in the Workspace — open it and finish step 5 first.',
    'prostudio.wizard.deleteAgent': 'Delete agent',
    'prostudio.wizard.doneBody': 'It now appears in the agent picker in the Workspace. Pick your products there and run them in bulk — no need to configure again.',
    'prostudio.wizard.doneTitle': '{nama} is ready to use.',
    'prostudio.wizard.dragHandle': 'Drag to rearrange',
    'prostudio.wizard.showInWs': 'Show in Workspace',
    'prostudio.wizard.hiddenFromWs': 'Hidden from Workspace',
    'prostudio.wizard.moreActions': 'More actions',
    'prostudio.wizard.showInWsFail': 'This setting could not be saved: ',
    'prostudio.wizard.configureFirst': 'This agent is not configured yet. Open it and finish step 5 — it will appear in the Workspace automatically once it is ready.',
    'prostudio.wizard.edit': 'Edit',
    'prostudio.wizard.emptyBody': 'Connect your own custom GPT so that SESAAT can use it during Generate.',
    'prostudio.wizard.emptyTitle': 'No agents yet',
    'prostudio.wizard.errName': 'Give this agent a name first.',
    'prostudio.wizard.errNotRun': 'Run Configure once before saving.',
    'prostudio.wizard.errProduct': 'Pick one product to test with.',
    'prostudio.wizard.errUrlBad': 'That is not a custom GPT link. It should start with chatgpt.com/g/',
    'prostudio.wizard.errUrlEmpty': 'Paste your custom GPT link.',
    'prostudio.wizard.extraLine': 'Extra instruction (optional)',
    'prostudio.wizard.extraLineHint': 'Appended to the first message, after the product name and link. It is used for every product you run with this agent.',
    'prostudio.wizard.extraLinePh': 'Example: use a casual tone. Focus on oily skin problems.',
    'prostudio.wizard.fieldImage': 'Image Prompt',
    'prostudio.wizard.fieldVideo': 'Video Prompt',
    'prostudio.wizard.fieldCaption': 'TikTok Caption',
    'prostudio.wizard.fieldHook': 'Hook Text',
    'prostudio.wizard.fieldScript': 'Video Script',
    'prostudio.wizard.finalOutput': 'Final output',
    'prostudio.wizard.finish': 'Finish',
    'prostudio.wizard.gptUrl': 'Custom GPT link',
    'prostudio.wizard.gptUrlHint': 'Open that GPT in your browser, then copy the address from the address bar.',
    'prostudio.wizard.gemUrl': 'Gem link',
    'prostudio.wizard.gemUrlHint': 'Open that Gem in your browser, then copy the address from the address bar.',
    'prostudio.wizard.listDesc': 'Each agent is set up once, then can be picked in the Workspace.',
    'prostudio.wizard.listTitle': 'Your agents',
    'prostudio.wizard.matchOk': 'The answer from your agent was understood. Check the matches below — change any that are wrong.',
    'prostudio.wizard.moveDown': 'Move down',
    'prostudio.wizard.moveUp': 'Move up',
    'prostudio.wizard.next': 'Next',
    'prostudio.wizard.notConfigured': 'Not configured',
    'prostudio.wizard.orderFail': 'The new order could not be saved: ',
    'prostudio.wizard.outImageOnly': 'Image only',
    'prostudio.wizard.outImageOnlySub': 'Stops there. Good for posters.',
    'prostudio.wizard.outImageVideo': 'Image and video',
    'prostudio.wizard.outImageVideoSub': 'Generate the image, then animate it.',
    'prostudio.wizard.photoEvery': 'Every scene',
    'prostudio.wizard.photoLast': 'Last scene only',
    'prostudio.wizard.photoNone': 'Not included',
    'prostudio.wizard.pickProduct': '— Pick one product —',
    'prostudio.wizard.platform': 'Platform',
    'prostudio.wizard.platformHint': 'Pick where this agent lives. Changing it clears the calibration — run Configure again.',
    'prostudio.wizard.productPhoto': 'Product photo included on',
    'prostudio.wizard.ready': 'Ready',
    'prostudio.wizard.replies': 'How many times the agent replies before finishing',
    'prostudio.wizard.repliesHint': 'Some agents ask questions before producing output. If yours produces output on the first message, leave it at 1.',
    /* Labelled, not "{n} sets" — the count can be 1, and English would
       need a plural rule the dictionary has no way to express. */
    'prostudio.wizard.rowMeta': 'Sets: {set} · Scenes: {babak} · {hasil}',
    'prostudio.wizard.rowUnfinished': 'Not finished — step 5 has not been run',
    'prostudio.wizard.run1': 'Checking the extension connection',
    'prostudio.wizard.run2': 'Sending the product and waiting for the agent',
    'prostudio.wizard.run3': 'Matching the answer to your form',
    'prostudio.wizard.runAgain': 'Run again',
    'prostudio.wizard.runConfigure': 'Run Configure',
    'prostudio.wizard.s1.name': 'Your agent',
    'prostudio.wizard.s1.q': 'Which agent?',
    'prostudio.wizard.s2.name': 'Conversation',
    'prostudio.wizard.s2.q': 'What does SESAAT tell your agent?',
    'prostudio.wizard.s3.name': 'Output',
    'prostudio.wizard.s3.q': 'What output do you want?',
    'prostudio.wizard.s4.name': 'Configure',
    'prostudio.wizard.s4.q': 'Let us test it once',
    'prostudio.wizard.s5.name': 'Configure',
    'prostudio.wizard.s5.q': 'Let us test it once',
    'prostudio.wizard.scenesPerSet': 'Scenes per set',
    'prostudio.wizard.setsPerProduct': 'Sets per product',
    'prostudio.wizard.sketchBody': 'Nothing is saved. Reload the page and everything is empty again.',
    'prostudio.wizard.sketchTitle': 'Sketch only',
    'prostudio.wizard.stepOf': 'Step {n} of 4',
    'prostudio.wizard.sumAgent': 'Agent',
    'prostudio.wizard.sumDesc': 'Press Edit on any row to go back to just that step.',
    'prostudio.wizard.sumFields': 'Output fields',
    'prostudio.wizard.sumFieldsValue': '{n} in total',
    'prostudio.wizard.sumOutput': 'Output',
    'prostudio.wizard.sumPlatform': 'Platform',
    'prostudio.wizard.sumReplies': 'Consecutive replies',
    'prostudio.wizard.sumSentence': 'Each product produces {set} sets. Each set contains {babak} scenes. {hasil} Product photo: {gambar}. Avatar: {avatar}.',
    'prostudio.wizard.sumSentenceImage': 'SESAAT generates images only — no video.',
    'prostudio.wizard.sumSentenceVideo': 'SESAAT generates {babak} images, then a video from each image.',
    'prostudio.wizard.sumSets': 'Sets and scenes',
    'prostudio.wizard.sumSetsValue': 'Sets: {set} · Scenes: {babak}',
    'prostudio.wizard.sumTestProduct': 'Test product',
    'prostudio.wizard.sumTitle': 'Agent summary',
    'prostudio.wizard.tagImage': 'Engine · image',
    'prostudio.wizard.tagVideo': 'Engine · video',
    'prostudio.wizard.testHint': 'One real product is sent to your agent just once, so that SESAAT learns the shape of its answer. After this you can run in bulk.',
    'prostudio.wizard.testProduct': 'Product to test with',

    /* ── purchases ───────────────────────────────────────────── */
    'purchases.accessFailed': 'Could not fetch the access details: {ralat}',
    'purchases.accessInfo': 'Access Details',
    'purchases.accountAccess': 'Account Access',
    'purchases.activeUntil': 'Active until {tarikh}.',
    'purchases.copied': 'Access details copied.',
    'purchases.copy': 'Copy',
    'purchases.copyFailed': 'Copy failed — please copy it manually.',
    'purchases.downloadEbook': 'Download Ebook',
    'purchases.downloadFailed': 'Download failed: {ralat}',
    'purchases.empty': 'No purchases yet',
    'purchases.emptyDesc': 'Visit the Marketplace to start shopping.',
    'purchases.expiredNote': 'Your access period has ended. Purchase again to continue — you will receive the same account.',
    'purchases.expiredOn': 'Access expired on {tarikh}.',
    'purchases.free': 'Free',
    'purchases.loadFailed': 'Could not load your purchases: {ralat}',
    'purchases.marketplace': 'Marketplace',
    'purchases.noInfo': 'No details shown. Contact the admin if you need them.',
    'purchases.opening': 'Opening…',
    'purchases.payIncomplete': 'This payment is not complete. You can resume it.',
    'purchases.payNow': 'Pay Now',
    'purchases.payVerifying': 'Your payment is being confirmed — this usually takes only a few seconds.',
    'purchases.preparing': 'Preparing…',
    'purchases.rejectReason': 'Reason: {sebab}',
    'purchases.renew': 'Renew Subscription',
    'purchases.requestedNote': 'Your order has been received. The admin will confirm the payment and hand over access shortly.',
    'purchases.status.awaitingPayment': 'Awaiting payment',
    'purchases.copyAll': 'Copy all',
    'purchases.status.partial': 'Partly delivered',
    'purchases.shortfallNote': '{n} unit(s) not delivered yet because stock ran out. Admin is working on it — you will be notified as soon as it is ready.',
    'purchases.linePending': '{n} more awaiting stock',
    'purchases.status.delivered': 'Completed',
    'purchases.status.refunded': 'Refunded',
    'purchases.status.rejected': 'Rejected',
    'purchases.status.requested': 'Awaiting confirmation',
    'purchases.subtitle': 'Get back to what you bought — any time.',
    'purchases.title': 'My Purchases',
    'purchases.toMarketplace': 'Go to Marketplace',
    'purchases.viewAccess': 'View Access',

    /* ── sales ───────────────────────────────────────────────── */
    'sales.empty.body': 'Change the filter or clear the search.',
    'sales.empty.title': 'No matching orders',
    'sales.exportCsv': 'Export CSV',
    'sales.filter.all': 'All',
    'sales.prototype': 'Prototype · /sales-v3.9',
    'sales.searchPlaceholder': 'Search order or email',
    'sales.stat.orders': 'Total orders',
    'sales.stat.revenue': 'Paid revenue',
    'sales.stat.revenueHint': 'Pending & refund not counted',
    'sales.stat.unsettledHint': 'Delivery not complete',
    'sales.status.paid': 'Paid',
    'sales.status.pending': 'Pending',
    'sales.status.refund': 'Refund',
    'sales.subtitle': 'Every order and what still needs to be delivered to the customer.',
    'sales.th.addon': 'Add-on',
    'sales.th.amount': 'Amount',
    'sales.th.bindMcn': 'Bind MCN',
    'sales.th.customer': 'Customer',
    'sales.th.date': 'Date',
    'sales.th.flowUltra': 'Flow Ultra',
    'sales.th.order': 'Order',
    'sales.th.package': 'Plan',
    'sales.title': 'Sales',
    'sales.unsettled': 'Not settled',

    /* ── sidebar ─────────────────────────────────────────────── */
    'sidebar.affiliates': 'Affiliates',
    'sidebar.almostFull': 'Almost full!',
    'sidebar.cleaning': 'Cleaning',
    'sidebar.cleaningTip': 'Cleaning up…',
    'sidebar.closeMenu': 'Close menu',
    'sidebar.dashboard': 'Dashboard',
    'sidebar.extCardTip': 'Extension — install v{versi}',
    'sidebar.extension': 'Extension',
    'sidebar.extInstall': 'Install · v{versi}',
    'sidebar.notes': 'Notes',
    'sidebar.openMenu': 'Open menu',
    'sidebar.optimize': 'Optimize',
    'sidebar.optimizeStorage': 'Optimize storage',
    'sidebar.optimizeTip': 'Optimize — remove unused files',
    'sidebar.payouts': 'Payouts',
    'sidebar.pricing': 'Pricing',
    'sidebar.products': 'Products',
    'sidebar.proStudio': 'PRO Studio',
    'sidebar.referrals': 'Referrals',
    'sidebar.resources': 'Resources',
    'sidebar.settings': 'Settings',
    'sidebar.storageFull': 'Storage full — please optimize',
    'sidebar.storageTip': 'Storage: {guna} / {had}',
    'sidebar.storageTipOptimize': 'Storage: {guna} / {had} — click to Optimize',
    'sidebar.subscribeNow': 'Subscribe Now',
    'sidebar.workspace': 'Workspace',

    /* ── tutorial ────────────────────────────────────────────── */
    'tutorial.a11y.deleteChapter': 'Delete chapter {nama}',
    'tutorial.a11y.deleteVideo': 'Delete video',
    'tutorial.a11y.markUnwatched': 'Mark as not watched',
    'tutorial.a11y.markWatched': 'Mark as watched',
    'tutorial.a11y.moveDown': 'Move down',
    'tutorial.a11y.moveUp': 'Move up',
    'tutorial.a11y.playLesson': 'Play lesson: {tajuk}',
    'tutorial.a11y.removeStep': 'Remove step',
    'tutorial.a11y.videoUnavailable': 'Video not available yet',
    'tutorial.all.desc': 'Pick a chapter, or search straight for the title you need.',
    'tutorial.all.title': 'All Lessons',
    'tutorial.art.readTime': '{n} min read',
    'tutorial.art.toc': 'In this article',
    'tutorial.badge.current': 'Current',
    'tutorial.badge.new': 'New',
    'tutorial.btn.cancel': 'Cancel',
    'tutorial.btn.continue': 'Continue',
    'tutorial.btn.continueWatching': 'Continue watching',
    'tutorial.btn.editPage': 'Edit page',
    'tutorial.btn.newVideo': 'New video',
    'tutorial.btn.rewatch': 'Rewatch',
    'tutorial.btn.save': 'Save',
    'tutorial.btn.saving': 'Saving…',
    'tutorial.btn.seeLess': 'See less',
    'tutorial.btn.seeMore': 'See more',
    'tutorial.btn.start': 'Start',
    'tutorial.btn.startWatching': 'Start watching',
    'tutorial.btn.watch': 'Watch',
    'tutorial.chapters.countMany': '{jumlah} lessons · {siap} done',
    'tutorial.chapters.countOne': '{jumlah} lesson · {siap} done',
    'tutorial.chapters.desc': 'Enter any chapter and the lessons run one after another to the end — you never have to hunt for the next video.',
    'tutorial.chapters.eyebrow': 'CHAPTER {nombor}',
    'tutorial.chapters.title': 'Chapters',
    'tutorial.confirm.deleteVideo': 'Delete this video?',
    'tutorial.confirm.leaveEdit': 'You have unsaved changes. Leave this page?',
    'tutorial.edit.addChapter': 'Chapter',
    'tutorial.edit.addStep': 'Add step…',
    'tutorial.edit.badge': 'Edit mode',
    'tutorial.edit.chapterName': 'Chapter name',
    'tutorial.edit.ctaLabelPlaceholder': 'Button label (optional)',
    'tutorial.edit.hint': 'Changes are only saved when you press Save.',
    'tutorial.edit.lessonTitle': 'Lesson title',
    'tutorial.edit.newBadgeTip': 'Show the NEW badge on this card',
    'tutorial.edit.newToggle': 'NEW',
    'tutorial.edit.shortDesc': 'Short description',
    'tutorial.edit.startHereOrder': 'Start Here — step order',
    'tutorial.edit.subtitleLabel': 'Page intro line',
    'tutorial.edit.subtitlePlaceholder': 'One line telling customers what this page is for',
    'tutorial.edit.ytInvalid': 'Unrecognised YouTube link — check the link again.',
    'tutorial.edit.ytMissing': 'No YouTube link — this video is hidden from customers.',
    'tutorial.edit.ytPlaceholder': 'Paste YouTube link here',
    'tutorial.empty.chapterBody': 'Pick another chapter, or come back later.',
    'tutorial.empty.chapterTitle': 'No lessons in this chapter yet',
    'tutorial.empty.searchBody': 'Clear the search to see all lessons.',
    'tutorial.empty.searchTitle': 'No lessons match your search',
    'tutorial.err.saveFailed': 'Save failed: {ralat}',
    'tutorial.err.unknown': 'unknown error',
    'tutorial.filter.all': 'All',
    'tutorial.foot.helpDesc': 'Ask directly in the WhatsApp support group.',
    'tutorial.foot.helpTitle': 'Still stuck?',
    'tutorial.foot.playlistDesc': 'Every tutorial video in one playlist.',
    'tutorial.foot.playlistTitle': 'Full playlist on YouTube',
    'tutorial.loading.body': 'Just a moment. Refresh the page if this persists.',
    'tutorial.loading.title': 'Loading tutorial…',
    'tutorial.page.title': 'Tutorial',
    'tutorial.rail.chapterCap': 'Chapter {nombor} · {nama}',
    'tutorial.rail.overview': 'Overview',
    'tutorial.rail.progress': 'Progress',
    'tutorial.search.placeholder': 'Search lessons',
    'tutorial.startHere.desc': 'Five steps from installing the extension to a video going live on TikTok. Follow this order first — the rest can wait.',
    'tutorial.startHere.title': 'Start Here',
    'tutorial.state.comingSoon': 'Coming soon',
    'tutorial.state.notWatched': 'Not watched',
    'tutorial.state.watched': 'Watched',
    'tutorial.thumb.noVideo': 'No video yet',
    'tutorial.untitledCategory': 'Untitled',
    'tutorial.untitledLesson': 'Untitled',

    /* ── workspace ───────────────────────────────────────────── */
    'workspace.bulk.avatar': 'Change Avatar',
    'workspace.bulk.clear': 'Clear selection',
    'workspace.bulk.delete': 'Delete',
    'workspace.bulk.delVideo': 'Del Video',
    'workspace.bulk.duplicate': 'Duplicate',
    'workspace.bulk.markFlow': 'Mark Flow',
    'workspace.bulk.markSchedule': 'Mark Schedule',
    'workspace.bulk.moveGroup': 'Move Group',
    'workspace.bulk.retry': 'Retry',
    'workspace.bulk.saveCloud': 'Save Cloud',
    'workspace.bulk.selectAll': 'Select all',
    'workspace.bulk.selected': '{n} selected',
    'workspace.clear': 'Clear',
    'workspace.clickToEdit': 'Click to edit',
    'workspace.close': 'Close',
    'workspace.closeEsc': 'Close (Esc)',
    'workspace.closeQuickNotes': 'Close Quick Notes',
    'workspace.collapseAll': 'Collapse all cards',
    'workspace.composer.addProduct': 'Add Product',
    'workspace.composer.addProductTip': 'Attach a product from your library',
    'workspace.composer.avatar': 'Avatar',
    'workspace.composer.avatarDefaultFemale': 'Default F',
    'workspace.composer.avatarDefaultMale': 'Default M',
    'workspace.composer.avatarNone': 'No avatar',
    'workspace.composer.bulkImport': 'Bulk Import',
    'workspace.composer.bulkImportTip': 'Bulk import products from Google Sheet / Excel',
    'workspace.composer.createOwn': 'Create your own',
    'workspace.composer.createOwnSub': 'Yours only',
    'workspace.composer.createOwnTip': 'Got an angle that keeps selling? Build it once — set the scenes, lock the style, then run it on every product you add. Yours only. Nobody else sees it.',
    'workspace.composer.editAvatar': 'Edit avatar',
    'workspace.composer.formulaLabel': 'Formula',
    'workspace.composer.formulaLocked': 'Not unlocked yet',
    'workspace.composer.formulaLockedMaintenance': 'This module is temporarily closed',
    'workspace.composer.formulaLockedPlan': 'This module is not unlocked for your account yet',
    'workspace.composer.formulaNote': '— each formula produces a different video style.',
    'workspace.composer.newModule': 'New module',   // admin sahaja — UI admin = English (kedua-dua bahasa)
    'workspace.composer.generate': 'Generate',
    'workspace.composer.generateTip': 'Your remaining quota today. Each product uses one quota; resets at 12:00 midnight.',
    'workspace.composer.generateTipNeedProduct': 'Attach a product first',
    'workspace.composer.genUpgrade': 'Upgrade',
    'workspace.composer.genPickFormula': 'Pick a formula first',
    'workspace.composer.genPickTemplate': 'Pick a template first',
    'workspace.composer.genTipTrialEnded': 'Trial coins are used up — upgrade to a paid licence',
    'workspace.composer.genTipPickFormula': 'Pick one formula first — each formula produces a different video style',
    'workspace.composer.genTipPickTemplate': 'Pick a GOYANG template first before you generate',
    'workspace.composer.avatarDefaultM': 'Default (M)',
    'workspace.composer.avatarDefaultF': 'Default (F)',
    'workspace.composer.avatarGoyangNote': 'GOYANG uses hands only — no avatar',
    'workspace.composer.moduleSettingsTip': 'Module settings (admin) — name, master prompt',
    'workspace.composer.newAvatar': 'New avatar',
    'workspace.composer.noProductAttached': 'No product attached yet.',
    'workspace.composer.nota.ai16saat': '2 scenes · 16s',
    'workspace.composer.nota.ai8saat': '1 scene · 3 sets',
    'workspace.composer.nota.flownity': '5 scenes, continuous',
    'workspace.composer.nota.goyang': 'Motion template',
    'workspace.composer.nota.homeniche': '2 scenes · home niche',
    'workspace.composer.nota.smart': 'Your own prompt',
    'workspace.composer.picker.add': 'Add ({n})',
    'workspace.composer.picker.added': 'Added',
    'workspace.composer.picker.alreadyAttached': '{n} already attached',
    'workspace.composer.picker.cancel': 'Cancel',
    'workspace.composer.picker.cartEmptyLine1': 'Tick products on the left.',
    'workspace.composer.picker.cartEmptyLine2': 'Card count is set here.',
    'workspace.composer.picker.cartTitle': 'Selected ({n})',
    'workspace.composer.picker.clear': 'Clear',
    'workspace.composer.picker.clearTip': 'Clear all',
    'workspace.composer.picker.close': 'Close',
    'workspace.composer.picker.closeTip': 'Close (Esc)',
    'workspace.composer.picker.decrease': 'Decrease',
    'workspace.composer.picker.empty': 'No products match. Clear the search or change the group.',
    'workspace.composer.picker.gridView': 'Grid view',
    'workspace.composer.picker.groupAll': 'All groups',
    'workspace.composer.picker.groupAria': 'Group',
    'workspace.composer.picker.increase': 'Increase',
    'workspace.composer.picker.listView': 'List view',
    'workspace.composer.picker.noneSelected': 'No product selected',
    'workspace.composer.picker.nSelected': '{n} selected',
    'workspace.composer.picker.pending': 'Pending',
    'workspace.composer.picker.pendingLocked': '{n} pending locked',
    'workspace.composer.picker.pendingLockedTip': '{n} products can\'t be selected — their product data is incomplete. Complete them using the Import Products button in the Products page, then try again.',
    'workspace.composer.picker.ready': 'Ready',
    'workspace.composer.picker.reasonIncomplete': 'Product data is incomplete.',
    'workspace.composer.picker.reasonNoData': 'No product data yet — scrape it using the Import Products button.',
    'workspace.composer.picker.remove': 'Remove',
    'workspace.composer.picker.removeAria': 'Remove from selection',
    'workspace.composer.picker.searchPlaceholder': 'Search',
    'workspace.composer.picker.selectAll': 'Select all ({n})',
    'workspace.composer.picker.sort.name': 'Name',
    'workspace.composer.picker.sort.productId': 'Product ID',
    'workspace.composer.picker.sort.status': 'Status',
    'workspace.composer.picker.sort.statusAsc': 'Pending first',
    'workspace.composer.picker.sort.statusDesc': 'Ready first',
    'workspace.composer.picker.sort.time': 'Time',
    'workspace.composer.picker.sort.timeAsc': 'Oldest first',
    'workspace.composer.picker.sort.timeDesc': 'Newest first',
    'workspace.composer.picker.tabMyProducts': 'My Products {n}',
    'workspace.composer.picker.tabSystem': 'System {n}',
    'workspace.composer.picker.title': 'Products',
    'workspace.composer.picker.totalCard': 'TOTAL CARD',
    'workspace.composer.proMode': 'Pro Mode',
    'workspace.composer.proModeOffTip': 'Pro Mode off — AI picks the best hook and style',
    'workspace.composer.proModeOnTip': 'Pro Mode ON — you set your own hook and style',
    'workspace.composer.proModeNoBlocks': 'GOYANG · no blocks yet — open PRO Studio',
    'workspace.composer.genPickBlocks': 'Pick blocks first',
    'workspace.composer.genTipPickBlocks': 'Pro Mode is on but GOYANG has no saved blocks yet — save them in PRO Studio → Blocks, or turn Pro Mode off',
    'workspace.composer.removeProduct': 'Remove {nama}',
    'workspace.composer.samplePreview': 'Sample preview',
    'workspace.composer.showLess': 'Show less',
    'workspace.composer.showMore': '+{n} more',
    'workspace.composer.style.auto': 'Auto',
    'workspace.composer.style.autoNote': 'AI picks the best hook and style for your product.',
    'workspace.composer.style.pro': 'Pro Mode',
    'workspace.composer.style.proNote': 'You set your own hook and style for each set.',
    'workspace.copyField': 'Copy {medan}',
    'workspace.copyLine': 'Copy line',
    'workspace.copySetField': 'Copy {medan} {n}',
    'workspace.empty.groupDesc': 'Add products through the Generate tab, or move existing products here.',
    'workspace.empty.groupTitle': 'This group is still empty',
    'workspace.empty.tabDesc': 'Mark products on another tab, or add new products.',
    'workspace.empty.tabTitle': 'No products here',
    'workspace.expandAll': 'Expand all cards',
    'workspace.group.baharu': 'New group',
    'workspace.group.batal': 'Cancel',
    'workspace.group.cariPlaceholder': 'Search group',
    'workspace.group.namaBaharuPlaceholder': 'New group name',
    'workspace.group.namaPlaceholder': 'Group name',
    'workspace.group.padamAmaran': 'This group contains {n} products. The products are not deleted — they move to the group you pick below.',
    'workspace.group.padamAria': 'Delete group {nama}',
    'workspace.group.padamButang': 'Delete group',
    'workspace.group.padamKosong': 'This group is empty. No products affected.',
    'workspace.group.padamTajuk': 'Delete group',
    'workspace.group.padamTitle': 'Delete group',
    'workspace.group.pindahKe': 'Move products to',
    'workspace.group.ralatKosong': 'Group name cannot be empty.',
    'workspace.group.ralatNamaBertindih': 'That name is already used by another group.',
    'workspace.group.ralatNamaTerpelihara': '"Main Group" is the default group name.',
    'workspace.group.renameAria': 'Rename group {nama}',
    'workspace.group.renameTitle': 'Rename',
    'workspace.group.ringkasanPindah': '{n} products → {group}',
    'workspace.group.simpan': 'Save',
    'workspace.group.tiadaPadanan': 'No group with that name.',
    'workspace.group.tukarTitle': 'Switch group',
    'workspace.group.tutupAria': 'Close',
    'workspace.group.tutupTitle': 'Close (Esc)',
    'workspace.group.utama': 'Main Group',
    'workspace.link.flowTip': 'Open the video collection in Google Flow',
    'workspace.link.tiktokTip': 'Open the product page on TikTok Shop',
    'workspace.modal.aria': 'Details for {nama}',
    'workspace.modal.hint': 'Click a cell to edit · Esc to close',
    'workspace.modal.unsaved': 'You have unsaved changes',
    'workspace.page.adminBadge': 'Admin',
    'workspace.page.chip.extension': 'Extension',
    'workspace.page.chip.fullPrompt': 'Full prompt',
    'workspace.page.controlPanel': 'Control Panel',
    'workspace.page.field.caption': 'Caption',
    'workspace.page.field.dialog': 'Dialog',
    'workspace.page.field.hook': 'Hook',
    'workspace.page.field.image': 'Image',
    'workspace.page.field.script': 'Script',
    'workspace.page.field.video': 'Video',
    'workspace.page.groupUtama': 'Main Group',
    'workspace.page.hide': 'Hide',
    'workspace.page.link.flow': 'Flow collection',
    'workspace.page.link.tiktok': 'TikTok product',
    'workspace.page.post.facebook': 'Facebook',
    'workspace.page.post.postOnly': 'Post Only',
    'workspace.page.post.scheduledPost': 'Scheduled Post',
    'workspace.page.post.showcaseOnly': 'Showcase Only',
    'workspace.page.post.threads': 'Threads',
    'workspace.page.post.tiktok': 'TikTok',
    'workspace.page.quickNotes': 'Quick Notes',
    'workspace.page.run.collection': 'Collection',
    'workspace.page.run.continueCollection': 'Continue Collection',
    'workspace.page.run.imageLabel': 'Image',
    'workspace.page.run.imageModelAria': 'Image model',
    'workspace.page.run.inProject': 'In project',
    'workspace.page.run.multiBrowser': 'Multi browser',
    'workspace.page.run.noSelection': 'No product selected',
    'workspace.page.run.runFlow': 'Run Flow',
    'workspace.run.maintenanceBar': 'Run Flow is under maintenance',
    'workspace.page.run.runTiktok': 'Run TikTok',
    'workspace.page.run.runFacebook': 'Run Facebook',
    'workspace.page.run.runPost': 'Run Post',
    'workspace.page.run.selectedCount': '{n} selected',
    'workspace.page.run.videoLabel': 'Video',
    'workspace.page.run.videoModelAria': 'Video model',
    'workspace.page.run.durationLabel': 'Video duration',
    'workspace.page.run.durationAria': 'Video duration',
    'workspace.page.run.ingredientsLabel': 'Ingredients',
    'workspace.page.schedule.createFirst': 'Create first schedule',
    'workspace.page.schedule.manage': 'Manage schedules',
    'workspace.page.schedule.use': 'Use schedule',
    'workspace.page.scheduleTemplate': 'Schedule template',
    'workspace.page.sect.advanced': 'Advanced',
    'workspace.page.sect.destination': 'Destination',
    'workspace.page.sect.model': 'Model',
    'workspace.page.sect.postMode': 'Post Mode',
    'workspace.page.sect.postTo': 'Post To',
    'workspace.page.sect.schedule': 'Schedule',
    'workspace.page.sect.scene': 'Scene',
    'workspace.page.sect.set': 'Set',
    'workspace.page.setN': 'Set {n}',
    'workspace.page.setPill': 'S{n}',
    'workspace.page.showControlPanel': 'Show Control Panel',
    'workspace.page.sort.module': 'Module',
    'workspace.page.sort.moduleAsc': 'A → Z',
    'workspace.page.sort.moduleDesc': 'Z → A',
    'workspace.page.sort.number': 'Number',
    'workspace.page.sort.numberAsc': '1 → 10',
    'workspace.page.sort.numberDesc': '10 → 1',
    'workspace.page.sort.time': 'Time',
    'workspace.page.sort.timeAsc': 'Oldest first',
    'workspace.page.sort.timeDesc': 'Newest first',
    'workspace.page.tab.agentAi': 'My Agent',
    'workspace.page.tab.flow': 'Flow',
    'workspace.page.tab.generate': 'Generate',
    'workspace.page.tab.schedule': 'Schedule',
    'workspace.page.title': 'Workspace',
    'workspace.panel.hideTip': 'Hide the Control Panel',
    'workspace.post.lockedNote': 'Threads and Facebook are for Shopee products — not open yet.',
    'workspace.post.postOnlyNote': 'Skips adding the product to Showcase. The yellow bag is still added if the product is already in your Showcase.',
    'workspace.post.scheduledNote': 'Every video gets its own date and time. TikTok publishes it later — your browser does not need to be open at that moment.',
    'workspace.post.scheduledNoteOff': 'Every video gets its own date and time. Turn on Direct Schedule below if you want TikTok to publish it on its own.',
    'workspace.post.showcaseNote': 'Add the product to Showcase without uploading a video.',
    'workspace.post.tiktokNote': 'For TikTok Shop products.',
    'workspace.post.fbNote': 'For Shopee products.',
    'workspace.post.pilihSatu': 'Pick one destination only. Threads is not open yet.',
    'workspace.post.fbPageActive': 'The robot posts to the Facebook page that is currently ACTIVE in this browser. One browser = one page — switch page on Facebook first if needed.',
    'workspace.fblaluan.title': 'Posting route',
    'workspace.fblaluan.hint': 'Choose the route that shows the Shopee bag on your phone.',
    'workspace.post.fbRunHint': 'Tick product cards → Run Facebook (any tab), or on the Schedule tab tick calendar slots → Run Facebook. "How to post" below decides: Post now · Schedule (calendar only) · Save as draft.',
    'workspace.page.sect.aiLabel': 'AI Label',
    'workspace.post.aiLabel': 'Add AI label',
    'workspace.post.aiLabelOn': 'The robot marks every video as AI content (TikTok: AI-generated content · Facebook: Add AI label).',
    'workspace.post.aiLabelOff': 'The robot does not mark videos as AI content. Platforms may act on unlabeled AI content.',
    'workspace.page.sect.ttChecks': 'TikTok Checks',
    'workspace.ttChecks.muzikOn': 'The robot turns on Music copyright check on TikTok for every video.',
    'workspace.ttChecks.muzikOff': 'The robot turns off Music copyright check on TikTok. Music in the video is not checked.',
    'workspace.ttChecks.kandunganOn': 'The robot waits for TikTok to finish Checking before Post / Schedule. If it does not finish, the video is saved as a draft.',
    'workspace.ttChecks.kandunganOff': 'The robot turns off Content check lite and goes straight to Post / Schedule. TikTok content warnings will not be detected.',
    'workspace.ttChecks.gagalSimpan': 'This choice could not be saved in this browser.',
    'workspace.page.sect.ttJenisPost': 'Post Type',
    'workspace.ttJenisPost.gambarOn': 'The robot uploads the card images (main image + extra images) as one TikTok photo post per card. No video is posted.',
    'workspace.ttJenisPost.gambarOff': 'The robot uploads the video of each selected set.',
    'workspace.post.fbSetLabel': 'Sets to post as reels',
    'workspace.post.fbSetHint': 'Each set = one reel. Pick which sets to post (e.g. S1 only).',
    'workspace.bag.title': 'Yellow bag',
    'workspace.bag.toggle': 'Add yellow bag',
    'workspace.bag.noteOn': 'The robot links the product from the card to every video (TikTok Shop yellow bag).',
    'workspace.bag.noteOff': 'Videos are posted without a yellow bag — no Showcase step, no product link.',
    'workspace.fbproduk.title': 'Product link',
    'workspace.fbproduk.toggle': 'Add product',
    'workspace.fbproduk.noteOn': 'The robot adds the Shopee link from the card to every reel (Facebook: Add product). The reel gets the "Commission eligible" label.',
    'workspace.fbproduk.noteOff': 'Reels are posted without a product link — no "Commission eligible" label. The Shopee link on the card is kept, just not used.',
    'ws.fb.perluKemasKini': 'Saving drafts and the AI label option for Facebook need the latest SESAAT extension. Please download and reinstall the extension from the dashboard, then try again.',
    'ws.fb.direct.on': 'Ticked cards are published on Facebook as soon as the robot finishes uploading.',
    'ws.fb.direct.off': 'The robot saves each reel as a DRAFT in your Facebook Content Library — you can review and publish it yourself.',
    'ws.fb.noVideo': 'No video is ready for the selected products. Run Flow first to generate the video.',
    'ws.fb.noShopeeWarn': '{n} product(s) have no Shopee link — the reel will be published WITHOUT the affiliate link.\n\nNever want a product link? Turn off "Add product" in the Control Panel.\n\nContinue?',
    'ws.fb.started': '🎬 Facebook Reels started!',
    'ws.fb.startedDetail': 'The robot is opening your Facebook Content Library and uploading the reel. Do not close that tab.',
    'ws.fb.startedDetailDraft': 'The robot is opening your Facebook Content Library and saving the reel as a draft. Do not close that tab.',
    'ws.fb.hasilPost': '{n} reel(s) published',
    'ws.fb.hasilJadual': '{n} reel(s) scheduled',
    'ws.fb.hasilDraf': '{n} draft(s) saved',
    'ws.fb.hasilGagal': '{n} failed',
    'ws.fb.hasilKosong': 'No reel was processed.',
    'ws.fb.hasilPage': 'Page: {nama}',
    'ws.run.extDisconnectedFb': 'This tab needs to be refreshed before Facebook can start.',
    'ws.fb.doneMsg': '✅ Facebook Reels done!',
    'ws.fb.doneAll': '{n} reel(s) published.',
    'ws.fb.doneSome': '{n} reel(s) published, {gagal} failed.',
    'ws.fb.failMsg': '❌ Facebook Reels failed',
    'ws.fb.failGeneric': 'Please try again.',
    'workspace.preview.count': '{produk} products · {video} videos',
    'workspace.preview.countWithSkip': '{produk} products · {video} videos · {langkau} skipped (no video)',
    'workspace.preview.noTimes': 'This schedule has no post times yet. Set them in Manage schedules.',
    'workspace.preview.noVideo': 'The products you ticked have no video for the sets this schedule uses.',
    'workspace.preview.overLimit': 'Beyond {hari} days — TikTok does not accept schedules that far ahead.',
    'workspace.preview.pickProducts': 'Tick a product to see the actual times.',
    'workspace.prototypeNote': 'Design plan · /workspace-v3.9',
    'workspace.qnFoot': 'Notes flagged in the Notes page appear here.',
    'workspace.run.collectionBlocked': 'Clear "In project" first.',
    'workspace.run.collectionEmpty': 'Fill in one only: In project OR Collection.',
    'workspace.run.collectionPlaceholder': 'Paste a collection link…',
    'workspace.run.collectionUsed': 'Use this collection — the "New Project" step is skipped.',
    'workspace.run.continueCollectionNote': 'Fill in the images or videos missing from a saved collection, and skip the sets that are already done. Select one product only.',
    'workspace.run.multiBrowserOff': 'Local storage only — for testing.',
    'workspace.run.multiBrowserOn': 'Videos are saved to the Cloud so other browsers can upload them to TikTok.',
    'workspace.run.needChannel': 'Switch on at least one channel',
    'workspace.run.needProduct': 'Select a product in the list first',
    'workspace.run.projectBlocked': 'Clear "Collection" first.',
    'workspace.run.projectEmpty': 'Leave this empty to create a new project every time.',
    'workspace.run.projectPlaceholder': 'Paste a project link…',
    'workspace.run.projectUsed': 'Use this project — the "New Project" step is skipped.',
    'workspace.run.proOnly': 'Available on the Pro Studio plan.',
    'workspace.run.setNote': 'One set produces one video. Tick only the sets you want to run.',
    'workspace.run.sceneNote': 'One scene produces one part of the video. Tick only the scenes you want to run.',
    'workspace.run.videoNote': 'Credit cost follows your Google plan. Flow shows the exact amount before you generate.',
    'workspace.run.durationNote': '10 seconds is only available on the Omni Flash model.',
    'workspace.page.run.resolutionLabel': 'Video resolution',
    'workspace.page.run.resolutionAria': 'Video resolution',
    'workspace.run.resolutionNote': 'Only Omni models offer a video quality option. Flow shows the actual credit cost before generating.',
    'workspace.page.run.dlSizeLabel': 'Video file size',
    'workspace.page.run.dlSizeAria': 'Video file size',
    'workspace.run.dlSizeNote': 'Matches the Download menu in Flow. The options change with your video resolution — 360p gives you 360p/720p; 720p (and every Veo model) gives you 720p/1080p. Upscaled adds about 30 seconds per video. 270p GIF and 4K (50 credits) are intentionally not offered. For 2-scene cards Flow downloads a joined video, so this choice does not apply.',
    'workspace.run.dlSizeLocked': 'This module joins the scenes into one video and downloads it straight from Flow — Flow offers no size choice for a joined video.',
    'workspace.run.ingredientsNote': 'On: the video references your product image. Off: normal Frames mode.',
    'workspace.workflow.title': 'Workflow',
    'workspace.workflow.q1': 'Where do the images come from?',
    'workspace.workflow.optGenerate': 'Generate in Flow',
    'workspace.workflow.optGenerateDesc': 'Flow generates an image first, then animates it into a video.',
    'workspace.workflow.optCard': 'Use card images',
    'workspace.workflow.optCardDesc': 'No image generation. The images on the card go straight into the video, e.g. a storyboard.',
    // ★ [2026-11-03] Dropdown kaya Workflow — PROTOTAIP /workspace-v3.9 (WorkflowPilihV39.jsx)
    'workspace.wfPilih.aria': 'Choose a workflow',
    'workspace.wfPilih.kosong': 'Choose a workflow',
    'workspace.wfPilih.draft': 'Draft',
    'workspace.wfPilih.draftNote': 'Draft workflows are visible to admins only. Customers see published workflows only.',
    'workspace.wfPilih.scenes': 'Scenes',
    'workspace.wfPilih.result': 'Result',
    'workspace.wfPilih.result.gabung': 'Combine',
    'workspace.wfPilih.result.asing': 'Each scene',
    'workspace.wfPilih.result.keduanya': 'Both',
    'workspace.wfPilih.versions': 'Versions per prompt',
    'workspace.wfPilih.versionsNote': 'More versions to choose from. Uses more credits.',
    'workspace.wfPilih.images': 'Images per prompt',
    // ★ [2026-11-03] Dropdown Workflow Workspace v4 mod New (WorkflowDropdownV4.jsx)
    'workspace.wfV4.tickFirst': 'Tick a card first',
    'workspace.wfV4.tickFirstNote': 'The workflow follows the Formula of the cards you tick.',
    'workspace.wfV4.mixed': '{n} workflows',
    'workspace.wfV4.mixedNote': 'Run one workflow at a time. Untick the cards that use a different Formula.',
    'workspace.wfV4.fromFormula': 'Other workflows come from the card\'s Formula.',
    'workspace.wfV4.noneLinked': 'No workflow linked for {formula}',
    'workspace.wfV4.noneLinkedNote': 'The run still follows the card\'s Formula. Link a robot to a workflow in the Workflow page to show it here.',
    'workspace.wfV4.blankNote': 'Blank card: this workflow is not connected to Run yet. The run will use AI image to video, 1 scene.',
    'workspace.wfV4.loadError': 'Could not load the workflow list',
    'workspace.wfV4.empty': 'The workflow list is empty',
    'workspace.wfV4.emptyNote': 'Open the Workflow page and load the starter workflows.',
    'workspace.wfV4.rosak': '{n} workflow(s) in the Workflow page are invalid and were skipped.',
    // ★ [2026-10-17] Scene output + card enjin Flow RESIPI (PLAN_FLOW_RESIPI.md)
    'workspace.productRef.title': 'Product photo',
    'workspace.productRef.optAll': 'All scenes',
    'workspace.productRef.optAllDesc': 'The product photo is the reference for every scene image.',
    'workspace.productRef.optFirst': 'First scene only',
    'workspace.productRef.optFirstDesc': 'Shows the product at the start. Other scenes are generated from their prompts.',
    'workspace.productRef.optLast': 'Last scene only',
    'workspace.productRef.optLastDesc': 'Reveals the product at the end. Other scenes are generated from their prompts.',
    'workspace.productRef.optNone': 'None',
    'workspace.productRef.optNoneDesc': 'All images are generated from prompts only, without the product photo or avatar.',
    'ws.resipi.imageScene': 'Image Prompt · Scene {n}',
    'ws.resipi.imageScenePlaceholder': 'Image Prompt Scene {n}...',
    'workspace.imageSave.title': 'Save images',
    'workspace.imageSave.optFolder': 'Downloads folder only',
    'workspace.imageSave.optFolderDesc': 'Images are saved in your Downloads folder. The card does not show them.',
    'workspace.imageSave.optCard': 'Downloads folder and card',
    'workspace.imageSave.optCardDesc': 'Images are saved in your Downloads folder and shown on the card.',
    'workspace.imageOutputs.title': 'Images per prompt',
    'workspace.imageOutputs.opt': '{n} image(s)',
    'workspace.imageOutputs.optOneDesc': 'One image for each Image Prompt.',
    'workspace.imageOutputs.optManyDesc': 'Flow generates {n} different images from the same prompt.',
    'ws.resipi.imageEnd': 'Image Prompt · After (End frame)',
    'workspace.sceneOutput.title': 'Scene output',
    'workspace.sceneOutput.optCombine': 'Combine into one video',
    'workspace.sceneOutput.optCombineDesc': 'All {n} scenes are joined into one video and saved to the card.',
    'workspace.sceneOutput.optEach': 'Download each scene',
    'workspace.sceneOutput.optEachDesc': 'Each of the {n} scenes is saved as a separate file in your Downloads folder. The card does not show these videos.',
    'ws.resipi.allScenes': 'all scenes',
    'ws.resipi.allScenesLong': 'one AI image used in all {n} scenes',
    'ws.resipi.imagePlaceholder': 'Image Prompt — one AI image for all scenes...',
    'ws.resipi.videoPlaceholder': 'Video Prompt Scene {n}...',
    'workspace.workflow.imageUnused': 'Not used — this workflow skips image generation.',
    'workspace.workflow.ingredientsLocked': 'Always on — Frames mode only takes a start and end frame.',
    'workspace.workflow.runLabel': 'Run Flow · Card images',
    'workspace.workflow.veoImageLimit': 'Veo uses up to 3 card images (main image, avatar, then image 2). Omni Flash uses all of them.',
    'workspace.workflow.durationLocked': 'Veo only makes 8-second videos from card images.',
    'workspace.workflow.kadTakSokong': 'The selected cards use {module} — this mode is for AI8SAAT and SMART 1 scene only. This run will use “Generate di Flow”. Select AI8SAAT cards to use card images.',
    'workspace.schedule.addBtn': 'Add',
    'workspace.schedule.addTime': 'Add time',
    'workspace.schedule.cancel': 'Cancel',
    'workspace.schedule.cancelDraft': 'Cancel',
    'workspace.schedule.clearBtn': 'Clear',
    'workspace.schedule.closeAria': 'Close',
    'workspace.schedule.closeBtn': 'Close',
    'workspace.schedule.closeTitle': 'Close (Esc)',
    'workspace.schedule.dateAtRun': 'Dates are calculated at Run',
    'workspace.schedule.delete': 'Delete',
    'workspace.schedule.deleteConfirm': 'Delete “{nama}”?',
    'workspace.schedule.deleteUnusedNote': 'This template is not used by any day.',
    'workspace.schedule.deleteUsedNote': 'Used by {n} days — all slots on those days will be cleared.',
    'workspace.schedule.deleteUsedNoteOne': 'Used by 1 day — all slots on that day will be cleared.',
    'workspace.schedule.discardChanges': 'Discard changes',
    'workspace.schedule.done': 'Done',
    'workspace.schedule.dragHandle': 'Drag to rearrange',
    'workspace.schedule.duplicate': 'Duplicate',
    'workspace.schedule.endTimeAria': 'End time',
    'workspace.schedule.fillBtn': 'Fill',
    'workspace.schedule.fillEvery': 'Every',
    'workspace.schedule.fillFrom': 'from',
    'workspace.schedule.fillIntervalAria': 'Interval between times',
    'workspace.schedule.fillTo': 'to',
    'workspace.schedule.interleaveHintOff': 'Available when more than one set is selected.',
    'workspace.schedule.interleaveHintOn': 'Send set 1 for every product first, then set 2. When off, one product is finished before moving to the next.',
    'workspace.schedule.listEmpty': 'No templates yet.',
    'workspace.schedule.listHeading': 'Templates',
    'workspace.schedule.modalAria': 'Schedule template',
    'workspace.schedule.moveDown': 'Move down',
    'workspace.schedule.moveUp': 'Move up',
    'workspace.schedule.nameAria': 'Template name',
    'workspace.schedule.newTemplate': 'New template',
    'workspace.schedule.newTimeAria': 'New time',
    'workspace.schedule.noneSelected': 'No template selected',
    'workspace.schedule.noneSelectedBody': 'Create a template to set your posting times.',
    'workspace.schedule.noTimesHint': 'Days using this template will have no slots at all.',
    'workspace.schedule.noTimesSet': 'No times set',
    'workspace.schedule.removeTime': 'Remove {t}',
    'workspace.schedule.rowInterleave': 'Interleave sets',
    'workspace.schedule.rowName': 'Name',
    'workspace.schedule.rowSendTimes': 'Send times',
    'workspace.schedule.rowSets': 'Sets',
    'workspace.schedule.save': 'Save',
    'workspace.schedule.saved': 'Saved',
    'workspace.schedule.sendTimesHint': 'The times you set here are used EVERY day. Videos beyond that count continue on the next day. Every {minit} minutes only — a TikTok limit.',
    'workspace.schedule.setsHint': 'Create one template per set if different sets go to different browsers.',
    'workspace.schedule.slotsPerDay': '{n} slots/day',
    /* Ringkasan kad jadual (scheduleV39.js → ringkasJadual). */
    'workspace.schedule.allSets': 'All sets',
    'workspace.schedule.noSets': 'No sets',
    'workspace.schedule.interleave': 'Interleaved',
    'workspace.schedule.byCard': 'By card',
    'workspace.schedule.noTimes': 'No times',
    'workspace.schedule.startToday': 'Starts today',
    'workspace.schedule.startTomorrow': 'Starts tomorrow',
    'workspace.schedule.startTimeAria': 'Start time',
    'workspace.schedule.subtitle': 'Timing rules only — products are chosen during Run, dates are chosen in the calendar.',
    'workspace.schedule.timeExists': '{jam} is already in this template.',
    'workspace.schedule.title': 'Schedule templates',
    'workspace.schedule.unsavedWarn': 'Changes to this template have not been saved.',
    /* Dua bentuk — English mesti membezakan tunggal/jamak, kalau tidak
       template yang dipakai satu hari tertulis "Used by 1 days". */
    'workspace.schedule.usedByDay': 'Used by {n} day',
    'workspace.schedule.usedByDays': 'Used by {n} days',
    /* Tiga ini dibaca oleh PEMBACA SKRIN sahaja (PilihWaktuV39) — tiada
       pada skrin, tetapi ia tetap antara muka. */
    'workspace.tiktok.timeLabel': 'Time',
    'workspace.tiktok.timeHour': '{label} — hour',
    'workspace.tiktok.timeMinute': '{label} — minute',
    'workspace.tiktok.accountNameAria': 'New account name',
    'workspace.tiktok.accountNamePlaceholder': 'Account name…',
    'workspace.tiktok.accountSelectAria': 'TikTok account',
    'workspace.tiktok.addAccount': 'Add account',
    'workspace.tiktok.searchAccount': 'Search account',
    'workspace.tiktok.noAccountMatch': 'No account with that name.',
    'workspace.tiktok.renameAccount': 'Rename account',
    'workspace.tiktok.deleteAccount': 'Delete account',
    'workspace.tiktok.deleteAccountConfirm': 'Delete?',
    /* Kalendar mingguan (KalendarMingguV39). Terma yang memang kekal
       English pada skrin ini — Set, Template, Status, Caption, Retry,
       Run, Drop product — dibiarkan seperti asal dalam kedua-dua bahasa. */
    'workspace.tiktok.cal.addTime': 'Add time',
    'workspace.tiktok.cal.caption': 'Caption',
    'workspace.tiktok.cal.close': 'Close',
    'workspace.tiktok.cal.dayToday': 'Today',
    'workspace.tiktok.cal.dayTomorrow': 'Tomorrow',
    'workspace.tiktok.cal.dayYesterday': 'Yesterday',
    'workspace.tiktok.cal.detailAria': 'Video details',
    'workspace.tiktok.cal.dragMarked': 'Drag all {n} marked slots at once',
    'workspace.tiktok.cal.dropProduct': 'Drop product',
    'workspace.tiktok.cal.extraTimeDot': 'Extra time on this day only',
    'workspace.tiktok.cal.newTimeLabel': 'New time for this day',
    'workspace.tiktok.cal.nextDays': 'Next {n} days',
    'workspace.tiktok.cal.noTemplate': 'No template',
    'workspace.tiktok.cal.tickDay': 'Select {hari} {tarikh} for Run',
    'workspace.tiktok.cal.tickDayTip': 'Tick to run this day\'s slots',
    'workspace.tiktok.cal.pilihHariTip': 'Select all slots on this day',
    'workspace.tiktok.cal.pickTemplate': 'Pick a template to open slots.',
    'workspace.tiktok.cal.pickTemplateOrAdd': 'Pick a template to open slots — or add your own time below.',
    'workspace.tiktok.cal.prevDays': 'Previous {n} days',
    'workspace.tiktok.cal.removeProduct': 'Remove',
    'workspace.tiktok.cal.removeProductAria': 'Remove the product from this slot',
    'workspace.tiktok.cal.removeTime': 'Remove this time',
    'workspace.tiktok.cal.retry': 'Retry',
    'workspace.tiktok.cal.retryTitle': 'Try scheduling this set again',
    'workspace.tiktok.gagalBar': '{n} slots failed',
    'workspace.tiktok.retryAll': 'Retry all',
    'workspace.tiktok.gagalTutup': 'Hide this notice — the Failed status stays on the card and slot. It shows again when a new slot fails.',
    'workspace.tiktok.sendBar': '{n} sending…',
    'workspace.tiktok.stopRun': 'Stop',
    'ws.sched.stop.ok': 'Run stopped. Stuck slots cleared.',
    'ws.sched.stop.tiada': 'No run to stop.',
    'ws.sched.vid.tiada': 'No video to delete for this set.',
    'workspace.tiktok.cal.rowDate': 'Date',
    'workspace.tiktok.cal.rowStatus': 'Status',
    'workspace.tiktok.cal.rowTemplate': 'Template',
    'workspace.tiktok.cal.rowTime': 'Time',
    'workspace.tiktok.cal.saveTimeAria': 'Save time',
    'workspace.tiktok.cal.setNoVideoWait': 'Set {n}: no video yet — the slot will wait',
    'workspace.tiktok.cal.setOnOtherAccount': 'Set {n}: already scheduled on {akaun} — can be repeated',
    'workspace.tiktok.cal.slotHint': 'Click to mark · drag to another time · drop on a filled slot to swap places',
    'workspace.tiktok.cal.statusWaiting': 'Waiting for Run',
    'workspace.tiktok.cal.stDone': 'Scheduled',
    'workspace.tiktok.cal.stPosted': 'Posted',
    'workspace.tiktok.cal.stDraft': 'Draft',
    'workspace.tiktok.cal.stFail': 'Failed',
    'workspace.tiktok.cal.stUncertain': 'Uncertain',
    'workspace.tiktok.cal.stRunning': 'Sending…',
    'workspace.tiktok.cal.stNone': 'Not run yet',
    'workspace.tiktok.cal.editStatus': 'Change slot status',
    'workspace.tiktok.cal.delVideo': 'Delete video',
    'workspace.tiktok.cal.delVideoTip': 'Delete the video for this set only — card, prompt and caption stay.',
    'workspace.tiktok.cal.templateForAria': 'Template for {hari} {tarikh}',
    'workspace.tiktok.cal.timeExists': 'That time is already on this day.',
    'workspace.tiktok.cal.timezone': 'Times shown in Asia/Kuala_Lumpur',
    'workspace.tiktok.cal.today': 'Today',
    'workspace.tiktok.cal.useSet': 'Use set {n}',
    'workspace.tiktok.cal.waitVideo': 'Waiting for video',
    'workspace.tiktok.cancel': 'Cancel',
    'workspace.tiktok.deselect': 'Deselect',
    'workspace.tiktok.statusSlotTitle': 'Change or clear the status on the marked slots — the videos STAY at their times, and the status pills on the cards follow',
    'workspace.tiktok.deselectTitle': 'Clear the marks, keep the slots',
    'workspace.tiktok.dragExhausted': 'All videos for this product are already in the calendar',
    'workspace.tiktok.dragHint': 'Drag to an empty slot in the calendar',
    'workspace.tiktok.emptyBody': 'Run Flow first — only products that already have video can be scheduled.',
    'workspace.tiktok.emptyTitle': 'No products with video',
    'workspace.tiktok.hint': 'Pick a template under each date, then drag products into the empty slots that appear.',
    'workspace.tiktok.products': 'Products',
    'workspace.tiktok.remove': 'Remove',
    'workspace.tiktok.removeSelectedTitle': 'Remove the marked slots from the calendar',
    'workspace.tiktok.save': 'Save',
    'workspace.tiktok.saveAccountAria': 'Save account',
    'workspace.tiktok.scheduleTemplate': 'Schedule template',
    'workspace.tiktok.setNoVideo': 'Set {n}: no video',
    'workspace.tiktok.setReady': 'Set {n}: ready',
    'workspace.tiktok.setScheduled': 'Set {n}: already scheduled',
    'workspace.tiktok.setScheduledOnAccount': 'Set {n}: already scheduled on {akaun}',
    'workspace.tiktok.slotsSelected': '{n} slots selected',
    'workspace.tiktok.runSelected': 'Run selected',
    'workspace.tiktok.runSelectedTitle': 'Run only the selected slots',
    'workspace.tiktok.undo': 'Undo',
    'workspace.tiktok.undoTitle': 'Undo the last change to this calendar',
    'workspace.uploadVideo': 'Upload video',
    'workspace.videoReady': 'Video ready',

    /* ── ws (WorkspaceView) ──────────────────────────────────── */
    'ws.agent.alert.agentTiada': 'Agent "{nama}" no longer exists — it may have been deleted in PRO Studio. Please select another agent.',
    'ws.agent.alert.belumConfigure': 'Agent "{nama}" is not configured yet.\n\nOpen PRO Studio → My Agent → Step 5 (Configure), run it once with a single product, then Save.',
    'ws.agent.alert.gagalBacaAgent': 'Could not read the latest settings for agent "{nama}". Check your internet connection, then press Run again.',
    'ws.agent.alert.pilihAgent': 'Please select an agent first.',
    'ws.agent.alert.pilihTemplate': 'Select a template first',
    'ws.agent.alert.tiadaKadFlownity': 'No FLOWNITY cards selected. Turn on the Smart switch to run Smart Custom cards with your own agent.',
    'ws.agent.alert.tiadaKadSmart': 'No Smart Custom cards selected. The Smart switch runs Smart Custom cards only — turn it off for FLOWNITY cards.',
    'ws.agent.alert.tiadaProduk': 'No products selected.',
    'ws.agent.ruang.tajuk': '{n} cards have no image space',
    'ws.agent.ruang.tajuk1': '1 card has no image space',
    'ws.agent.ruang.sebab': 'This agent saves images to the card. A card can hold only {jumlah} images: 1 main image and {maks} additional images. The cards below have no space left for new images.',
    'ws.agent.ruang.baris': 'Additional images {ada}/{maks} · needs {perlu} free',
    'ws.agent.ruang.caraTajuk': 'How to fix',
    'ws.agent.ruang.cara1': 'Press Edit data on the marked card, remove the additional images you do not need, then Save.',
    'ws.agent.ruang.cara2': 'Or untick that card and choose another one.',
    'ws.agent.ruang.cara3': 'Press Run again.',
    'ws.agent.ruang.tiadaJalan': 'Nothing was run.',
    'ws.agent.ruang.faham': 'Got it',
    'ws.agent.ruang.label': 'No image space',
    'ws.agent.ruang.labelTip': 'The selected agent needs {perlu} free image space on this card. Remove additional images through Edit data.',
    'ws.flowPrompt.tajuk': '{n} cards are missing prompts',
    'ws.flowPrompt.tajuk1': '1 card is missing prompts',
    'ws.flowPrompt.sebab': 'Flow needs a prompt for every set it runs. Without one, the robot has to use the product name alone — the result is unusable and your Flow credits are wasted.',
    'ws.flowPrompt.sebabBabak': 'Flow needs a prompt for every scene. Scenes without a prompt are skipped, so the final video is incomplete.',
    'ws.flowPrompt.caraTajuk': 'How to fix',
    'ws.flowPrompt.cara1': 'Fill in the missing prompt — regenerate that card, or paste your own prompt.',
    'ws.flowPrompt.cara2': 'Or untick that card, or untick that set in the Control Panel.',
    'ws.flowPrompt.cara3': 'Press Run again.',
    'ws.flowPrompt.tiadaJalan': 'Nothing was run.',
    'ws.flowPrompt.faham': 'Got it',
    'ws.flowModel.tajuk': 'Run stopped — video model not available',
    'ws.flowModel.sebabUltra': 'The video model "{model}" is not available in your Google Flow account. This model is only offered on the Google AI Ultra plan.',
    'ws.flowModel.sebab': 'The video model "{model}" is not available in your Google Flow account.',
    'ws.flowModel.tiadaKredit': 'SESAAT stopped before creating a collection, so no Flow credits were used.',
    'ws.flowModel.baris': 'Stopped before the collection was created',
    'ws.flowModel.caraTajuk': 'How to fix',
    'ws.flowModel.langkah1': 'Open Control Panel → Flow',
    'ws.flowModel.langkah2Ada': 'Choose a Video model available in your account',
    'ws.flowModel.langkah2': 'Choose a different Video model',
    'ws.flowModel.langkah3': 'Press Run again',
    'ws.flowModel.nota': 'The other cards in this Run were not run either.',
    'ws.flowModel.faham': 'Got it',
    'ws.flowPrompt.kurangGambar': 'Image Prompt',
    'ws.flowPrompt.kurangVideo': 'Video Prompt',
    'ws.flowPrompt.kurangDua': 'Image Prompt and Video Prompt',
    'ws.flowPrompt.barisSet': 'Set {n}: missing {apa}',
    'ws.flowPrompt.barisBabak': 'Scene {n}: no prompt',
    'ws.flowPrompt.chipSet': 'No prompt · Set {senarai}',
    'ws.flowPrompt.chipBabak': 'No prompt · Scene {senarai}',
    'ws.agent.progress.dihentikan': 'Stopped ({siap}/{jumlah})',
    'ws.agent.progress.memproses': 'Processing {nama}...',
    'ws.agent.progress.cubaSemula': 'Retrying {nama}...',
    'ws.agent.progress.mula': 'Starting...',
    'ws.agent.progress.selesai': 'Done {siap}/{jumlah}',
    'ws.agent.saya.belumConfigure': ' — not configured',
    'ws.agent.saya.butangRun': 'Run Agent ({bil})',
    'ws.agent.saya.pilih': '— Select an agent —',
    'ws.agent.saya.tajuk': 'My Agent',
    'ws.agent.saya.tiadaAgent': 'No agents — open PRO Studio',
    'ws.agent.saya.titleBelumConfigure': 'This agent is not configured in PRO Studio yet',
    'ws.agent.saya.titlePilihDulu': 'Select an agent first',
    'ws.agent.saya.titleRun': 'Run Agent',
    'ws.agent.titlePilihProduk': 'Select products first',
    'ws.agent.titleSedangJalan': 'Running...',
    'ws.agent.unnamed': 'Unnamed agent',
    'ws.ap.title': 'Agent Flownity',
    'ws.ap.comingSoon': 'Coming soon',
    'ws.ap.readyMade': 'Ready-made agent by SESAAT. FLOWNITY cards only for now.',
    'ws.ap.myAgent': 'My agent',
    'ws.ap.noAgentYet': 'No agent yet',
    'ws.ap.buildOwn': 'Build your own agent in PRO Studio, then pick it here.',
    'ws.ap.openProStudio': 'Open PRO Studio',
    'ws.ap.selectAgent': 'Select agent',
    'ws.ap.notConfigured': 'Not configured',
    'ws.ap.pickToSetup': 'Pick an agent to see its setup.',
    'ws.ap.platform': 'Platform',
    'ws.ap.notConfiguredLong': 'Not configured. Run it once in PRO Studio, step 5, then save.',
    'ws.ap.configured': 'Configured',
    'ws.ap.setsPerProduct': 'Sets per product',
    'ws.ap.scenes': 'Scenes',
    'ws.ap.output': 'Output',
    'ws.ap.productPhoto': 'Product photo',
    'ws.ap.avatar': 'Avatar',
    'ws.ap.fieldsMatched': 'Fields matched',
    'ws.ap.savesImagesOne': 'Saves {n} image',
    'ws.ap.savesImagesMany': 'Saves {n} images',
    'ws.ap.saveImages': 'Saves images',
    'ws.ap.perCardOne': '{n} image per card',
    'ws.ap.perCardMany': '{n} images per card',
    'ws.ap.editInProStudio': 'Edit in PRO Studio',
    'ws.ap.outImgNoVidOne': '{n} image, no video',
    'ws.ap.outImgNoVidMany': '{n} images, no video',
    'ws.ap.outImgVidOne': '{n} image, {n} video',
    'ws.ap.outImgVidMany': '{n} images, {n} videos',
    'ws.ap.sceneEvery': 'every scene',
    'ws.ap.sceneNone': 'not used',
    'ws.ap.sceneN': 'scene {n}',
    'ws.ap.running': 'Running {c} of {t}',
    'ws.ap.noProduct': 'No product selected',
    'ws.ap.nSelected': '{n} selected',
    'ws.ap.skipped': '{n} skipped, {jenis}',
    'ws.ap.notSmart': 'not Smart Custom',
    'ws.ap.notFlownity': 'not FLOWNITY',
    'ws.ap.tipRunning': 'Running...',
    'ws.ap.tipPickConfigured': 'Pick a configured agent first',
    'ws.ap.tipSelectCard': 'Select at least one card',
    'ws.ap.runMyAgent': 'Run My Agent',
    'ws.ap.runAgentAI': 'Run Agent AI',
    'ws.alert.skippedNoData': '{n} products were pulled in but were NOT generated because they have no product data.\n\nPlease import those products using the Import Products button in the Products page, then pull them back into the Workspace.',
    'ws.arkib.alert.berjaya': '"{nama}" has been archived!',
    'ws.arkib.alert.gagal': 'Failed to archive the product.',
    'ws.arkib.alert.import': '"{nama}" has been imported into this space!',
    'ws.auto.cancel': 'Cancel',
    'ws.auto.close': 'Close',
    'ws.auto.disconnectedBody': 'The extension is not responding. This usually happens after an extension update — it is not a problem with your products.',
    'ws.auto.disconnectedHint': 'Click the button below to refresh this tab. After refreshing, click the Run button again.',
    'ws.auto.disconnectedSub': 'This tab needs to be refreshed first',
    'ws.auto.disconnectedTitle': '🔴 Extension Disconnected',
    'ws.auto.extensionConnected': 'Extension Connected',
    'ws.auto.processing': 'Processing…',
    'ws.auto.refreshNow': 'Refresh This Tab Now',
    'ws.avatar.campur': 'The selected cards mix male and female. Select cards of the same gender only — one avatar cannot serve two genders.',
    'ws.avatar.hanyaGender': ' · {gender} only',
    'ws.avatar.hijab': 'Hijab',
    'ws.avatar.namaLalai': 'Avatar',
    'ws.avatar.tiadaAvatar': 'You have no {gender} avatars in your library. Create a {gender} avatar first in the Avatar panel (sidebar).',
    'ws.avatar.tiadaHijab': 'No Hijab',
    'ws.avatar.tiadaJantina': 'The selected cards have no gender — the avatar cannot be changed (the avatar must match the gender in the original prompt).',
    'ws.avatar.tukarTajuk': 'Change avatar',
    'ws.avatarChange.cancel': 'Cancel',
    'ws.avatarChange.confirm': 'Yes, change it',
    'ws.avatarChange.count': '{n} cards will have their avatar changed. Prompts and videos stay — no regeneration, no coins.',
    'ws.avatarChange.fallbackName': 'Avatar',
    'ws.avatarChange.female': '♀ Female',
    'ws.avatarChange.hijab': 'Hijab',
    'ws.avatarChange.male': '♂ Male',
    'ws.avatarChange.noHijab': 'No Hijab',
    'ws.avatarChange.warning': 'Finished prompts and videos were written for the original avatar (gender, age, hijab). Make sure the new avatar matches — for example, if the original wore a hijab, do not pick one without, or the results will not match.',
    'ws.avatarChange.warningLabel': '⚠️ Please note:',
    'ws.card.avatarAttached': 'Avatar attached',
    'ws.card.collapse': 'Collapse card',
    'ws.card.copyCollectionLink': 'Copy collection link',
    'ws.card.copyLink': 'Copy link',
    'ws.card.customBackground': 'custom background',
    'ws.card.duplicateFrom': 'This card is a copy of "{nama}" — click to make more copies',
    'ws.card.duplicateFromDeleted': 'This card is a copy (the original card has been deleted) — click to make more copies',
    'ws.card.editData': 'Edit data',
    'ws.card.editDataFail': 'Could not load the product data. Try again.',
    'ws.card.editLink': 'Edit link',
    'ws.card.expand': 'Expand card',
    'ws.card.extraInstruction': 'Extra Instruction',
    'ws.card.extraInstructionAdd': 'Add an extra instruction (appended to the Agent AI message)',
    'ws.card.extraInstructionTip': 'Extra Instruction: {teks}',
    'ws.card.handMen': 'Hand: Men',
    'ws.card.handWomen': 'Hand: Women',
    'ws.card.hookSyncWarn': '⚠ Image Prompt did not auto-update — hook pattern not detected. Please edit the Image Prompt manually.',
    'ws.card.linkTiktokProduct': 'TikTok product',
    'ws.card.linkShopee': 'Shopee link',
    'ws.card.linkShopeeProduct': 'Shopee product',
    'ws.gateBeg.tajuk': '{n} cards have no TikTok product',
    'ws.gateBeg.sebab': 'A yellow bag cannot be added to these cards. Nothing has been run yet.',
    'ws.gateBeg.tandaTiada': 'No link',
    'ws.gateBeg.petua': 'To post these cards without a yellow bag: turn off "Add yellow bag" in the Control Panel, then run only these cards.',
    'ws.gateBeg.petuaSemua': 'The videos will be sent as usual, just without a yellow bag. Your "Add yellow bag" setting stays as it is.',
    'ws.gateBeg.langkau': 'Skip these {n} cards, run the other {m}',
    'ws.gateBeg.tanpaBeg': 'Post these {n} cards without a yellow bag',
    'ws.card.loadFullTextFailed': 'Failed to load the text from the cloud. Please close this window and try again.',
    'ws.card.loadingFullText': 'Loading full text from the cloud…',
    'ws.card.men': 'Men',
    'ws.card.moduleBadge': 'Module: {nama}',
    'ws.card.noLink': 'No link',
    'ws.card.openInExtension': 'Open in Extension',
    'ws.card.retryPipeline': 'Retry Pipeline',
    'ws.card.retryShort': 'RETRY',
    'ws.card.templateBg': 'Template BG: {fail}',
    'ws.card.video.checking': 'Checking…',
    'ws.card.video.local': 'Local Video',
    'ws.card.video.none': 'No Video',
    'ws.card.video.ready': 'Video Ready',
    'ws.card.video.savedCloud': 'Saved to Cloud',
    'ws.card.video.savedLocal': 'Saved in this browser',
    'ws.card.video.thumbAlt': 'Video thumbnail',
    'ws.card.viewEditFullPrompt': 'View / Edit full prompt',
    'ws.card.women': 'Women',
    'ws.common.cancel': 'Cancel',
    'ws.common.close': 'Close',
    'ws.common.copy': 'Copy',
    'ws.common.noName': '(unnamed)',
    'ws.common.save': 'Save',
    'ws.common.tryAgain': 'Try again',
    'ws.confirm.calculating': ' (calculating space…)',
    'ws.confirm.cancel': 'Cancel',
    'ws.confirm.delete': 'Delete',
    'ws.confirm.deleteProductsBtn': 'Delete {n} products',
    'ws.confirm.deleteProductsMsg': 'The related videos will be deleted from cloud storage{ruang}. This action cannot be undone.',
    'ws.confirm.deleteProductsTitle': 'Delete {n} products?',
    'ws.confirm.deleteVideoMsg': 'The video for {setLabel} will be deleted from the cloud. This action cannot be undone.',
    'ws.confirm.deleteVideoTitle': 'Delete this video?',
    'ws.confirm.deleteVideosBtn': 'Delete {n} videos',
    'ws.confirm.deleteVideosMsg': 'Only the videos will be deleted from cloud storage — products, prompts and captions stay{ruang}. This action cannot be undone.',
    'ws.confirm.deleteVideosTitle': 'Delete {n} videos?',
    'ws.confirm.deleting': 'Deleting…',
    'ws.confirm.deletingProgress': 'Deleting {selesai}/{jumlah}…',
    'ws.confirm.kosongPromptBtn': 'Clear {n} cards',
    'ws.confirm.kosongPromptBtnSatu': 'Clear 1 card',
    'ws.confirm.kosongPromptDijana': ' Still generating, skipped: {n}.',
    'ws.confirm.kosongPromptHadMasa': 'the connection is too slow',
    'ws.confirm.kosongPromptMsg': 'All prompt text and TikTok captions in {set} will be cleared. Videos, images and product data stay. This action cannot be undone.',
    'ws.confirm.kosongPromptSemak': 'Checking…',
    'ws.confirm.kosongPromptSemakMsg': 'Checking the text on the marked cards…',
    'ws.confirm.kosongPromptSudahKosong': ' Already empty, skipped: {n}.',
    'ws.confirm.kosongPromptTitle': 'Clear prompts in {set}?',
    'ws.confirm.kosongPromptTulis': 'Clearing…',
    'ws.confirm.spaceFreed': ' (~{saiz} will be freed)',
    'ws.detail.loadFailed': 'Failed to load the product text',
    'ws.detail.loadFailedRetry': 'The text could not be loaded — click to try again',
    'ws.detail.loadingPrompt': 'Loading prompt text…',
    'ws.dup.cancel': 'Cancel',
    'ws.dup.confirm': 'Copy {jumlah} cards',
    'ws.dup.confirmLama': 'Yes, copy {jumlah} cards',
    'ws.dup.copiesAria': 'Number of copies',
    'ws.dup.copiesPerCard': 'Copies per card',
    'ws.dup.copiesPerCardLama': 'How many copies of each card?',
    'ws.dup.copiesTitle': 'Number of copies (no limit)',
    'ws.dup.dataKadFailed': '{n} copies created, but the product data edited on the original card could not be copied. Please delete those copies and duplicate again.',
    'ws.dup.failed': 'Failed to create copies: {msg}',
    'ws.dup.noQuota': 'No generation quota is used',
    'ws.dup.notCopied': 'Videos, thumbnails and TikTok upload status are not copied — each new card generates its own video when you Run Flow. Copies are numbered, for example (2), so the video folders are easy to tell apart.',
    'ws.dup.selectedCount': '{n} cards selected',
    'ws.dup.success': '{n} copies created — ready for Run Flow. No generation quota was used.',
    'ws.dup.title': 'Duplicate cards',
    'ws.dup.titleLama': 'Duplicate Cards',
    'ws.dup.tryAgain': 'Try again',
    'ws.dup.willCreate': '{jumlah} new cards will be created with the same prompts, product images, template and avatar.',
    'ws.dup.willCreateLama': '{jumlah} new cards will be created with the same prompts, product images, template and avatar. No generation quota is used because there is no new AI generation.',
    'ws.ext.putus.detailAgent': 'This tab must be refreshed before you can run Agent AI.',
    'ws.ext.putus.detailAgentSambung': 'This tab must be refreshed before continuing the Agent AI run.',
    'ws.ext.putus.tajuk': 'Extension Disconnected',
    'ws.ext.yatim.butang': 'Refresh',
    'ws.ext.yatim.tajuk': 'The extension was reloaded',
    'ws.ext.yatim.teks': 'Refresh this tab to resume syncing with the extension.',
    'ws.extra.cancel': 'Cancel',
    'ws.extra.delete': 'Delete',
    'ws.extra.panelTitle': 'Extra Instruction — appended to this product\'s Agent AI message',
    'ws.extra.placeholder': 'Example: Use a humorous style. Target an audience of mothers.',
    'ws.extra.save': 'Save',
    'ws.flownity.akanDatang': ' — Coming Soon',
    'ws.flownity.alert.gagalMula': 'Failed to start the scrape: {ralat}',
    'ws.flownity.alert.pilihProduk': 'Select at least one product first.',
    'ws.flownity.alert.urlBelumSet': 'The admin has not set the {jenisAgen} URL for FLOWNITY.\nOpen the ⚙ FLOWNITY gear → "Scrape Agent AI" section → choose {platform} → enter the URL + Save.',
    'ws.flownity.scrapeTajuk': 'Scrape Agent AI',
    'ws.gen.goyangNeedTemplate': 'GOYANG requires a template. Please select a template in the sidebar before trying again.',
    'ws.gen.initializing': 'Initializing…',
    'ws.gen.modulesNotReady': 'The module list is still loading. Please wait 1-2 seconds, then try Generate again.',
    'ws.gen.needSubModule': 'This module requires a {label} selection. Please choose one in the sidebar before Generate.',
    'ws.gen.noData': 'This product has no product data. Please import it again using the Import Products button in the Products page, then pull it back into the Workspace.',
    'ws.gen.status.cached': 'Intelligence cached — skipping analysis...',
    'ws.gen.status.init': 'Initializing...',
    'ws.gen.step.generate.detail': 'Generating high-quality content...',
    'ws.gen.step.generate.label': 'Creative Generation',
    'ws.gen.step.research.detail': 'Searching web for product info...',
    'ws.gen.step.research.label': 'Web Research',
    'ws.gen.step.vision.detail': 'Analyzing product image...',
    'ws.gen.step.vision.label': 'Image Analysis',
    'ws.gen.subModuleFallback': 'technique',
    'ws.gen.unknownError': 'Unknown error.',
    // Hasil Generate tertinggal (page ditutup semasa Generate) — useHasilGenerateTertinggal
    'ws.gen.tertinggal.terhenti': 'Generation stopped before it finished. Please press Retry.',
    'ws.gen.tertinggal.terlaluBesar': 'The generated result is too large to save. Please press Retry.',
    'ws.gen.tertinggal.kosong': 'Generation did not return a result. Please press Retry.',
    'ws.gen.tertinggal.tiadaLogic': 'The generated result could not be added because the module settings have not loaded yet. Please press Retry.',
    'ws.gender.lelaki': 'Male',
    'ws.gender.lelakiRingkas': 'Men',
    'ws.gender.perempuan': 'Female',
    'ws.gender.perempuanRingkas': 'Women',
    'ws.image.metaSaveFailed': 'The image was uploaded, but saving its details failed: {msg}',
    'ws.image.readFailed': 'Failed to read the image file: {msg}',
    'ws.image.reloginNeeded': 'Please log in again to replace the image.',
    'ws.image.unknownError': 'unknown',
    'ws.image.uploadFailed': 'Failed to upload the image: {msg}',
    'ws.import.done': '{n} products imported',
    'ws.import.failed': 'Failed to save the bulk import. Please try again.',
    'ws.input.empty': 'Please enter the product data.',
    'ws.input.skippedNoData': '{n} products were added but were NOT generated because they have no product data.\n\nPlease import the products using the Import Products button in the Products page, then pull them into the Workspace again.',
    'ws.jadual.kepalaProduk': 'Products',
    'ws.jadual.seret.habis': 'All three sets are already in this calendar',
    'ws.jadual.seret.pukal': 'Drag {bil} marked cards at once · the number of slots follows the template of the day you drop on',
    'ws.jadual.seret.tunggal': 'Drag to a calendar slot · {guna}/3 sets already scheduled',
    'ws.jadual.titik.diLuar': 'Set {n}: already scheduled on {tarikh}',
    'ws.jadual.titik.dijadual': 'Set {n}: already in this calendar',
    'ws.jadual.titik.sedia': 'Set {n}: ready',
    'ws.jadual.titik.tiadaVideo': 'Set {n}: no video yet',
    'ws.jana.batalTitle': 'Cancel',
    'ws.jana.dismiss': 'Dismiss',
    'ws.jana.gagal.gangguan': 'Temporary Outage',
    'ws.jana.gagal.tajuk': 'Pipeline Failed',
    'ws.jana.kapsul.teks': 'writing 3 sets of prompts…',
    'ws.jana.lama.kemajuan': 'Progress',
    'ws.jana.lama.langkahSiap': 'Completed',
    'ws.jana.lama.memproses': 'Processing your content...',
    'ws.jana.lama.mula': 'Initializing...',
    'ws.jana.lama.mulaPipeline': 'Initializing pipeline...',
    'ws.jana.lama.semuaSelesai': 'All steps finished',
    'ws.jana.lama.siap': 'Pipeline Complete!',
    'ws.jana.lama.tajuk': 'AI Pipeline',
    'ws.jana.lencana.peringkat': '3 Stages',
    'ws.jana.lencana.title': 'Pipeline AI — 3 stages of analysis for quality video output',
    'ws.jana.lencanaAi': 'PIPELINE AI',
    'ws.kad.bgLalai': 'custom background',
    'ws.kad.buangTanda': 'Clear mark',
    'ws.kad.bukaSemua': 'Expand all cards',
    'ws.kad.editNama': 'Edit name',
    'ws.kad.idTitle': 'TikTok product ID',
    'ws.kad.pilTandaFlow': 'Click to mark as Flow',
    'ws.kad.pilTandaTiktok': 'Click to mark as TikTok',
    'ws.kad.pratonton': 'Preview',
    'ws.kad.salinNama': 'Copy name',
    'ws.kad.tambahGambar': 'Add image',
    'ws.kad.tambahIsi': 'Add product or image',
    'ws.kad.tambahProduk': 'Add product',
    'ws.kad.tandakan': 'Mark card',
    'ws.kad.tangan': 'Hand: {gender}',
    'ws.kad.templateBg': 'Template BG: {fail}',
    'ws.kad.tukarGambar': 'Change Image',
    'ws.kad.tutupSemua': 'Collapse all cards',
    'ws.mixed.body': 'You selected products from {n} different modules. Each module uses a different automation script, so they cannot run together in one batch. Please select from one module only, or run them separately.',
    'ws.mixed.productCount': '{n} products',
    'ws.mixed.subtitle': 'Select only one module to Run Flow',
    'ws.mixed.title': 'Cannot Run Mixed Modules',
    'ws.mixed.understood': 'Got it',
    'ws.panel.paparButang': 'Show Control Panel',
    'ws.panel.paparTitle': 'Show Control Panel',
    'ws.preview.alt': 'Preview',
    'ws.prompt.editAllFullscreen': 'Edit all prompts (fullscreen)',
    'ws.prompt.empty': 'Empty',
    'ws.prompt.hookPlaceholder': 'Hook line — editing here auto-syncs to the Image Prompt',
    'ws.prompt.nsceneDialoguePlaceholder': 'Dialogue Scene {n}...',
    'ws.prompt.nsceneImagePlaceholder': 'Image Prompt Scene {n}...',
    'ws.prompt.nsceneVideoPlaceholder': 'Video Prompt Veo 3 Scene {n}...',
    'ws.prompt.tiktokCaptionPlaceholder': 'TikTok caption...',
    'ws.prompt.voiceoverPlaceholder': 'Voiceover script — auto-syncs to the Video Prompt',
    'ws.prompt.voiceoverScene1Placeholder': 'Voiceover Scene 1 — auto-syncs to the Video Prompt (Scene 1)',
    'ws.prompt.voiceoverScene2Placeholder': 'Voiceover Scene 2 — auto-syncs to the Video Prompt (Scene 2)',
    'ws.promptModal.bgFragment': '🔎 BG fragment: {nilai}',
    'ws.promptModal.closeQuickNotes': 'Close Quick Notes',
    'ws.promptModal.fragmentUsed': '🔎 Fragment used: {nilai}',
    'ws.promptModal.nsceneImagePlaceholder': 'Image Prompt Scene {n} — visuals only',
    'ws.promptModal.nsceneVideoPlaceholder': 'Video Prompt Veo 3 Scene {n} — visuals + dialogue',
    'ws.promptModal.openQuickNotes': 'Open Quick Notes',
    'ws.promptModal.saveBtn': 'Save',
    'ws.promptModal.saveHint': 'Edit, then click Save to keep your changes',
    'ws.promptModal.saved': 'Saved successfully',
    'ws.promptModal.savedBtn': 'Saved',
    'ws.promptModal.title': 'Edit Prompts',
    'ws.promptModal.unsavedChanges': 'There are unsaved changes',
    'ws.promptModal.untitledProduct': 'Untitled product',
    'ws.pukal.batalTitle': 'Clear selection',
    'ws.pukal.cancel': 'Cancel',
    'ws.pukal.del': 'Del',
    'ws.pukal.delete': 'Delete',
    'ws.pukal.dipilih': '{bil} selected',
    'ws.pukal.dipilihLama': '{bil} selected',
    'ws.pukal.duplicate': 'Duplicate',
    'ws.pukal.duplicateTitle': 'Duplicate the selected cards — same prompt, ready for Run Flow (uses no generation quota)',
    'ws.pukal.groupBaharu': 'New Group',
    'ws.pukal.groupUtama': 'Main Group',
    'ws.pukal.kosong': 'Clear',
    'ws.pukal.kosongPromptTitle': 'Clear the prompt text and captions on the marked cards — videos, images and product data are not touched',
    'ws.pukal.mark': 'Mark',
    'ws.pukal.markAgentDilangkau': '{bil} card(s) were not moved. Only Smart Custom and FLOWNITY cards can be placed in {tab}.',
    'ws.pukal.move': 'Move',
    'ws.pukal.namaGroup': 'Group name',
    'ws.pukal.pilihSemua': 'Select all',
    'ws.pukal.pilihSemuaTitle': 'Select All (Ctrl+A)',
    'ws.pukal.retry': 'Retry',
    'ws.pukal.retryTitle': 'Regenerate the pipeline for the marked cards',
    'ws.pukal.save': 'Save',
    'ws.pukal.semuaSet': 'All Sets',
    'ws.pukal.status': 'Status',
    'ws.pukal.statusTitle': 'Change or clear the status on the marked cards for the account open in the calendar. That account\'s calendar slots follow; videos and captions are not touched.',
    'ws.pukal.tukar': 'Change',
    'ws.quota.dailyEmpty': 'Your daily quota is used up ({used}/{cap}). The quota resets at 12:00 AM (Malaysia).',
    'ws.quota.dailyShort': 'You selected {count} products, but only {remaining} of {cap} quota is left today.\n\nPlease reduce your selection to {remaining} products or fewer. The quota resets at 12:00 AM (Malaysia).',
    'ws.quota.trialEmpty': 'Your trial coins are used up (0/{cap}). Please upgrade to a paid plan to continue.',
    'ws.quota.trialShort': 'You selected {count} products, but you only have {remaining} trial coins left.\n\nPlease reduce your selection to {remaining} products or fewer, then try again.',
    'ws.raw.close': 'Close',
    'ws.raw.copied': '✓ Copied!',
    'ws.raw.copyAll': 'Copy All',
    'ws.raw.copyChatLink': 'Copy chat link',
    'ws.raw.plain': 'Plain',
    'ws.raw.title': 'Raw AI Output',
    'ws.retry.bulkConfirm': 'Re-run the pipeline for {n} products?\n\n• Existing prompts & captions will be deleted and generated again\n• Existing videos are KEPT — they are not touched\n• Uses {n} of your daily generation quota',
    'ws.retry.confirm': 'Reset "{name}" to a fresh pipeline?\n\nThis will:\n• Clear all prompts & captions\n• Reset the status to pending\n• Keep existing videos\n• Run the AI pipeline again automatically',
    'ws.retry.failed': '❌ Failed to reset: {msg}',
    'ws.run.continueOneOnly': 'For Continue Collection, select only ONE product.',
    'ws.run.extDisconnected': 'Extension Disconnected',
    'ws.run.extDisconnectedFlow': 'This tab needs to be refreshed before Flow can start.',
    'ws.run.extDisconnectedTiktok': 'This tab needs to be refreshed before TikTok can start.',
    'ws.run.failed': '❌ Automation failed!',
    'ws.run.flowTitle': 'Flow Automation — {n} products',
    'ws.run.hydrateFailed': 'Failed to load the prompt text from the Cloud. Please check your internet connection and try again.',
    'ws.run.loginFirst': 'Please log in first.',
    'ws.run.moduleMaintenance': 'This module is under maintenance — we are adapting the extension to the latest Google Labs Flow interface. We will reopen it as soon as it is stable.',
    'ws.run.noCollection': 'This product has no saved collection yet.\nRun Flow normally once first — the collection is saved automatically, then you can Continue.',
    'ws.run.selectProduct': 'Please select the products you want to run.',
    'ws.run.selectSet': 'Please select at least one Set (S1-S3).',
    'ws.run.workflowKadModul': 'The "Use card images" workflow currently works for AI8SAAT and SMART 1 scene only.\n\nThe selected cards use {module}. Switch Workflow to "Generate in Flow" to run these cards.',
    'ws.run.workflowKadVeo': 'Veo models use only 3 card images.\n\n{n} selected cards have more than 3 images — the rest will not go into the video (priority: main image, avatar, then image 2, 3…).\n\nChoose Omni Flash to use every image.\n\nContinue with 3 images?',
    'ws.run.setsLabel': 'Sets: {sets}',
    'ws.run.started': '🚀 Automation started!',
    'ws.run.startedDetail': '{n} products have been sent to the extension. The extension is opening Google Flow...',
    'ws.run.step.done': 'Done!',
    'ws.run.step.prepare': 'Preparing products...',
    'ws.run.step.send': 'Sending instructions to the extension...',
    'ws.run.tasksSent': '{n} tasks sent ✓',
    'ws.run.tiktokAfterFlow': '\n★ TikTok will run automatically after Flow finishes.',
    'ws.save.ralat.arahan': 'Click {reload} to reload. After reloading you can paste the clipboard contents to recover.',
    'ws.save.ralat.butangReload': 'Reload',
    'ws.save.ralat.clipboard': '✓ {bil} edits were copied to the clipboard as a backup.',
    'ws.save.ralat.tajuk': 'Save failed after 3 attempts',
    'ws.save.status.mencubaSemula': 'Retrying ({cubaan})...',
    'ws.save.status.menyimpan': 'Saving...',
    'ws.save.status.tersimpan': '✓ Saved',
    'ws.sched.min15': 'TikTok requires a schedule at least 15 minutes from now.\n\nPlease pick a later start time.',
    'ws.sched.noCaption': 'No caption on this set.',
    'ws.sched.noSlot': 'No empty slots — {n} videos could not be placed. Pick a template on another date, or clear existing slots.',
    'ws.sched.noTemplate': 'This day has no template yet — pick a template on the day header first, then drag videos in.',
    'ws.sched.akaunTerakhir': 'Cannot delete the last account. Create another one first.',
    'ws.sched.akaunDipadam': 'Calendar account deleted.',
    'ws.sched.run.noDate': 'Tick at least one date on the calendar header before Run.',
    'ws.sched.run.noPilih': 'Please select at least one slot before Run — tick a day header to select all its slots, or click slots individually.',
    'ws.phone.noVideo': "No video on the selected cards or slots. Select a card or slot that has a video.",
    'ws.sched.run.noRunnable': 'No slots can run on the ticked dates.',
    'ws.sched.run.rVideo': '{n} slots have no video yet (run Flow first)',
    'ws.sched.run.rGambar': '{n} slots have a card with no images (Photo post)',
    'ws.tiktok.gambar.tiada': 'Photo post: {n} cards have no images (main / extra image). Untick these cards or add images, then Run again.',
    'ws.tiktok.gambar.tiadaSet': 'Photo post: select at least one set (the post status is saved on that set).',
    'ws.sched.run.rLain': '{n} slots skipped — already run by another browser',
    'ws.sched.run.rMasa': '{n} slots are too soon (less than 15 minutes away)',
    'ws.sched.run.rHad': '{n} slots are past the {had}-day limit',
    'ws.sched.run.rProduk': '{n} slots whose product no longer exists',
    'ws.sched.run.rKunci': '{n} slots whose date could not be read — reopen the Schedule tab, then drag those videos in again',
    'ws.sched.run.skipped': '{n} slots skipped (video not ready / time passed). {jalan} will run.',
    'ws.sched.run.otherGroup': '{n} of these slots belong to another group — they will still run.',
    'ws.sched.run.title': 'TikTok Schedule — {n} videos',
    'ws.sched.run.detail': '{n} videos at each slot\'s exact time.',
    'ws.sched.run.startedDetail': '{n} videos sent to the Extension — will be scheduled at each slot\'s exact time.',
    'ws.sched.run.bar': '{n} videos · {julat}',
    'ws.sched.run.barKosong': 'No slots selected',
    'workspace.cara.title': 'How to post',
    'workspace.cara.now': 'Post now',
    'workspace.cara.schedule': 'Schedule',
    'workspace.cara.draft': 'Save as draft',
    'workspace.cara.nowNote': 'Publishes immediately. One video per Run only.',
    'workspace.cara.scheduleNote': 'The robot schedules each video at the time of its ticked calendar slot.',
    'workspace.cara.scheduleKunci': 'Only from the Schedule tab — the calendar sets the time.',
    'workspace.cara.draftNoteFb': 'Saved as a draft in Facebook Content Library — review and publish yourself.',
    'workspace.cara.draftNoteTt': 'Saved as a draft on TikTok — edit (add music, etc.) and publish yourself.',
    'workspace.cara.pilihDulu': 'Pick one option first — the Run button stays locked until you do.',
    'workspace.cara.showcase': 'Showcase Only does not upload any video, so this does not apply.',
    'workspace.cara.runKunci': 'Pick how to post',
    'workspace.page.post.directSchedule': 'Direct Schedule',
    'workspace.page.post.directScheduleOff': 'Schedule (Save in Draft)',
    'ws.sched.direct.on': 'The robot schedules & publishes each video at its slot time. You don\'t need the browser open at that time.',
    'ws.sched.direct.off': 'The robot saves each video as a DRAFT first — you can edit it (add music, etc.) and publish it yourself on TikTok.',
    'ws.sched.overLimit': 'This schedule exceeds the TikTok limit.\n\n{bil} videos at {selang}-minute intervals push the last video beyond {had} days from now, and TikTok does not accept dates that far ahead.\n\nPlease shorten the interval, or reduce the number of videos in this run.',
    'ws.sched.partial': '{diletak} videos placed{hujung}. {tinggal} more did not fit — slots on the following days are full or have no template yet.',
    'ws.sched.pickStart': 'Please pick the schedule start time first.',
    'ws.sched.placed': '{diletak} videos placed{hujung}.',
    'ws.sched.step5': 'TikTok only accepts schedules on 5-minute steps (for example 14:30, 14:35).\n\nPlease adjust the minutes on the start time.',
    'ws.sched.untilSuffix': ' (until {tarikh})',
    'ws.senarai.kosong.agent': 'Mark products as {agent} from the {flow} / {tiktok} tab for them to appear here',
    'ws.senarai.kosong.tajuk': 'No Products',
    'ws.senarai.kosong.tajukTab': 'No {tab} Products',
    'ws.senarai.kosong.tambah': 'Add products using the form above',
    'ws.senarai.kosong.tiktok': 'Mark products as {tiktok} to see them here',
    'ws.sort.hint': 'Press the same option again to reverse the direction.',
    'ws.sort.module': 'Module',
    'ws.sort.number': 'Number',
    'ws.sort.time': 'Time',
    'ws.sort.manual': 'My order',
    'ws.gbr.buka': 'Show all {bil} product images',
    'ws.gbr.tutup': 'Collapse product images',
    'ws.gbr.utama': 'Main',
    'ws.gbr.tiadaAvatar': 'No avatar',
    'ws.gbr.seret': 'Image {n} — click to view large, drag to arrange. Image 1 is the main image',
    'ws.gbr.tajuk': 'Product image {n} of {bil} — sent to Flow',
    // Paparan gambar besar (PratontonGambar.jsx, 2026-09-13) — borang Data Produk + jalur gambar kad
    'gbrBesar.aria': 'Large image view',
    'gbrBesar.tutup': 'Close (Esc)',
    'gbrBesar.sebelum': 'Previous image (←)',
    'gbrBesar.seterusnya': 'Next image (→)',
    'gbrBesar.ralat': 'This image cannot be displayed.',
    'gbrBesar.lihat': 'View large',
    'gbrBesar.lihatNo': 'View image {n} large',
    'gbrBesar.utama': 'Main',
    'gbrBesar.dipilih': 'Selected',
    'gbrBesar.pilih': 'Select this image',
    'gbrBesar.buang': 'Remove from selection',
    'gbrBesar.penuh': 'All {max} extra image slots are full — remove one first.',
    'gbrBesar.kiraTambahan': 'Extra images {n}/{max}',
    'gbrBesar.galeri': 'TikTok gallery',
    'ws.sort.tip.manual': 'Drag cards to arrange',
    'ws.susun.seret.tunggal': 'Drag to arrange cards',
    'ws.susun.seret.pukal': 'Drag {bil} selected cards together to arrange',
    'ws.sort.tip.newest': 'Newest first',
    'ws.sort.tip.oldest': 'Oldest first',
    'ws.space.kelihatan': 'Visible',
    'ws.space.kustomAntaraMuka': 'Interface Customization',
    'ws.space.labelCaptionTiktok': 'TikTok Caption Label',
    'ws.space.labelPromptImej': 'Image Prompt Label',
    'ws.space.labelPromptVideo': 'Video Prompt Label',
    'ws.space.logicLalai': 'Default AI Logic',
    'ws.space.modLarian.label': 'Execution Mode',
    'ws.space.modLarian.tip': 'Choose how the browser processes your videos.',
    'ws.space.modScene.tajuk': 'Scene Mode',
    'ws.space.modScene.teks': 'One browser handles every scene at once.',
    'ws.space.modSet.tajuk': 'Set Mode',
    'ws.space.modSet.teks': 'Work is split by set across different browsers.',
    'ws.space.pilihPersona': 'Select a default persona...',
    'ws.space.sentiasaHidup': 'ALWAYS ON',
    'ws.space.set1Tajuk': 'SET 1 TITLE',
    'ws.space.set2Tajuk': 'SET 2 TITLE',
    'ws.space.set3Tajuk': 'SET 3 TITLE',
    'ws.space.showField': 'SHOW FIELD',
    'ws.space.subtajuk': 'Control how the AI works for the {nama} space',
    'ws.space.tajuk': 'Space Settings',
    'ws.space.urusSet': 'Set Management',
    'ws.split.alert.gagalMuat': 'Failed to load the full product text from the Cloud. Please check your internet connection and try again.',
    'ws.susun.ikutMasa': 'By Time',
    'ws.susun.ikutModule': 'By Module',
    'ws.susun.ikutNombor': 'By Number',
    'ws.susun.menaik': 'Ascending (1-10)',
    'ws.susun.menurun': 'Descending (10-1)',
    'ws.susun.modulAZ': 'Module A-Z',
    'ws.susun.modulZA': 'Module Z-A',
    'ws.susun.terbaru': 'Newest',
    'ws.susun.terbaruDulu': 'Newest First',
    'ws.susun.terlama': 'Oldest',
    'ws.susun.terlamaDulu': 'Oldest First',
    'ws.tab.agentAi': 'My Agent',
    'ws.tab.all': 'All',
    'ws.tab.flow': 'Flow',
    'ws.tab.generate': 'Generate',
    'ws.tab.schedule': 'Schedule',
    'ws.tab.tiktok': 'Tiktok',
    'ws.tiktok.doneMode.postOnly': ' Post Video Only.',
    'ws.tiktok.doneMode.tanpaBeg': ' Post video without a yellow bag.',
    'ws.tiktok.doneMode.showcaseOnly': ' Showcase Only.',
    'ws.tiktok.doneMode.showcasePost': ' Showcase + Post Video.',
    'ws.tiktok.failed': '❌ TikTok Automation failed!',
    'ws.tiktok.mode.postOnly': ' | Post Only',
    'ws.tiktok.mode.tanpaBeg': ' | No yellow bag',
    'ws.tiktok.mode.showcaseOnly': ' | Showcase Only',
    'ws.tiktok.mode.showcasePost': ' | Showcase + Post',
    'ws.cara.pilihDulu': 'Pick how to post first (Post now · Schedule · Save as draft) in the Control Panel.',
    'ws.sched.run.nowSatu': 'Post now is for ONE video only. You ticked {n} slots — keep just one, or pick Schedule / Save as draft.',
    'ws.fb.jadualKalendar': 'Schedule is only available from the Schedule tab — there is no time to schedule here. Go to the Schedule tab and tick slots, or pick Post now / Save as draft.',
    'ws.fb.perluKemasKiniKalendar': 'Run Facebook from the calendar needs the latest SESAAT extension. Please download and reinstall the extension from the dashboard, then try again.',
    'ws.fb.run.startedDetail': '{n} reels sent to the Extension — the robot opens Facebook Content Library and follows each slot time. Do not close that tab.',
    'ws.fb.run.rMasa': '{n} slots are too close to now (under 30 minutes)',
    'ws.fb.run.rHad': '{n} slots are beyond the {had}-day limit',
    'ws.fb.stopNota': 'The Facebook robot cannot be stopped mid-way — the reel being uploaded will be finished first.',
    'ws.caption.fb': 'Facebook Caption',
    'ws.caption.fbHint': 'Empty = the TikTok caption is used.',
    'ws.caption.fbPlaceholder': 'Facebook caption (optional)',
    'ws.caption.fbKosong': 'Empty — the Facebook robot uses the TikTok caption',
    'ws.caption.suis': 'Switch the caption shown: TikTok / Facebook',
    'ws.caption.label': 'Caption',
    'ws.caption.fbKosongSengaja': 'Empty — Facebook will post without a caption',
    'ws.tiktok.noneSelected': 'No products selected to run.',
    'ws.tiktok.sentLabel': '{n} products × {sets} sets sent ✓',
    'ws.tiktok.started': '🎉 TikTok Automation started!',
    'ws.tiktok.startedDetail': '{n} products have been sent straight to the extension.{mod} The extension is opening TikTok Studio...',
    'ws.tiktok.step.prepare': 'Preparing products for TikTok...',
    'ws.tiktok.title': 'TikTok Automation — {n} products',
    'ws.toast.kosongPromptGagal': 'Could not clear the prompts: {msg}. Nothing was changed.',
    'ws.toast.kosongPromptSemakGagal': 'Could not check the cards: {msg}. Nothing was cleared.',
    'ws.toast.kosongPromptSemuaJana': 'All marked cards are still generating — wait for them to finish first.',
    'ws.toast.kosongPromptSeparuh': '{ok} of {n} cards cleared — the rest failed: {msg}',
    'ws.toast.kosongPromptSiap': 'Prompts cleared on {n} cards.',
    'ws.toast.kosongPromptSiapSatu': 'Prompts cleared on 1 card.',
    'ws.toast.kosongPromptTiada': 'No prompts to clear in {set}.',
    'ws.toast.productsDeleted': '{n} products deleted · {size} freed',
    'ws.toast.saveFailedVideoRestored': 'Save failed — the video was restored. {msg}',
    'ws.agent.toast.simpanGagal': 'The agent result for "{nama}" could not be saved. Please run this product again. ({msg})',
    'ws.sched.kosong.ok': '{n} slot statuses cleared.',
    'ws.sched.kosong.okSatu': '1 slot status cleared.',
    'ws.sched.kosong.running': 'Slots that are being sent were not cleared — use Stop to halt them.',
    'ws.sched.kosong.tiada': 'No status to clear on the marked slots.',
    'ws.toast.statusDikosongkan': '{n} statuses cleared.',
    'ws.toast.statusDikosongkanSatu': '1 status cleared.',
    'ws.toast.statusDitukar': '{n} statuses changed to {status}.',
    'ws.toast.statusDitukarSatu': '1 status changed to {status}.',
    'ws.toast.statusSudah': 'Already {status} — nothing changed.',
    'ws.toast.statusBerjalan': 'Sets that are still sending were left unchanged — use Stop to halt them.',
    'ws.toast.statusTiada': 'No status for {akaun} on the marked cards.',
    'ws.toast.statusTiadaVideo': 'Cards with no video for that set were skipped — a status only applies to a set that has a video.',
    'ws.toast.statusSlotTiadaVideo': 'Slots whose set has no video were skipped — a status only applies to a set that has a video.',
    'ws.toast.statusKadTiada': 'Slots whose card is no longer in the list were skipped.',
    'ws.status.setKosong': 'No video and no status for this set on the marked cards',
    'ws.status.akaun': 'Account: {akaun}',
    'ws.status.tukarKe': 'Change {set} to',
    'ws.status.tukarSlot': 'Change {n} slots to',
    'ws.status.semuaSetFrasa': 'all sets',
    'ws.toast.videoDeleteFailed': 'Failed to delete the video: {msg}',
    'ws.toast.videoDeleted': 'Video deleted successfully',
    'ws.toast.videoDeletedOrphan': 'The video was deleted from the database, but the file in Storage could not be removed — it will be cleaned up automatically later',
    'ws.toast.videoKeptLocal': '💾 {n} videos stay in this browser — Cloud video storage is not available right now. Nothing is lost: they can still be posted to TikTok from this browser.',
    'ws.toast.videoPushFailed': '{n} videos could not be uploaded — open them in a browser with the SESAAT Extension active.',
    'ws.toast.videoQuotaKept': '💾 {n} videos stay in this browser — the daily cloud limit is used up, storage is full, or the video is over 50MB. They can still be posted to TikTok from this browser.',
    'ws.toast.videoPushed': '{n} videos uploaded to Cloud',
    'ws.toast.videosDeleted': '{n} videos deleted · {size} freed',
    'ws.toast.videosDeletedOrphan': '{n} videos deleted · some Storage files could not be removed — they will be cleaned up automatically later',
    'ws.umum.batal': 'Cancel',
    'ws.umum.ok': 'OK',
    'ws.umum.selesai': 'Done',
    'ws.umum.simpan': 'Save',
    'ws.umum.tutup': 'Close',
    'ws.upload.blockedModule': '📥 For {label} products, video upload must be done through the SESAAT extension.\n\nSteps:\n1. Open the SESAAT extension\n2. Find the same product card\n3. Click the "Mass Video" button\n4. Choose the video file (set1/set2/set3) from the Downloads folder\n\nVideos for this module cannot be saved to Cloud because they are large — they are only kept in the storage of this browser.',
    'ws.upload.blockedModuleFallback': 'this module',
    'ws.upload.fileTooLarge': 'File too large (max 100MB)',
    'ws.upload.notAuthenticated': 'User not authenticated. Please log in again.',
    'ws.upload.storageFull': '⚠️ Storage limit reached!\n\nUsed: {used} MB / {limit} MB\nFile size: {file} MB\n\nPlease delete some old videos to free up space.',
    'ws.upload.videoFailed': 'Failed to upload the video:\n{msg}',
    'ws.upload.videoTooBigCloud': 'This video is {size}MB — over the 50MB cloud limit. Please upload it through the SESAAT Extension: large videos are kept in your browser and can still be posted to TikTok.',
    'ws.upload.cloudUnavailable': 'Cloud storage is not available right now, or the video exceeds the 50MB limit. Please upload this video through the SESAAT Extension.',
    'ws.vergate.body': 'You are still using an old version of the SESAAT extension. Please update to continue.',
    'ws.vergate.checking': 'Checking…',
    'ws.vergate.close': 'Close',
    'ws.vergate.download': 'Download the Latest Version',
    'ws.vergate.dupBody': 'This browser has {n} SESAAT extensions active at the same time. Your latest version works as normal, but the old one is still installed and needs to be removed.',
    'ws.vergate.dupOk': 'Got it',
    'ws.vergate.dupStep1': 'Type chrome://extensions in the address bar',
    'ws.vergate.dupStep2': 'Find every SESAAT card. Press Remove on the ones that are not the latest version',
    'ws.vergate.dupStep3': 'Make sure only one remains, then refresh this tab',
    'ws.vergate.dupSub': 'Please remove the old version from your browser',
    'ws.vergate.dupTitle': 'An Old Extension Is Still Installed',
    'ws.vergate.dupWhy1': 'A Run command can be sent twice — duplicate videos and wasted Flow quota.',
    'ws.vergate.dupWhy2': 'The old extension still tries to save your data, but the system rejects those saves without notice.',
    'ws.vergate.dupWhy3': 'It counts as two browsers against your licence limit.',
    'ws.vergate.dupWhyTitle': 'Why does it need to be removed?',
    'ws.vergate.duplicateNotice': 'We detected more than one SESAAT extension in this browser. The new version does not replace the old one automatically — it installs as a separate extension. Please remove the old one at step 2 below.',
    'ws.vergate.iHaveUpdated': 'I Have Updated',
    'ws.vergate.requiredVersion': 'Required: v{versi}',
    'ws.vergate.step1': 'Download the ZIP file below',
    'ws.vergate.step2': 'Open chrome://extensions and press Remove on the old SESAAT extension',
    'ws.vergate.step3': 'Install the new extension (full guide in Dashboard, Step 1)',
    'ws.vergate.step4': 'Return to this tab and press “I Have Updated”',
    'ws.vergate.stillOld': 'The system still detects an old version. The most common cause: the old extension has not been removed from chrome://extensions. Please make sure only one SESAAT extension remains, then try again.',
    'ws.vergate.sub': 'The latest version is required to continue',
    'ws.usgate.title': 'One More Step',
    'ws.usgate.sub': 'Turn on one switch in your browser',
    'ws.usgate.why': 'This switch lets SESAAT receive automation updates directly from us — so you do not have to reinstall the extension every time we ship a fix. You only need to do this once.',
    'ws.usgate.chrome': 'Google Chrome',
    'ws.usgate.c1': 'Open chrome://extensions in a new tab',
    'ws.usgate.c2': 'Click Details on the SESAAT extension',
    'ws.usgate.c3': 'Turn on Allow user scripts',
    'ws.usgate.edge': 'Microsoft Edge',
    'ws.usgate.e1': 'Open edge://extensions in a new tab',
    'ws.usgate.e2': 'If Allow user scripts is there — turn it on',
    'ws.usgate.e3': 'If not — turn on Developer mode at the bottom instead',
    'ws.usgate.gagal': 'Still not detected. Make sure the switch is really on, then reload this page before trying again.',
    'ws.usgate.semak': 'I have turned it on — check',
    'ws.usgate.imgcap': `Look for this row and turn on the switch on the right. Chrome shows a \"may be unsafe\" warning — that is normal; this switch only lets SESAAT receive updates from us.`,
    'ws.vergate.title': 'Extension Update Required',
    'ws.vergate.yourVersionOld': 'Your version: outdated',
    'ws.video.delete': 'Delete',
    'ws.video.deleting': 'Deleting…',
    'ws.video.download': 'Download',
    'ws.video.extNotResponding': 'The extension is not responding. Make sure the SESAAT Extension is active.',
    'ws.video.notInLocalCache': 'Video not found in local cache',
    'ws.video.uploadVideo': 'Upload Video',
    'ws.videoModal.close': 'Close (Esc)',
    'ws.videoModal.escToClose': 'Esc to close',
    'ws.videoModal.loading': 'Loading the video from the extension…',

    /* ── Marketplace product page · Boost · Hook Library (2026-09-23) ── */
    'market.detail.loadFailed': 'Failed to load the product: {ralat}',
    'market.detail.failed': 'Failed: {ralat}',
    'market.detail.freeClaimed': 'Done! The ebook has been opened. You can download it again from My Purchases.',
    'market.detail.notFoundTitle': 'Product not found',
    'market.detail.notFoundDesc': 'This product may have been removed or has not been published yet.',
    'market.detail.stock': 'Stock: {n}',
    'market.detail.save': 'Save {n}%',
    'market.detail.fulfil.manual': 'Delivered by admin',
    'market.detail.fulfil.autoStock': 'Automatic account access',
    'market.detail.fulfil.autoDownload': 'Automatic download',
    'market.detail.fulfil.manualDesc': 'Once your payment is confirmed, the admin will hand over your access manually. Check the status in My Purchases.',
    'market.detail.fulfil.autoStockDesc': 'As soon as payment is complete, the login details are released instantly in My Purchases. No waiting, no need to contact the admin.',
    'market.detail.fulfil.autoStockDaysDesc': 'As soon as payment is complete, the login details are released instantly in My Purchases — access is valid for {n} days. No waiting, no need to contact the admin.',
    'market.detail.fulfil.freeDesc': 'Click and download right away — no payment needed.',
    'market.detail.fulfil.autoDownloadDesc': 'Once your payment is confirmed, you can download the file right away in My Purchases.',
    'market.detail.soldOutBtn': 'Sold Out',
    'market.detail.processing': 'Processing…',
    'market.detail.getFree': 'Get it Free',
    'market.detail.buy': 'Buy — {harga}',
    'market.detail.checkoutTitle': 'Continue to Payment',
    'market.detail.payNote': 'You will be taken to the Billplz payment page (FPX or card). Once payment is complete, your order appears in My Purchases.',
    'market.detail.payNoteAutoStock': 'You will be taken to the Billplz payment page (FPX or card). Once payment is complete, your access is released instantly in My Purchases.',
    'boost.subtitle': 'TikTok and Shopee programs from our MCN and TAP partners.',
    'boost.adminBadge': 'Admin only · preview only',
    'boost.previewBanner': 'Preview only. The data below is sample data typed into the code — it is not connected to Google Sheets yet, and the form does not save anything. Customers cannot see this page.',
    'boost.previewState': 'Preview state',
    'boost.state.locked': 'No plan',
    'boost.state.form': 'Not registered',
    'boost.state.list': 'Registered',
    'boost.openMenu': 'Open menu',
    'boost.locked.title': 'Boost for paying customers',
    'boost.locked.desc': 'This page is available to Premium, Fighter and SMART customers with an active subscription.',
    'boost.locked.cta': 'View plans',
    'boost.form.title': 'Register to join Boost',
    'boost.form.desc': 'Fill this in once. Your WhatsApp number is used to add you to the MCN / TAP support group.',
    'boost.form.name': 'Full name',
    'boost.form.namePh': 'Name as in your IC',
    'boost.form.email': 'Email',
    'boost.form.emailPh': '(your account email)',
    'boost.form.emailHint': 'Taken from your SESAAT account.',
    'boost.form.wa': 'WhatsApp number',
    'boost.form.tiktok': 'TikTok username',
    'boost.form.submit': 'Submit',
    'boost.form.previewNote': 'Preview only — this form does not save anything yet. The Submit button only takes you to the list view for preview.',
    'boost.howTitle': 'How to use Boost',
    'boost.step1': 'Open the seller campaign link on your PHONE.',
    'boost.step2': 'Add the seller\'s product to your showcase, from inside the TikTok app.',
    'boost.step3': 'Scrape your showcase with the SESAAT extension — Import Products, choose Option 2.',
    'boost.sellerCount': '{n} seller(s)',
    'boost.viewSellers': 'View sellers',
    'boost.joinGroup': 'Join group',
    'boost.allMcn': 'All MCN / TAP',
    'boost.searchPh': 'Search seller name',
    'boost.allCategories': 'All categories',
    'boost.bonusOnly': 'Bonus only',
    'boost.emptyTitle': 'No matching sellers',
    'boost.emptyDesc': 'Try clearing the search or the category filter.',
    'boost.col.seller': 'Seller',
    'boost.col.category': 'Category',
    'boost.col.bonus': 'Bonus',
    'boost.col.gmv': 'GMV requirement',
    'boost.col.postRate': 'Post rate',
    'boost.col.note': 'Note',
    'boost.col.link': 'Campaign link',
    'boost.yes': 'Yes',
    'boost.open': 'Open',
    'boost.copy': 'Copy',
    'boost.copied': 'Copied',
    'boost.footHint': 'Open the link on your phone, add the product to your showcase from the TikTok app, then scrape your showcase in the extension (Import Products → Option 2).',
    'boost.adminNotOpen': 'Admin only — customers can\'t see Boost yet',
    'boost.loading': 'Loading Boost…',
    'boost.retry': 'Try again',
    'boost.profile.title': 'Your Boost profile',
    'boost.profile.desc': 'Filled in once. These details are shared only with the MCN you apply to.',
    'boost.profile.empty': 'Your profile isn\'t filled in yet. You\'ll be asked for it when you apply.',
    'boost.profile.edit': 'Edit profile',
    'boost.profile.save': 'Save profile',
    'boost.profile.saved': 'Profile saved',
    'boost.profile.note': 'Changes apply to new applications only.',
    'boost.form.tiktok2': 'Second TikTok username',
    'boost.form.shopee': 'Shopee username',
    'boost.form.optional': 'optional',
    'boost.form.required': 'required for this program',
    'boost.form.waHint': 'Used by the MCN to add you to their WhatsApp group.',
    'boost.cancel': 'Cancel',
    'boost.apply': 'Apply',
    'boost.apply.title': 'Apply · {mcn} {program}',
    'boost.apply.desc': 'Check your details. They are sent to {mcn} for this program.',
    'boost.apply.consent': 'I agree to share my name, email, WhatsApp number and usernames with {mcn} for this program.',
    'boost.apply.submit': 'Submit application',
    'boost.apply.sending': 'Submitting…',
    'boost.apply.bindNote': 'A {platform} account can bind to one MCN only.',
    'boost.tag.many': 'Join many',
    'boost.tag.one': '1 MCN only',
    'boost.status.approved': 'Approved',
    'boost.status.pending': 'Pending',
    'boost.status.declined': 'Declined',
    'boost.soon': 'Coming soon',
    'boost.boundTo': 'Bound to {mcn}',
    'boost.openProgram': 'Open',
    'boost.back': 'All programs',
    'boost.catalog.title': 'Seller list is on the way',
    'boost.catalog.desc': '{mcn} is preparing the sellers and products. They will appear here automatically.',
    'boost.noPrograms': 'No programs open yet',
    'boost.noProgramsDesc': 'New MCN programs will appear here.',
    'boost.err.load': 'Boost could not be loaded',
    'boost.err.server': 'Something went wrong on the server. Please try again.',
    'boost.err.tak-layak': 'Boost is for active Premium, Fighter and SMART plans.',
    'boost.err.program-tutup': 'This program is no longer accepting applications.',
    'boost.err.perlu-setuju': 'Please tick the box to agree to share your details with this MCN.',
    'boost.err.profil-tak-lengkap': 'Please fill in your name and WhatsApp number.',
    'boost.err.nama-salah': 'Please enter your full name (2–80 characters).',
    'boost.err.wa-salah': 'Please enter a valid WhatsApp number (9–15 digits).',
    'boost.err.tiktok-salah': 'Please enter a valid TikTok username (letters, numbers, _ and . only).',
    'boost.err.tiktok2-salah': 'The second TikTok username isn\'t valid (letters, numbers, _ and . only).',
    'boost.err.shopee-salah': 'Please enter a valid Shopee username (letters, numbers, _ . and - only).',
    'boost.err.bind-lain': 'Your account is already bound to another MCN for this platform. An account can bind to one MCN only.',
    'boost.err.ditolak': '{mcn} has declined this application. Please contact them if you think this is a mistake.',
    'boost.err.tiada-emel': 'Your account has no email. Please sign in with an account that has an email.',
    'boost.err.boost-tutup': 'Boost is not open yet.',
    'hooks.subtitle': '800+ TikTok Shop opening lines in Bahasa Melayu — for Malaysian affiliates.',
    'hooks.mode.app': 'After login',
    'hooks.mode.public': 'Visitor not registered',
    'hooks.proto.title': 'Prototype · admin only.',
    'hooks.proto.desc': 'None of the buttons on this page work yet. The switch above toggles between the view for a logged-in customer and the view for a visitor who has just arrived from TikTok.',
    'hooks.searchPh': 'Search hooks — e.g. “kurus”, “promo”, “review”…',
    'hooks.freeAccount': 'Free account',
    'hooks.meter.free': 'Free copies today',
    'hooks.meter.size': '{hook} hooks · {niche} niches · {kategori} categories',
    'hooks.filter.niche': 'Niche',
    'hooks.filter.category': 'Category',
    'hooks.niche.all': 'All',
    'hooks.niche.general': 'General',
    'hooks.niche.beauty': 'Beauty',
    'hooks.niche.fashion': 'Fashion',
    'hooks.niche.parenting': 'Parenting',
    'hooks.niche.food': 'F&B & Kitchen',
    'hooks.niche.gadget': 'Gadget',
    'hooks.niche.health': 'Health',
    'hooks.niche.home': 'Home & Living',
    'hooks.cat.all': 'All categories',
    'hooks.cat.urgency': 'Urgency',
    'hooks.cat.sales': 'Sales & Promotions',
    'hooks.cat.review': 'Reviews & Proof',
    'hooks.cat.curiosity': 'Curiosity',
    'hooks.cat.warning': 'Warning',
    'hooks.cat.pov': 'POV',
    'hooks.cat.story': 'Storytelling',
    'hooks.cat.trending': 'Trending',
    'hooks.cat.solution': 'Solution',
    'hooks.personal.title': 'Type your product name — every hook adapts instantly',
    'hooks.personal.desc': 'Example: “benda ni” becomes “serum XYZ” in every hook, automatically.',
    'hooks.personal.locked': 'Unlocked with a free account',
    'hooks.personal.cta': 'Personalise',
    'hooks.card.save': 'Save hook',
    'hooks.card.copy': 'Copy',
    'hooks.card.copied': 'Copied',
    'hooks.card.toVideo': 'Turn into video',
    'hooks.wall.title': '700+ more hooks waiting for you',
    'hooks.wall.desc': 'Register a free account to unlock every hook, save your own collection and filter by your niche.',
    'hooks.wall.cta': 'Register free now',
    'hooks.loadMore': 'Load {n} more',
    'hooks.remaining': '· {n} left',
    'hooks.more.title': 'These hooks do not stop here',
    'hooks.more.desc': 'Elsewhere, after copying you still have to shoot and edit it yourself. In SESAAT, the same hook can go straight into a TikTok video that is ready to upload.',
    'hooks.more.ctaPublic': 'Try it with a free account',
    'hooks.more.ctaApp': 'Open Workspace',
    'hooks.step1.title': 'Pick a hook & product',
    'hooks.step1.desc': 'Copy any hook and pick the TikTok Shop product you want to promote.',
    'hooks.step2.title': 'SESAAT generates the video',
    'hooks.step2.desc': 'AI produces the promo video — no shooting, no need to show your face.',
    'hooks.step3.title': 'Posted to TikTok automatically',
    'hooks.step3.desc': 'The video is uploaded to your TikTok account on your schedule.',
    'hooks.agent.pill': 'For SESAAT agents',
    'hooks.agent.title': 'Share this free page. The commission stays yours.',
    'hooks.agent.perk1': 'You share something free that people are already looking for — not selling software directly.',
    'hooks.agent.perk2': 'Your link keeps tracking: a visitor who registers today and subscribes next month is still counted as yours.',
    'hooks.agent.perk3': 'A ready-made slideshow pack is prepared every month — just upload it to your TikTok.',
    'hooks.agent.linkLabel': 'Your referral link',
    'hooks.agent.copyLink': 'Copy my link',
    'hooks.agent.packNote': 'A monthly slideshow pack to upload to your TikTok will appear here — just download and post.',
    'maintenance.defaultTitle': 'System under maintenance',
    'maintenance.defaultBody': 'SESAAT is temporarily unavailable while we fix an issue. Please try again shortly.',
    'maintenance.autoNote': 'This page will reopen automatically as soon as the system is back to normal.',
    // ★ HOSTED FLOW BETA — mode selector + customer job status + admin dashboard (central worker office PC)
    'hosted.status.dalam_barisan': 'Waiting',
    'hosted.status.sedang_dijana': 'Generating',
    'hosted.status.menyimpan': 'Saving',
    'hosted.status.siap': 'Ready',
    'hosted.status.perlu_semakan': 'Needs attention',
    'hosted.status.dibatalkan': 'Cancelled',
    'hosted.mode.title': 'Run using',
    'hosted.mode.hosted': 'Hosted Flow Beta',
    'hosted.mode.hostedSub': 'SESAAT office PC generates in the background. You can close the browser.',
    'hosted.mode.biasa': 'Run Flow (normal)',
    'hosted.mode.biasaSub': 'Use your own Chrome extension.',
    'hosted.submit': 'Submit',
    'hosted.batal': 'Cancel',
    'hosted.batalTanya': 'Cancel this job? It has not been sent to Google yet.',
    'hosted.quota': '{remaining}/{cap} videos · {cardsRemaining}/{cardCap} cards left today',
    'hosted.closeNote': 'You can close the browser — the office PC finishes it in the background.',
    'hosted.jobsAktif': 'Hosted status',
    'hosted.tiadaJob': 'No jobs submitted yet.',
    'hosted.perluSemakanNota': 'Admin is reviewing — you do not need to do anything.',
    'hosted.run.sending': 'Sending to SESAAT office PC...',
    'hosted.run.title': 'Hosted Flow Beta — {n} job',
    'hosted.run.detail': '{n} card × {sets} set → office PC. You can close the browser; videos land on the card when ready.',
    'hosted.run.progress': '{siap}/{jumlah} sent...',
    'hosted.run.offlineTitle': 'SESAAT office PC not reachable',
    'hosted.run.offlineDetail': 'No job was sent. Try again shortly or contact SESAAT admin.',
    'hosted.run.sent': '{n} job accepted into queue ✓',
    'hosted.run.partial': '⚠ Some jobs failed',
    'hosted.run.okTitle': '🚀 Jobs queued on the office PC!',
    'hosted.run.okDetail': '{n} new job sent.',
    'hosted.run.dupDetail': ' {n} already in queue (no duplicate).',
    'hosted.run.failDetail': ' {n} failed: {sebab}',
    'hosted.run.seeStatus': ' See status in Control Panel.',
    'hosted.run.failed': '❌ Hosted submit failed',
    'hosted.run.kadOnly': "Hosted Flow Beta uses 'Use card images' only. Switch Workflow to 'Use card images' in the Control Panel first, then Submit again.",
    // Batch notification bell (HostedNotifBell.jsx)
    'hosted.notif.title': 'Notifications',
    'hosted.notif.kosong': 'No notifications yet.',
    'hosted.notif.batchTajuk': 'Hosted Flow — video batch',
    'hosted.notif.videoSiap': 'videos ready',
    'hosted.notif.perluSemak': 'need review',
    'hosted.notif.gagal': 'failed',
};
